/**
 * Indirect Resources Management Dashboard
 * Copy of Selected Contractors structure: header, toolbar, filter bar, table with frozen left columns,
 * resizable column widths, Export/Import, Add row modal, batch delete, ConfirmDialog, Toast, Help.
 * Data: job_#, empl_code, empl_name, discipline, role + 52 week columns (rolling window).
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useMsal } from '@azure/msal-react';
import ResourceHistogramChart from './ResourceHistogramChart';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from '../common/ConfirmDialog';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import EmployeeCodeLegendPanel from '../common/EmployeeCodeLegendPanel';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import FilterTemplateDropdown from '../common/FilteringTemplate/FilterTemplateDropdown';
import FilterIcon from '../common/FilteringTemplate/FilterIcon';
import useFilterTemplate from '../../hooks/useFilterTemplate';
import useColumnOrder from '../../hooks/useColumnOrder';
import ColumnConfigPanel from '../common/ColumnConfigPanel';
import { applyColumnOrder } from '../../utils/columnLayout';

/** Standardized-filter column defs for the Under-Allocation analytics table (PMO Filtering Template). */
const UNDER_ALLOC_COLUMNS = [
  { key: 'empl_name', label: 'Employee', type: 'text' },
  { key: 'role', label: 'Role', type: 'text' },
  { key: 'week_start_date', label: 'Week', type: 'date' },
  { key: 'total', label: 'Allocated', type: 'number' },
  { key: 'available', label: 'Available', type: 'number' },
];

/** Get Monday 00:00 of the week containing date d (ISO week: Monday = start). Returns YYYY-MM-DD. */
function getMondayOfWeek(d) {
  const date = typeof d === 'string' && d.length >= 10 ? new Date(d.slice(0, 10) + 'T12:00:00') : new Date(d);
  const day = date.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + mondayOffset);
  return date.toISOString().slice(0, 10);
}

const formatWeekLabel = (isoDate) => {
  try {
    const d = new Date(isoDate + 'T12:00:00');
    return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: '2-digit' });
  } catch {
    return isoDate;
  }
};

/** Week header date (day of month) for bottom row */
const getWeekHeaderDate = (isoDate) => {
  try {
    const d = new Date(isoDate + 'T12:00:00');
    return d.getDate().toString();
  } catch {
    return isoDate;
  }
};

/** Format week cell value for display: smart decimals (0→"0", 1→"1", 0.5→"0.5", 0.25→"0.25", 0.75→"0.75") */
function formatWeekCellDisplay(v) {
  if (v === null || v === undefined || v === '') return '0';
  const n = parseFloat(v);
  if (Number.isNaN(n)) return '0';
  const rounded = Math.round(n * 100) / 100;
  if (rounded === 0 || rounded === 1) return String(Math.round(rounded));
  if (rounded === 0.5) return '0.5';
  return rounded.toFixed(2);
}

/** Get background color for a week cell value - spectrum green -> yellow -> red */
function getWeekCellColor(v) {
  if (v === null || v === undefined || v === '') return '#4caf50'; // treat empty as 0 = green
  const n = parseFloat(v);
  if (Number.isNaN(n)) return '#4caf50'; // treat invalid as 0 = green
  if (n <= 0) return '#4caf50';   // green
  if (n <= 0.25) return '#8bc34a'; // light green / yellow-green
  if (n <= 0.5) return '#ffeb3b';  // yellow
  if (n <= 0.75) return '#ff9800'; // orange
  return '#f44336';                // red (n >= 0.75)
}

const WEEK_COLOR_LEGEND = [
  { label: '0', color: '#4caf50' },
  { label: '0.25', color: '#8bc34a' },
  { label: '0.5', color: '#ffeb3b' },
  { label: '0.75', color: '#ff9800' },
  { label: '1', color: '#f44336' },
];

function WeekColorLegend({ compact = false }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 8 : 12, alignItems: 'center' }}>
      <span style={{ fontSize: compact ? '0.75rem' : '0.85rem', color: '#555', fontWeight: 600 }}>FTE colour:</span>
      {WEEK_COLOR_LEGEND.map(({ label, color }) => (
        <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: compact ? '0.75rem' : '0.85rem', color: '#333' }}>
          <span style={{ width: 16, height: 16, borderRadius: 3, backgroundColor: color, border: '1px solid #ccc', display: 'inline-block' }} />
          {label}
        </span>
      ))}
    </div>
  );
}

/** Format employee code to always be 4 digits */
function formatEmplCode(code) {
  if (!code) return '';
  const numericOnly = String(code).replace(/\D/g, '');
  if (numericOnly && numericOnly.length <= 4) return numericOnly.padStart(4, '0');
  return String(code);
}

/**
 * Resolve the display value for the Job Name column.
 * If a row has no stored job_name but does have an estimate_name, fall back to the
 * estimate_name (estimate-only rows that aren't yet linked to an awarded project).
 * Returns { text, derived } where derived=true means the value came from estimate_name.
 */
function resolveJobNameDisplay(row) {
  const jn = row.job_name != null ? String(row.job_name).trim() : '';
  if (jn) return { text: jn, derived: false };
  const en = row.estimate_name != null ? String(row.estimate_name).trim() : '';
  if (en) return { text: en, derived: true };
  return { text: '', derived: false };
}

/**
 * Whether a job reference represents awarded work (a real job number) vs. the
 * "Not Awarded" estimate placeholder, the "Holiday" time-off marker, or blank.
 * Mirrors isAwardedJob in src/jobs/overAllocationReportJob.js.
 */
function isAwardedJob(jobRef) {
  if (jobRef == null) return false;
  const s = String(jobRef).trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  if (lower === 'not awarded' || lower === 'holiday') return false;
  return true;
}

const PLAN_STATUS_OPTIONS = [
  { value: 'estimate', label: 'Estimate' },
  { value: 'awarded', label: 'Awarded' },
  { value: 'completed', label: 'Completed' },
];
const PLAN_STATUS_STYLES = {
  estimate: { color: '#6c757d', backgroundColor: '#e9ecef' },
  awarded: { color: '#155724', backgroundColor: '#d4edda' },
  completed: { color: '#92400e', backgroundColor: '#fde68a' },
};

function resolvePlanStatus(row) {
  const s = String(row?.plan_status || '').trim().toLowerCase();
  if (s === 'estimate' || s === 'awarded' || s === 'completed') return s;
  return isAwardedJob(row?.['job_#']) ? 'awarded' : 'estimate';
}

function planStatusLabel(status) {
  return PLAN_STATUS_OPTIONS.find((o) => o.value === status)?.label || status;
}

/** Format last modified date for display (date + time + full name) */
function formatLastModified(row) {
  if (!row.updated_at) return '';
  const d = new Date(row.updated_at);
  const dateStr = d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  const timeStr = d.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', hour12: true });
  const name = row.last_modified_by_name || row.last_modified_by || '';
  const displayName = name.split('@')[0].trim() || name;
  return `${dateStr} ${timeStr} ${displayName}`;
}

/** Parse week cell input to number (2 decimals) or null if empty/invalid. Default 0 when saving. */
function parseWeekCellInput(str) {
  const s = String(str ?? '').trim();
  if (s === '') return null;
  const n = parseFloat(s);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100) / 100;
}

const WEEK_QUICK_VALUES = [0, 0.25, 0.5, 0.75, 1];

/** Compute allocation as sum of week values from the current week (Monday) through the next 12 months. */
function computeAllocation(row, weekDates) {
  if (!row?.weeks || !weekDates?.length) return 0;
  // Use Monday of the current ISO week so in-progress week cells still count (weeks start Monday).
  const startMonday = new Date(getMondayOfWeek(new Date()) + 'T12:00:00');
  const endDate = new Date(startMonday);
  endDate.setFullYear(endDate.getFullYear() + 1);
  let sum = 0;
  for (const wd of weekDates) {
    const d = new Date(wd + 'T12:00:00');
    if (d >= startMonday && d <= endDate) {
      const v = parseFloat(row.weeks[wd]);
      if (!Number.isNaN(v)) sum += v;
    }
  }
  return Math.round(sum * 100) / 100;
}

/** Last week index (in weekDates) where row has a non-zero value; -1 if none. */
function getLastNonZeroWeekIndex(row, weekDates) {
  if (!row?.weeks || !weekDates?.length) return -1;
  for (let i = weekDates.length - 1; i >= 0; i--) {
    const v = row.weeks[weekDates[i]];
    const n = parseFloat(v);
    if (!Number.isNaN(n) && n > 0) return i;
  }
  return -1;
}

/** First week index (in the visible calendar) where row has a non-zero value; -1 if none. */
function getFirstNonZeroWeekIndex(row, weekDates) {
  if (!row?.weeks || !weekDates?.length) return -1;
  for (let i = 0; i < weekDates.length; i++) {
    const v = row.weeks[weekDates[i]];
    const n = parseFloat(v);
    if (!Number.isNaN(n) && n > 0) return i;
  }
  return -1;
}

function weekCellNumber(row, weekDate) {
  const n = parseFloat(row?.weeks?.[weekDate]);
  return Number.isNaN(n) ? 0 : n;
}

/** People allocated in the first visible week (this Monday / From date) first, then the next week, etc. */
function compareBySoonestAllocation(a, b, weekDates) {
  const ia = getFirstNonZeroWeekIndex(a, weekDates);
  const ib = getFirstNonZeroWeekIndex(b, weekDates);
  const aNone = ia < 0;
  const bNone = ib < 0;
  if (aNone !== bNone) return aNone ? 1 : -1;
  if (ia !== ib) return ia - ib;
  if (ia >= 0) {
    const va = weekCellNumber(a, weekDates[ia]);
    const vb = weekCellNumber(b, weekDates[ib]);
    if (va !== vb) return vb - va;
  }
  const nameCmp = String(a.empl_name || '').localeCompare(String(b.empl_name || ''), undefined, { numeric: true, sensitivity: 'base' });
  if (nameCmp !== 0) return nameCmp;
  return String(a['job_#'] || '').localeCompare(String(b['job_#'] || ''), undefined, { numeric: true, sensitivity: 'base' });
}

/** Build year groups: [{ year, count }] for merged year row. count = number of week columns in that year. */
const getYearGroups = (weekDates) => {
  if (!weekDates?.length) return [];
  const groups = [];
  let currentYear = null;
  let count = 0;
  for (const wd of weekDates) {
    const y = new Date(wd + 'T12:00:00').getFullYear();
    if (y !== currentYear) {
      if (currentYear != null) groups.push({ year: currentYear, count });
      currentYear = y;
      count = 0;
    }
    count++;
  }
  if (currentYear != null) groups.push({ year: currentYear, count });
  return groups;
};

/** Build month groups: [{ year, month, count }] for merged month row. count = number of week columns in that month. */
const getMonthGroups = (weekDates) => {
  if (!weekDates?.length) return [];
  const groups = [];
  let currentKey = null;
  let currentYear = null;
  let currentMonth = null;
  let count = 0;
  for (const wd of weekDates) {
    const d = new Date(wd + 'T12:00:00');
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const month = d.toLocaleDateString('en-CA', { month: 'short' });
    const year = d.getFullYear();
    if (key !== currentKey) {
      if (currentKey != null) groups.push({ year: currentYear, month: currentMonth, count });
      currentKey = key;
      currentYear = year;
      currentMonth = month;
      count = 0;
    }
    count++;
  }
  if (currentKey != null) groups.push({ year: currentYear, month: currentMonth, count });
  return groups;
};

const FIXED_COL_KEYS = ['last_modified', 'unique_id', 'estimate_number', 'estimate_name', 'job_#', 'job_name', 'plan_status', 'empl_code', 'empl_name', 'discipline', 'role', 'role_discipline', 'division', 'shop', 'allocation', 'note', 'comments'];
const DEFAULT_HIDDEN_COLS = ['last_modified', 'unique_id', 'estimate_name', 'empl_code', 'role_discipline', 'shop', 'note', 'comments'];
const DEFAULT_COLUMN_WIDTHS = {
  last_modified: 140,
  unique_id: 280,
  'job_#': 100,
  job_name: 160,
  plan_status: 110,
  estimate_number: 120,
  estimate_name: 160,
  empl_code: 120,
  empl_name: 180,
  discipline: 120,
  role: 100,
  role_discipline: 140,
  division: 140,
  shop: 100,
  allocation: 100,
  note: 150,
  comments: 160,
  week: 52
};
const MIN_WIDTHS = { last_modified: 100, unique_id: 100, 'job_#': 60, job_name: 80, plan_status: 80, estimate_number: 80, estimate_name: 100, empl_code: 80, empl_name: 100, discipline: 80, role: 60, role_discipline: 100, division: 80, shop: 60, allocation: 80, note: 80, comments: 80, week: 40 };
const GAP_SPACER_WIDTH = 12; // Used in monthly view
const COLUMN_LABELS = { last_modified: 'Last Modified', unique_id: 'Unique ID', 'job_#': 'Job #', job_name: 'Description', plan_status: 'Status', estimate_number: 'Estimate #', estimate_name: 'Estimate Name', empl_code: 'Empl Code', empl_name: 'Empl Name', discipline: 'Discipline', role: 'Role', role_discipline: 'Role-Discipline', division: 'Division', shop: 'Shop', allocation: 'Allocation', note: 'Note', comments: 'Comments' };
const ALLOCATION_OPTIONS = [0, 0.25, 0.5, 0.75, 1];

// Legend codes — keep in sync with src/utils/employeeCodeLegend.js (item 10, 2026-08-10).
// Discipline (= Trade) codes match the Accounting → Coding Structure disciplines.
const LEGEND_DISCIPLINES = [
  ['PIP', 'Piping'], ['MWR', 'M&R (Millwright / Mechanical)'], ['ELE', 'Electrical'],
  ['SHM', 'Sheet Metal / Ducts / HVAC'], ['PFS', 'Pipe Shop Fabrication'], ['CIV', 'Civil'],
  ['STR', 'Structural'], ['TNK', 'Tank Fabrication'], ['BMT', 'Boiler Maker / Field Erected Tanks'],
  ['MUL', 'Multi-discipline'],
];
const LEGEND_ROLES = [
  ['F', 'Foreman'], ['GF', 'General Foreman'], ['SUP', 'Superintendent'], ['PM', 'Project Manager'],
  ['AM', 'Area Manager'], ['DIR', 'Director'], ['CLK', 'Clerk'], ['SCH', 'Scheduler'],
  ['TC', 'Technical Coordinator'], ['EST', 'Estimator'], ['PUR', 'Purchaser'], ['QC', 'Quality Control'],
  ['HSE', 'Safety Officer'], ['ADM', 'Office Admin'], ['TRD', 'Tradesperson'], ['WLD', 'Welder'],
];
/** Fixed columns whose Advanced filter should use numeric operators (>, ≥, <, ≤, =, ≠). */
const NUMERIC_FILTER_COLS = ['allocation'];
const DATE_HEADER_HEIGHT = 48; // px - frozen and scrollable headers must match
const BODY_ROW_HEIGHT = 28; // px - frozen and scrollable body rows must match

/** Row key: use unique_id as the identifier */
const rowKey = (row) => row.unique_id || `${row.empl_code}|||${row['job_#'] || ''}`;

function parseAuditChanges(changes) {
  if (!changes) return {};
  if (typeof changes === 'string') {
    try { return JSON.parse(changes); } catch { return {}; }
  }
  return typeof changes === 'object' ? changes : {};
}

function formatAuditVal(v) {
  if (v === null || v === undefined || v === '') return '(empty)';
  return String(v);
}

function prettyAuditField(field) {
  if (!field) return '';
  return COLUMN_LABELS[field] || String(field).replace(/_/g, ' ');
}

function parseAuditTarget(entry) {
  const c = parseAuditChanges(entry?.changes);
  const firstCell = Array.isArray(c.cells) ? c.cells[0] : null;
  const unique_id = c.unique_id || firstCell?.unique_id || null;
  const rawWeek = c.week_start_date || firstCell?.week_start_date || null;
  const weekDate = rawWeek ? getMondayOfWeek(String(rawWeek).slice(0, 10)) : null;
  const rawField = Array.isArray(c.fieldChanges) && c.fieldChanges[0]?.field ? c.fieldChanges[0].field : null;
  return {
    unique_id,
    weekDate,
    columnKey: rawField && FIXED_COL_KEYS.includes(rawField) ? rawField : null,
    empl_code: entry?.empl_code || c.empl_code || null,
    job_ref: entry?.job_ref || c['job_#'] || null,
    action_type: entry?.action_type || null,
  };
}

function describeAuditJump(entry) {
  const c = parseAuditChanges(entry?.changes);
  const action = entry?.action_type;
  const person = entry?.empl_name
    ? `${entry.empl_name}${entry.empl_code ? ` (${formatEmplCode(entry.empl_code)})` : ''}`
    : (entry?.empl_code ? formatEmplCode(entry.empl_code) : null);
  const job = entry?.job_ref || c['job_#'] || null;
  const when = entry?.created_at
    ? new Date(entry.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
    : null;
  const by = entry?.user_name || entry?.user_email || null;
  const lines = [];

  const pushCell = (cell, fallbackWeek) => {
    if (!cell && !fallbackWeek) return;
    const week = String((cell && cell.week_start_date) || fallbackWeek || '').slice(0, 10);
    const to = cell && cell.to !== undefined ? cell.to : cell?.value;
    const hasFrom = cell && cell.from !== undefined;
    lines.push({
      field: week ? `Week of ${formatWeekLabel(week)}` : 'Week cell',
      weekDate: week && week.length >= 10 ? getMondayOfWeek(week) : null,
      from: hasFrom ? cell.from : undefined,
      to,
      hasFrom,
    });
  };

  if (action === 'CELL_UPDATE' || (action === 'REVERT' && c.week_start_date && !c.fieldChanges && !c.cells)) {
    pushCell(c, c.week_start_date);
  } else if (Array.isArray(c.fieldChanges) && c.fieldChanges.length) {
    c.fieldChanges.forEach((fc) => {
      lines.push({
        field: prettyAuditField(fc.field),
        columnKey: fc.field && FIXED_COL_KEYS.includes(fc.field) ? fc.field : null,
        from: fc.from,
        to: fc.to,
        hasFrom: true,
      });
    });
  } else if (action === 'BATCH_FILL' && Array.isArray(c.cells) && c.cells.length) {
    c.cells.slice(0, 8).forEach((cell) => pushCell(cell));
    if (c.cells.length > 8) {
      lines.push({ field: `+${c.cells.length - 8} more cell(s)`, hasFrom: false, to: null, more: true });
    }
  } else if (action === 'CREATE') {
    lines.push({ field: 'Row', to: 'added', hasFrom: false });
  } else if (action === 'DELETE') {
    lines.push({ field: 'Row', to: 'deleted', hasFrom: false });
  } else if (action === 'REVERT') {
    if (c.undoCreate) lines.push({ field: 'Row', to: 'create undone', hasFrom: false });
    else if (c.undoDelete) lines.push({ field: 'Row', to: 'delete undone', hasFrom: false });
    else lines.push({ field: 'Change', to: 'reverted', hasFrom: false });
  } else if (c.value !== undefined || c.to !== undefined) {
    pushCell(c, c.week_start_date);
  }

  return { person, job, when, by, action, lines };
}

function AuditFromTo({ line }) {
  if (line.more) return <span style={{ color: '#6b7280' }}>{line.field}</span>;
  if (line.hasFrom) {
    return (
      <span>
        <span style={{ fontWeight: 600, color: '#374151' }}>{line.field}</span>
        {': '}
        <span style={{ color: '#9ca3af', textDecoration: 'line-through' }}>{formatAuditVal(line.from)}</span>
        <span style={{ color: '#6b7280' }}>{' \u2192 '}</span>
        <span style={{ color: '#111827', fontWeight: 700 }}>{formatAuditVal(line.to)}</span>
      </span>
    );
  }
  if (line.to !== undefined && line.to !== null) {
    return (
      <span>
        <span style={{ fontWeight: 600, color: '#374151' }}>{line.field}</span>
        {': '}
        <span style={{ color: '#111827', fontWeight: 700 }}>{formatAuditVal(line.to)}</span>
      </span>
    );
  }
  return <span style={{ fontWeight: 600, color: '#374151' }}>{line.field}</span>;
}

const AUDIT_TABLE_COLUMNS = [
  { key: 'date', label: 'Date', type: 'text' },
  { key: 'action', label: 'Action', type: 'text' },
  { key: 'employee', label: 'Employee', type: 'text' },
  { key: 'job', label: 'Job', type: 'text' },
  { key: 'column', label: 'Column', type: 'text' },
  { key: 'from', label: 'From', type: 'text' },
  { key: 'to', label: 'To', type: 'text' },
  { key: 'by', label: 'By', type: 'text' },
];

function flattenAuditEntries(entries) {
  const flat = [];
  for (const entry of entries || []) {
    const lines = describeAuditJump(entry).lines;
    const push = (line, i) => {
      const when = new Date(entry.created_at).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
      });
      flat.push({
        entry,
        line,
        lineIndex: i,
        date: when,
        action: entry.action_type || '',
        employee: entry.empl_name ? `${entry.empl_name} (${formatEmplCode(entry.empl_code)})` : (entry.empl_code || ''),
        job: entry.job_ref || '',
        column: line.field || '',
        from: line.hasFrom ? formatAuditVal(line.from) : '',
        to: (line.to !== undefined && line.to !== null) ? formatAuditVal(line.to) : '',
        by: entry.user_name || entry.user_email || '',
      });
    };
    if (!lines.length) push({ field: '—', hasFrom: false }, 0);
    else lines.forEach(push);
  }
  return flat;
}

function isAuditRevertable(entry) {
  const action = entry?.action_type;
  const c = parseAuditChanges(entry?.changes);
  if (action === 'CELL_UPDATE' && c.unique_id && c.week_start_date) return true;
  if (action === 'UPDATE' && c.unique_id) return true;
  if (action === 'CREATE' && c.unique_id) return true;
  if (action === 'DELETE' && Array.isArray(c.snapshot) && c.snapshot.length > 0) return true;
  if (action === 'BATCH_FILL' && Array.isArray(c.cells) && c.cells.some((x) => x && x.from !== undefined)) return true;
  if (action === 'REVERT') {
    if (c.unique_id && c.week_start_date) return true;
    if (Array.isArray(c.fieldChanges) && c.fieldChanges.length > 0) return true;
    if (Array.isArray(c.cells) && c.cells.length > 0) return true;
    if (c.undoCreate && c.unique_id) return true;
    if (c.undoDelete && Array.isArray(c.snapshot) && c.snapshot.length > 0) return true;
  }
  return false;
}

function renderAuditWhatChanged(entry) {
  const c = parseAuditChanges(entry?.changes);
  const action = entry?.action_type;
  if (action === 'CELL_UPDATE' || (action === 'REVERT' && c.week_start_date && !c.fieldChanges && !c.cells)) {
    const week = String(c.week_start_date || '').slice(0, 10);
    const to = c.to !== undefined ? c.to : c.value;
    const weekLabel = week ? formatWeekLabel(week) : 'week';
    if (c.from !== undefined) {
      return `${weekLabel}: ${formatAuditVal(c.from)} → ${formatAuditVal(to)}`;
    }
    if (to !== undefined) return `${weekLabel}: ${formatAuditVal(to)}`;
  }
  if (Array.isArray(c.fieldChanges) && c.fieldChanges.length > 0) {
    const shown = c.fieldChanges.slice(0, 3);
    const extra = c.fieldChanges.length - shown.length;
    return shown.map((fc) => `${prettyAuditField(fc.field)}: ${formatAuditVal(fc.from)} → ${formatAuditVal(fc.to)}`).join(' · ')
      + (extra > 0 ? ` · +${extra} more` : '');
  }
  if (action === 'CREATE') return 'Row added';
  if (action === 'DELETE') return c.deleted_rows != null ? `Deleted ${c.deleted_rows} week row(s)` : 'Row deleted';
  if (action === 'BATCH_FILL') {
    const n = Array.isArray(c.cells) ? c.cells.length : c.count;
    return n != null ? `Filled ${n} cell(s)` : 'Batch fill';
  }
  if (action === 'REVERT') {
    if (c.undoCreate) return `Reverted create #${c.revertedAuditId || ''}`;
    if (c.undoDelete) return `Restored delete #${c.revertedAuditId || ''}`;
    return c.revertedAuditId ? `Reverted #${c.revertedAuditId}` : 'Reverted';
  }
  if (action === 'IMPORT') return `Imported ${c.imported || 0}, skipped ${c.skipped || 0}`;
  return '—';
}

const IndirectResourcesDashboard = () => {
  const { instance, accounts } = useMsal();

  // Live data push: ref + callback for real-time sync
  const fetchDataRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchDataRef.current) fetchDataRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('indirectResources', 'global', true, handleDataChange);

  // Socket ID tracking for sync headers
  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  const [data, setData] = useState({ windowStart: null, weekDates: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingCell, setSavingCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editingCellPosition, setEditingCellPosition] = useState(null); // { top, left } for popup when filtered
  const [editValue, setEditValue] = useState('');
  const [editingFixedCell, setEditingFixedCell] = useState(null); // { empl_code, columnKey }
  const [editFixedValue, setEditFixedValue] = useState('');
  const [savingFixedCell, setSavingFixedCell] = useState(null); // 'empl_code-columnKey'
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRow, setNewRow] = useState({ 'job_#': '', job_name: '', estimate_number: '', estimate_name: '', empl_code: '', empl_name: '', discipline: '', role: '', role_discipline: '', division: '', shop: '', start_date: '', finish_date: '', allocation: '', note: '', comments: '' });
  const [addEmplSearch, setAddEmplSearch] = useState('');
  const [showAddEmplDropdown, setShowAddEmplDropdown] = useState(false);
  const [emplSearchResults, setEmplSearchResults] = useState([]);
  const [emplSearchLoading, setEmplSearchLoading] = useState(false);
  const emplSearchDebounceRef = useRef(null);
  const [addJobSearch, setAddJobSearch] = useState('');
  const [showAddJobDropdown, setShowAddJobDropdown] = useState(false);
  const [jobSearchResults, setJobSearchResults] = useState([]);
  const [jobSearchLoading, setJobSearchLoading] = useState(false);
  const jobSearchDebounceRef = useRef(null);
  const [addEstSearch, setAddEstSearch] = useState('');
  const [showAddEstDropdown, setShowAddEstDropdown] = useState(false);
  const [estSearchResults, setEstSearchResults] = useState([]);
  const [estSearchLoading, setEstSearchLoading] = useState(false);
  const estSearchDebounceRef = useRef(null);
  const [divisionsList, setDivisionsList] = useState([]);
  const [inlineSuggestions, setInlineSuggestions] = useState([]);
  const [showInlineSuggestions, setShowInlineSuggestions] = useState(false);
  const [inlineSuggestionsLoading, setInlineSuggestionsLoading] = useState(false);
  const showInlineSuggestionsRef = useRef(false);
  const inlineSearchDebounceRef = useRef(null);
  const addModalRef = useRef(null);
  const [adding, setAdding] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpTab, setHelpTab] = useState('guide'); // 'guide' | 'legend'
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState('histogram'); // 'histogram' | 'pivot' | 'over_allocation' | 'under_allocation'
  const [showAnalyticsHelp, setShowAnalyticsHelp] = useState(false);
  const [analyticsHelpTab, setAnalyticsHelpTab] = useState('guide'); // 'guide' | 'legend'
  const [overAllocations, setOverAllocations] = useState([]);
  const [overAllocationsLoading, setOverAllocationsLoading] = useState(false);
  const [overAllocationsError, setOverAllocationsError] = useState(null);
  const [overAllocationsLoaded, setOverAllocationsLoaded] = useState(false);
  const [sendingOverAllocReport, setSendingOverAllocReport] = useState(false);
  const [overAllocGroupByEmployee, setOverAllocGroupByEmployee] = useState(true);
  const [overAllocExpandedGroups, setOverAllocExpandedGroups] = useState(() => new Set());
  // Under-allocation tab state + window controls (default ~3 months)
  const [underAllocations, setUnderAllocations] = useState([]);
  const [underAllocationsLoading, setUnderAllocationsLoading] = useState(false);
  const [underAllocationsError, setUnderAllocationsError] = useState(null);
  const [underAllocationsLoaded, setUnderAllocationsLoaded] = useState(false);
  const [underAllocMode, setUnderAllocMode] = useState('weeks'); // 'weeks' | 'range'
  const [underAllocWeeks, setUnderAllocWeeks] = useState(13); // ~3 months
  const [underAllocStart, setUnderAllocStart] = useState('');
  const [underAllocEnd, setUnderAllocEnd] = useState('');
  const [underAllocSort, setUnderAllocSort] = useState({ key: 'empl_name', dir: 'asc' });
  const [underAllocGroupByEmployee, setUnderAllocGroupByEmployee] = useState(true);
  const [underAllocExpandedGroups, setUnderAllocExpandedGroups] = useState(() => new Set());
  const [pivotGroupBy, setPivotGroupBy] = useState('job'); // 'job' | 'discipline' | 'role' | 'role_discipline' | 'empl_name' | 'shop'
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const s = localStorage.getItem('indirectResourcesVisibleColumns');
      if (s) {
        const saved = JSON.parse(s);
        if (saved.awarded !== undefined && saved.plan_status === undefined) saved.plan_status = saved.awarded;
        return FIXED_COL_KEYS.reduce((acc, k) => ({ ...acc, [k]: saved[k] !== undefined ? saved[k] : !DEFAULT_HIDDEN_COLS.includes(k) }), {});
      }
    } catch (_) {}
    return FIXED_COL_KEYS.reduce((acc, k) => ({ ...acc, [k]: !DEFAULT_HIDDEN_COLS.includes(k) }), {});
  });
  const { columnOrder, moveColumn } = useColumnOrder('indirect-resources-column-order', FIXED_COL_KEYS);
  const visibleFixedKeys = useMemo(
    () => applyColumnOrder(FIXED_COL_KEYS.map(k => ({ key: k, label: COLUMN_LABELS[k] })), columnOrder)
      .filter(c => visibleColumns[c.key])
      .map(c => c.key),
    [columnOrder, visibleColumns]
  );
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditData, setAuditData] = useState({ entries: [], total: 0, actionTypes: [] });
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [auditSort, setAuditSort] = useState({ key: 'date', dir: 'desc' });
  const [isAuditSuperadmin, setIsAuditSuperadmin] = useState(false);
  const [revertingAuditId, setRevertingAuditId] = useState(null);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [auditCallout, setAuditCallout] = useState(null);
  const auditJumpRef = useRef(null);
  const [showMonthly, setShowMonthly] = useState(false);
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const s = localStorage.getItem('indirectResourcesColumnWidths');
      if (s) return { ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(s) };
    } catch (_) {}
    return { ...DEFAULT_COLUMN_WIDTHS };
  });
  const [columnWidthHistory, setColumnWidthHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Allocation filter (item 5, 2026-08-10): default ON — show only rows with allocation > 0.
  const [allocAboveZero, setAllocAboveZero] = useState(true);
  const [dateRangeFrom, setDateRangeFrom] = useState(() => getMondayOfWeek(new Date())); // Monday of current week as default
  const [dateRangeTo, setDateRangeTo] = useState(() => {
    const monday = getMondayOfWeek(new Date());
    const end = new Date(monday + 'T12:00:00');
    end.setDate(end.getDate() + 51 * 7);
    return end.toISOString().slice(0, 10);
  });
  const [monthsAhead, setMonthsAhead] = useState(null); // 1-12 or null (alternative to manual To date)
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterMode, setFilterMode] = useState({});
  const [filterText, setFilterText] = useState({});
  const [filterButtonPositions, setFilterButtonPositions] = useState({});
  const [sortColumn, setSortColumn] = useState(null); // 'job_#', 'empl_code', etc. or null
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'
  // Default ON: people with allocation in the first visible week (this Monday / From date) at the top.
  const [sortByRecentAlloc, setSortByRecentAlloc] = useState(() => {
    try {
      const s = localStorage.getItem('indirectResourcesSortByRecentAlloc');
      if (s === '0' || s === 'false') return false;
    } catch (_) {}
    return true;
  });
  const [resizing, setResizing] = useState(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const [dragFill, setDragFill] = useState({ active: false, startRowIndex: null, startWeekIndex: null, currentRowIndex: null, currentWeekIndex: null, startValue: null });
  const dragFillRef = useRef({ currentRowIndex: null, currentWeekIndex: null });
  const [fixedDragFill, setFixedDragFill] = useState({ active: false, startRowIndex: null, currentRowIndex: null, columnKey: null, startValue: null });
  const fixedDragFillRef = useRef({ currentRowIndex: null });
  const tableScrollRef = useRef(null); // ref for the scrollable table container
  const dragAutoScrollRef = useRef(null); // requestAnimationFrame ID for auto-scroll
  const [undoStack, setUndoStack] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);

  const { toast, showToast } = useToast(3000);
  const { confirm, dialogProps } = useConfirm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const windowStart = getMondayOfWeek(dateRangeFrom);
      const toMonday = getMondayOfWeek(dateRangeTo);
      const fromMs = new Date(windowStart + 'T12:00:00').getTime();
      const toMs = new Date(toMonday + 'T12:00:00').getTime();
      const weeks = Math.max(1, Math.min(104, Math.round((toMs - fromMs) / (7 * 24 * 60 * 60 * 1000)) + 1));
      const params = new URLSearchParams();
      params.set('windowStart', windowStart);
      params.set('weeks', String(weeks));
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/indirect-resources?${params.toString()}`);
      if (res?.data?.success && res?.data?.data) {
        setData(res.data.data);
      } else {
        setError('Failed to load data');
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.status === 403
        ? 'Access denied. Resources or Admin department required.'
        : (err?.response?.data?.message || 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, dateRangeFrom, dateRangeTo]);

  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch divisions list for dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedApiCall(instance, accounts, 'GET', '/divisions');
        if (res?.data?.success || Array.isArray(res?.data?.data || res?.data)) {
          setDivisionsList(res.data.data || res.data || []);
        }
      } catch (_) {}
    })();
  }, [instance, accounts]);

  // Employee typeahead search (from lt_employee master list)
  const searchEmployees = useCallback(async (query) => {
    if (!query || query.length < 1) { setEmplSearchResults([]); return []; }
    setEmplSearchLoading(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/lt-employee/search?q=${encodeURIComponent(query)}&limit=15&indirectOnly=true`);
      const list = res?.data?.success ? (res.data.data || []) : [];
      setEmplSearchResults(list);
      return list;
    } catch (_) {
      setEmplSearchResults([]);
      return [];
    } finally {
      setEmplSearchLoading(false);
    }
  }, [instance, accounts]);

  // Job/project typeahead search (uses dedicated endpoint that bypasses department restrictions)
  const searchJobs = useCallback(async (query) => {
    if (!query || query.length < 1) { setJobSearchResults([]); return []; }
    setJobSearchLoading(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/indirect-resources/projects-search?q=${encodeURIComponent(query)}&limit=15`);
      const list = res?.data?.data || [];
      setJobSearchResults(list);
      return list;
    } catch (_) {
      setJobSearchResults([]);
      return [];
    } finally {
      setJobSearchLoading(false);
    }
  }, [instance, accounts]);

  const fetchOverAllocations = useCallback(async () => {
    setOverAllocationsLoading(true);
    setOverAllocationsError(null);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', '/indirect-resources/over-allocations?weeks=52');
      setOverAllocations(Array.isArray(res?.data?.data) ? res.data.data : []);
      setOverAllocationsLoaded(true);
    } catch (err) {
      setOverAllocationsError(err?.response?.data?.message || err?.message || 'Failed to load over-allocations');
      setOverAllocations([]);
    } finally {
      setOverAllocationsLoading(false);
    }
  }, [instance, accounts]);

  const sendOverAllocationReportNow = useCallback(async () => {
    if (!window.confirm('Send the over-allocation report email to all managers + Yousef now?')) return;
    setSendingOverAllocReport(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources/over-allocations/send-now');
      const r = res?.data || {};
      const counts = `${r.overCount ?? r.itemsCount ?? 0} over, ${r.underCount ?? 0} under`;
      showToast(r.sent ? `Sent to ${r.recipientsCount} recipient(s) (${counts})` : (r.message || 'Nothing sent'), r.sent ? 'success' : 'info');
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || 'Failed to send report', 'error');
    } finally {
      setSendingOverAllocReport(false);
    }
  }, [instance, accounts, showToast]);

  const fetchUnderAllocations = useCallback(async () => {
    setUnderAllocationsLoading(true);
    setUnderAllocationsError(null);
    try {
      let qs;
      if (underAllocMode === 'range' && underAllocStart && underAllocEnd) {
        qs = `startDate=${encodeURIComponent(underAllocStart)}&endDate=${encodeURIComponent(underAllocEnd)}`;
      } else {
        qs = `weeks=${encodeURIComponent(underAllocWeeks)}`;
      }
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/indirect-resources/under-allocations?${qs}`);
      setUnderAllocations(Array.isArray(res?.data?.data) ? res.data.data : []);
      setUnderAllocationsLoaded(true);
    } catch (err) {
      setUnderAllocationsError(err?.response?.data?.message || err?.message || 'Failed to load under-allocations');
      setUnderAllocations([]);
    } finally {
      setUnderAllocationsLoading(false);
    }
  }, [instance, accounts, underAllocMode, underAllocStart, underAllocEnd, underAllocWeeks]);

  const handleUnderAllocSort = useCallback((key) => {
    setUnderAllocSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  }, []);

  // Standardized PMO "Filtering Template" applied to the under-allocation table.
  const underFilter = useFilterTemplate({ items: underAllocations, columns: UNDER_ALLOC_COLUMNS });

  const displayedUnderAllocations = useMemo(() => {
    const list = underFilter.filteredItems;
    const { key, dir } = underAllocSort;
    const mult = dir === 'asc' ? 1 : -1;
    const sorted = [...list].sort((a, b) => {
      let av = a[key], bv = b[key];
      if (key === 'total' || key === 'available') {
        av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
        if (av !== bv) return (av - bv) * mult;
      } else {
        av = (av == null ? '' : String(av)).toLowerCase();
        bv = (bv == null ? '' : String(bv)).toLowerCase();
        const cmp = av.localeCompare(bv, undefined, { numeric: true });
        if (cmp !== 0) return cmp * mult;
      }
      // tie-breaker: employee then week
      const an = (a.empl_name || a.empl_code || '').toLowerCase();
      const bn = (b.empl_name || b.empl_code || '').toLowerCase();
      if (an !== bn) return an < bn ? -1 : 1;
      return a.week_start_date < b.week_start_date ? -1 : a.week_start_date > b.week_start_date ? 1 : 0;
    });
    return sorted;
  }, [underFilter.filteredItems, underAllocSort]);

  // Excel-style grouping per employee (same pattern as the HSE training database grid).
  // Group order follows the current sort (first occurrence of each employee).
  const groupedUnderAllocations = useMemo(() => {
    if (!underAllocGroupByEmployee) return null;
    const map = new Map();
    for (const it of displayedUnderAllocations) {
      const key = `${it.empl_code || ''}::${it.empl_name || ''}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return Array.from(map.entries()).map(([key, rows]) => ({
      key,
      empl_name: rows[0].empl_name || '',
      empl_code: rows[0].empl_code || '',
      role: rows.find(r => r.role)?.role || '',
      rows,
      totalAvailable: Math.round(rows.reduce((s, r) => s + (parseFloat(r.available) || 0), 0) * 100) / 100,
    }));
  }, [displayedUnderAllocations, underAllocGroupByEmployee]);

  const toggleUnderAllocGroup = useCallback((key) => {
    setUnderAllocExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const groupedOverAllocations = useMemo(() => {
    if (!overAllocGroupByEmployee) return null;
    const map = new Map();
    for (const it of overAllocations) {
      const key = `${it.empl_code || ''}::${it.empl_name || ''}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return Array.from(map.entries()).map(([key, rows]) => {
      const sorted = [...rows].sort((a, b) => String(a.week_start_date || '').localeCompare(String(b.week_start_date || '')));
      const peak = sorted.reduce((m, r) => Math.max(m, parseFloat(r.total) || 0), 0);
      return {
        key,
        empl_name: sorted[0].empl_name || '',
        empl_code: sorted[0].empl_code || '',
        rows: sorted,
        peakTotal: Math.round(peak * 100) / 100,
      };
    });
  }, [overAllocations, overAllocGroupByEmployee]);

  const toggleOverAllocGroup = useCallback((key) => {
    setOverAllocExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const searchEstimates = useCallback(async (query) => {
    if (!query || query.length < 1) { setEstSearchResults([]); return []; }
    setEstSearchLoading(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/estimates/search?q=${encodeURIComponent(query)}&limit=15`);
      const list = res?.data?.data || [];
      setEstSearchResults(list);
      return list;
    } catch (_) {
      setEstSearchResults([]);
      return [];
    } finally {
      setEstSearchLoading(false);
    }
  }, [instance, accounts]);

  const EMPL_AUTOCOMPLETE_COLS = useMemo(() => new Set(['empl_code', 'empl_name']), []);
  const JOB_AUTOCOMPLETE_COLS = useMemo(() => new Set(['job_#', 'job_name', 'estimate_number', 'estimate_name']), []);
  const DIVISION_COL = 'division';

  // Job/estimate typeahead may still hint values already on the plan (Projects API
  // is merged after). Employee typeahead is master-only — never seed from plan
  // rows (those can be old free-typed mismatches like 1111 + a person name).
  const getExistingSuggestions = useCallback((query, columnKey) => {
    if (EMPL_AUTOCOMPLETE_COLS.has(columnKey)) return [];
    const rows = data.rows || [];
    if (!rows.length || !JOB_AUTOCOMPLETE_COLS.has(columnKey)) return [];
    const q = (query || '').toLowerCase();
    const seen = new Set();
    const results = [];
    for (const row of rows) {
      const key = `${row['job_#'] || ''}|${row.job_name || ''}|${row.estimate_number || ''}`;
      if (seen.has(key) || (!row['job_#'] && !row.job_name && !row.estimate_number)) continue;
      if (q && !((row['job_#'] || '').toLowerCase().includes(q) || (row.job_name || '').toLowerCase().includes(q) || (row.estimate_number || '').toLowerCase().includes(q))) continue;
      seen.add(key);
      results.push({ type: 'job', job_number: row['job_#'] || '', job_name: row.job_name || '', estimate_number: row.estimate_number || '', estimate_name: row.estimate_name || '', division_name: row.division || '', existing: true });
      if (results.length >= 10) break;
    }
    return results;
  }, [data.rows, EMPL_AUTOCOMPLETE_COLS, JOB_AUTOCOMPLETE_COLS]);

  const searchInlineSuggestions = useCallback((query, columnKey) => {
    if (inlineSearchDebounceRef.current) clearTimeout(inlineSearchDebounceRef.current);
    const existing = EMPL_AUTOCOMPLETE_COLS.has(columnKey) ? [] : getExistingSuggestions(query, columnKey);
    if (!query || query.length < 1) {
      setInlineSuggestions(existing);
      setShowInlineSuggestions(existing.length > 0);
      return;
    }
    setInlineSuggestions(existing);
    setShowInlineSuggestions(true);
    setInlineSuggestionsLoading(true);
    inlineSearchDebounceRef.current = setTimeout(async () => {
      try {
        let apiResults = [];
        if (EMPL_AUTOCOMPLETE_COLS.has(columnKey)) {
          const res = await authenticatedApiCall(instance, accounts, 'GET', `/lt-employee/search?q=${encodeURIComponent(query)}&limit=12&indirectOnly=true`);
          apiResults = (res?.data?.data || []).map(e => ({ type: 'employee', ...e }));
          setInlineSuggestions(apiResults);
          return;
        }
        if (JOB_AUTOCOMPLETE_COLS.has(columnKey)) {
          const res = await authenticatedApiCall(instance, accounts, 'GET', `/indirect-resources/projects-search?q=${encodeURIComponent(query)}&limit=12`);
          apiResults = (res?.data?.data || []).map(j => ({ type: 'job', ...j }));
        }
        const existingKeys = new Set(existing.map(e => `${e.job_number}|${e.job_name}|${e.estimate_number}`));
        const deduped = apiResults.filter(r => !existingKeys.has(`${r.job_number}|${r.job_name}|${r.estimate_number}`));
        setInlineSuggestions([...existing, ...deduped]);
      } catch (_) {
        setInlineSuggestions(existing);
      } finally {
        setInlineSuggestionsLoading(false);
      }
    }, 250);
  }, [instance, accounts, EMPL_AUTOCOMPLETE_COLS, JOB_AUTOCOMPLETE_COLS, getExistingSuggestions]);

  const selectInlineSuggestion = useCallback(async (suggestion, row) => {
    setShowInlineSuggestions(false);
    setInlineSuggestions([]);
    setEditingFixedCell(null);
    setEditFixedValue('');
    const rk = rowKey(row);
    let payload = {
      unique_id: row.unique_id,
      'job_#': row['job_#'] ?? '',
      job_name: row.job_name ?? '',
      estimate_number: row.estimate_number ?? '',
      estimate_name: row.estimate_name ?? '',
      empl_code: row.empl_code ?? '',
      empl_name: row.empl_name ?? '',
      discipline: row.discipline ?? '',
      role: row.role ?? '',
      role_discipline: row.role_discipline ?? '',
      division: row.division ?? '',
      shop: row.shop ?? '',
      start_date: row.start_date || null,
      finish_date: row.finish_date || null,
      allocation: row.allocation != null ? row.allocation : null,
      note: row.note ?? '',
      comments: row.comments ?? '',
      // Scope validation to the identity being picked so a dirty Job does not
      // block an Employee fix (and vice versa).
      changedDomains: suggestion.type === 'employee' ? ['employee'] : ['job', 'estimate'],
    };
    if (suggestion.type === 'employee') {
      payload.empl_code = suggestion.emp_code || '';
      payload.empl_name = suggestion.emp_name || '';
      // Discipline (= Trade): prefer the canonical discipline_code (PIP/MWR/…)
      // over the legacy raw payroll trade text ("PF-F", "MW", …).
      payload.discipline = suggestion.discipline_code || suggestion.trade || payload.discipline;
      // Role rides along with the person from the employee master (Hiring /
      // Layoff role_code). Keep the row's existing role when the master has none.
      if (suggestion.role_code) payload.role = suggestion.role_code;
      payload.role_discipline = [payload.role, payload.discipline].filter(Boolean).join(' - ');
      if (suggestion.division) payload.division = suggestion.division;
    } else if (suggestion.type === 'job') {
      payload['job_#'] = suggestion.job_number || 'Not Awarded';
      payload.job_name = suggestion.job_name || '';
      payload.estimate_number = suggestion.estimate_number || '';
      payload.estimate_name = suggestion.estimate_name || '';
      if (suggestion.division_name) payload.division = suggestion.division_name + (suggestion.division_location ? ` (${suggestion.division_location})` : '');
    }
    setSavingFixedCell(`${rk}-multi`);
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', '/indirect-resources/row', payload, syncHeaders());
      await fetchData();
      showToast('Saved', 'success');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSavingFixedCell(null);
    }
  }, [instance, accounts, fetchData, showToast, syncHeaders]);

  // When monthsAhead is set, keep dateRangeTo = dateRangeFrom + N months
  useEffect(() => {
    if (monthsAhead == null) return;
    const from = new Date(dateRangeFrom + 'T12:00:00');
    const to = new Date(from);
    to.setMonth(to.getMonth() + monthsAhead);
    const toStr = to.toISOString().slice(0, 10);
    setDateRangeTo(toStr);
  }, [monthsAhead, dateRangeFrom]);

  const fetchAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '2000');
      params.set('offset', '0');
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/indirect-resources/audit?${params.toString()}`);
      if (res?.data?.success) {
        setAuditData({ entries: res.data.data, total: res.data.total, actionTypes: res.data.actionTypes || [] });
      }
    } catch (err) {
      console.error('Audit log fetch error:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [instance, accounts]);

  const auditFlatItems = useMemo(() => flattenAuditEntries(auditData.entries), [auditData.entries]);
  const auditFilterTpl = useFilterTemplate({ items: auditFlatItems, columns: AUDIT_TABLE_COLUMNS });

  const auditDisplayRows = useMemo(() => {
    const q = auditSearch.trim().toLowerCase();
    const searched = !q
      ? auditFilterTpl.filteredItems
      : auditFilterTpl.filteredItems.filter((row) => (
        [row.date, row.action, row.employee, row.job, row.column, row.from, row.to, row.by]
          .join(' ')
          .toLowerCase()
          .includes(q)
      ));
    const mul = auditSort.dir === 'asc' ? 1 : -1;
    const valOf = (row) => {
      switch (auditSort.key) {
        case 'date': return new Date(row.entry.created_at).getTime();
        case 'action': return row.action;
        case 'employee': return row.employee.toLowerCase();
        case 'job': return row.job.toLowerCase();
        case 'column': return row.column.toLowerCase();
        case 'from': return row.from.toLowerCase();
        case 'to': return row.to.toLowerCase();
        case 'by': return row.by.toLowerCase();
        default: return 0;
      }
    };
    return [...searched].sort((a, b) => {
      const va = valOf(a);
      const vb = valOf(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * mul;
    });
  }, [auditFilterTpl.filteredItems, auditSearch, auditSort]);

  const handleAuditSort = useCallback((key) => {
    setAuditSort((prev) => (
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'date' ? 'desc' : 'asc' }
    ));
  }, []);

  const needAuditPerms = showAuditModal || !!auditCallout;
  useEffect(() => {
    if (!needAuditPerms) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info');
        if (!cancelled && resp.data.success) {
          setIsAuditSuperadmin(!!resp.data.data?.isSuperadmin);
        }
      } catch (_) {
        if (!cancelled) setIsAuditSuperadmin(false);
      }
    })();
    return () => { cancelled = true; };
  }, [needAuditPerms, instance, accounts]);

  useEffect(() => {
    try {
      localStorage.setItem('indirectResourcesColumnWidths', JSON.stringify(columnWidths));
    } catch (_) {}
  }, [columnWidths]);

  useEffect(() => {
    try {
      localStorage.setItem('indirectResourcesVisibleColumns', JSON.stringify(visibleColumns));
    } catch (_) {}
  }, [visibleColumns]);

  useEffect(() => { showInlineSuggestionsRef.current = showInlineSuggestions; }, [showInlineSuggestions]);

  const saveColumnWidthsToHistory = useCallback(() => {
    setColumnWidthHistory(prev => [...prev.slice(-19), columnWidths]);
  }, [columnWidths]);

  const undoColumnResize = useCallback(() => {
    if (columnWidthHistory.length === 0) return;
    const prev = columnWidthHistory[columnWidthHistory.length - 1];
    setColumnWidths(prev);
    setColumnWidthHistory(h => h.slice(0, -1));
  }, [columnWidthHistory]);

  const pushUndo = useCallback((fn) => {
    setUndoStack(prev => [...prev.slice(-49), fn]);
  }, []);

  const runUndo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const fn = prev[prev.length - 1];
      fn();
      return prev.slice(0, -1);
    });
  }, []);

  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        runUndo();
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [runUndo]);

  const resetColumnWidths = useCallback(() => {
    saveColumnWidthsToHistory();
    setColumnWidths({ ...DEFAULT_COLUMN_WIDTHS });
  }, [saveColumnWidthsToHistory]);

  const autoFitColumns = useCallback(() => {
    saveColumnWidthsToHistory();
    // Approximate px per character (0.85rem) + padding; minimize empty space
    const CHAR_PX = 7;
    const CELL_PADDING = 20;
    const WEEK_PADDING = 8;
    const rows = data.rows ?? [];
    const next = { ...DEFAULT_COLUMN_WIDTHS };
    FIXED_COL_KEYS.forEach(k => {
      const headerLen = (COLUMN_LABELS[k] ?? k).length;
      const maxCell = rows.length
        ? Math.max(0, ...rows.map(r => String(r[k] ?? '').length))
        : 0;
      const contentChars = Math.max(headerLen, maxCell, 1);
      next[k] = Math.max(MIN_WIDTHS[k], Math.ceil(contentChars * CHAR_PX + CELL_PADDING));
    });
    let maxWeekCellLen = 2; // week header is day of month (1-2 chars)
    rows.forEach(r => {
      if (r.weeks && typeof r.weeks === 'object') {
        Object.values(r.weeks).forEach(v => {
          const len = String(v ?? '').length;
          if (len > maxWeekCellLen) maxWeekCellLen = len;
        });
      }
    });
    next.week = Math.max(MIN_WIDTHS.week, Math.ceil(maxWeekCellLen * 6 + WEEK_PADDING));
    setColumnWidths(next);
  }, [saveColumnWidthsToHistory, data.rows]);

  const getRowValue = useCallback((row, key) => {
    if (key === 'allocation') return String(computeAllocation(row, data.weekDates || []));
    if (key === 'plan_status' || key === 'awarded') return planStatusLabel(resolvePlanStatus(row));
    return row[key];
  }, [data.weekDates]);

  const getUniqueValues = useCallback((columnKey) => {
    let rows = [...(data.rows || [])];
    if (allocAboveZero) rows = rows.filter(r => computeAllocation(r, data.weekDates || []) > 0);
    if (searchTerm.trim()) {
      const t = searchTerm.trim().toLowerCase();
      rows = rows.filter(r => FIXED_COL_KEYS.some(k => { const v = getRowValue(r, k); return v != null && v !== '' && String(v).toLowerCase().includes(t); }));
    }
    Object.entries(columnFilters).forEach(([key, values]) => {
      if (key !== columnKey && values?.length > 0) {
        rows = rows.filter(item => {
          const val = getRowValue(item, key);
          const isBlank = val === null || val === undefined || val === '' || val === '0';
          if (isBlank) return values.includes('(Blank)');
          return values.includes(String(val));
        });
      }
    });
    const values = new Set();
    let hasBlank = false;
    rows.forEach(r => {
      const val = getRowValue(r, columnKey);
      if (val === null || val === undefined || val === '' || (columnKey === 'allocation' && val === '0')) { hasBlank = true; }
      else { values.add(String(val)); }
    });
    const sorted = Array.from(values).sort((a, b) => {
      const na = parseFloat(a), nb = parseFloat(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b, undefined, { numeric: true });
    });
    if (hasBlank) sorted.push('(Blank)');
    return sorted;
  }, [data.rows, data.weekDates, searchTerm, allocAboveZero, columnFilters, getRowValue]);

  const handleFilterToggle = useCallback((columnKey, value) => {
    setColumnFilters(prev => {
      const allValues = getUniqueValues(columnKey);
      const current = prev[columnKey];
      if (!current) {
        const newValues = allValues.filter(v => v !== value);
        return newValues.length ? { ...prev, [columnKey]: newValues } : (() => { const { [columnKey]: _, ...rest } = prev; return rest; })();
      }
      if (current.includes(value)) {
        const newValues = current.filter(v => v !== value);
        if (!newValues.length) { const { [columnKey]: _, ...rest } = prev; return rest; }
        return { ...prev, [columnKey]: newValues };
      }
      const newValues = [...current, value];
      if (newValues.length >= allValues.length) { const { [columnKey]: _, ...rest } = prev; return rest; }
      return { ...prev, [columnKey]: newValues };
    });
  }, [getUniqueValues]);

  const clearColumnFilter = useCallback((columnKey) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: [] }));
    setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
  }, []);

  const selectAllColumnFilter = useCallback((columnKey) => {
    setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
  }, []);

  const applyTextFilter = useCallback((columnKey, mode, text) => {
    const isEmptyMode = ['empty', 'notEmpty'].includes(mode);
    if (!text.trim() && !isEmptyMode) {
      setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    } else {
      setFilterMode(prev => ({ ...prev, [columnKey]: mode }));
      setFilterText(prev => ({ ...prev, [columnKey]: isEmptyMode ? '' : text }));
      setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    }
    setOpenFilterColumn(null);
    setFilterSearch('');
  }, []);

  const clearAllFilters = useCallback(() => {
    setColumnFilters({});
    setFilterMode({});
    setFilterText({});
    setSearchTerm('');
    setFilterSearch('');
    setOpenFilterColumn(null);
    setAllocAboveZero(false);
    showToast('All filters cleared', 'success');
  }, [showToast]);

  const getActiveFilters = useCallback(() => {
    const active = [];
    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values?.length > 0) active.push({ key, type: 'values', count: values.length });
    });
    Object.entries(filterMode).forEach(([key, mode]) => {
      if (mode && filterText[key]) active.push({ key, type: 'text', mode, value: filterText[key] });
    });
    return active;
  }, [columnFilters, filterMode, filterText]);

  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0 || searchTerm || allocAboveZero;

  const filteredRows = useMemo(() => {
    let result = [...(data.rows || [])];
    if (searchTerm.trim()) {
      const t = searchTerm.trim().toLowerCase();
      result = result.filter(r =>
        FIXED_COL_KEYS.some(k => {
          const v = getRowValue(r, k);
          return v != null && v !== '' && String(v).toLowerCase().includes(t);
        })
      );
    }
    if (allocAboveZero) result = result.filter(r => computeAllocation(r, data.weekDates || []) > 0);
    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values?.length > 0) {
        result = result.filter(item => {
          const val = getRowValue(item, key);
          const isBlank = val === null || val === undefined || val === '' || (key === 'allocation' && val === '0');
          if (isBlank) return values.includes('(Blank)');
          return values.includes(String(val));
        });
      }
    });
    Object.entries(filterMode).forEach(([key, mode]) => {
      const text = (filterText[key] || '').trim();
      if (!text && !['empty', 'notEmpty'].includes(mode)) return;
      const isNumericOperator = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'].includes(mode);
      result = result.filter(item => {
        const rawVal = getRowValue(item, key);
        if (isNumericOperator) {
          // Same semantics as useFilterTemplate: non-numeric cell only passes ≠.
          const numVal = parseFloat(rawVal);
          const filterNum = parseFloat(text);
          if (isNaN(numVal)) return mode === 'neq';
          if (isNaN(filterNum)) return true;
          switch (mode) {
            case 'eq': return numVal === filterNum;
            case 'neq': return numVal !== filterNum;
            case 'gt': return numVal > filterNum;
            case 'gte': return numVal >= filterNum;
            case 'lt': return numVal < filterNum;
            case 'lte': return numVal <= filterNum;
            default: return true;
          }
        }
        const strVal = rawVal != null && rawVal !== '' ? String(rawVal).toLowerCase() : '';
        const textLower = text.toLowerCase();
        switch (mode) {
          case 'equals': return strVal === textLower;
          case 'notEquals': return strVal !== textLower;
          case 'contains': return strVal.includes(textLower);
          case 'notContains': return !strVal.includes(textLower);
          case 'startsWith': return strVal.startsWith(textLower);
          case 'endsWith': return strVal.endsWith(textLower);
          case 'empty': return !strVal;
          case 'notEmpty': return !!strVal;
          default: return true;
        }
      });
    });
    return result;
  }, [data.rows, data.weekDates, searchTerm, allocAboveZero, columnFilters, filterMode, filterText, getRowValue]);

  const sortedRows = useMemo(() => {
    const weekDates = data.weekDates || [];
    if (sortByRecentAlloc) {
      return [...filteredRows].sort((a, b) => compareBySoonestAllocation(a, b, weekDates));
    }
    if (!sortColumn) return filteredRows;
    const key = sortColumn;
    const sorted = [...filteredRows].sort((a, b) => {
      const va = getRowValue(a, key);
      const vb = getRowValue(b, key);
      const aStr = va != null && va !== '' ? String(va).toLowerCase() : '';
      const bStr = vb != null && vb !== '' ? String(vb).toLowerCase() : '';
      const cmp = aStr.localeCompare(bStr, undefined, { numeric: true });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredRows, sortColumn, sortDirection, sortByRecentAlloc, data.weekDates, getRowValue]);

  const applyPendingAuditJump = useCallback(() => {
    const t = auditJumpRef.current;
    if (!t || loading) return;

    const raw = data.rows || [];
    let row = t.unique_id ? raw.find((r) => String(r.unique_id) === String(t.unique_id)) : null;
    if (!row && (t.empl_code || t.job_ref)) {
      row = raw.find((r) =>
        String(r.empl_code || '') === String(t.empl_code || '')
        && String(r['job_#'] || '') === String(t.job_ref || '')
      );
    }

    if (!row) {
      if (Date.now() - t.started < 8000) return;
      showToast('That row is not currently visible (it may be filtered out or deleted).', 'warning');
      auditJumpRef.current = null;
      return;
    }

    const rk = rowKey(row);
    const visible = (sortedRows || []).some((r) => rowKey(r) === rk);
    if (!visible) {
      if (!t.clearedFilters) {
        t.clearedFilters = true;
        setAllocAboveZero(false);
        setSearchTerm('');
        setColumnFilters({});
        setFilterMode({});
        setFilterText({});
        showToast('Filters cleared so the changed row is visible.', 'info');
      } else if (Date.now() - t.started > 8000) {
        showToast('That row is not currently visible (it may be filtered out or deleted).', 'warning');
        auditJumpRef.current = null;
      }
      return;
    }

    auditJumpRef.current = null;
    setHighlightedRowId(rk);
    if (t.weekDate) {
      setSelectedCell({ type: 'week', rk, weekDate: t.weekDate });
    } else if (t.columnKey) {
      setSelectedCell({ type: 'fixed', rk, columnKey: t.columnKey });
    } else {
      setSelectedCell(null);
    }
    setAuditCallout({
      ...(t.summary || {}),
      rk,
      weekDate: t.weekDate || null,
      columnKey: t.columnKey || null,
      anchor: null,
    });

    requestAnimationFrame(() => {
      const el = tableScrollRef.current;
      if (!el) return;
      const rowEl = el.querySelector(`[data-row-id="${rk}"]`);
      if (rowEl) {
        const rRect = rowEl.getBoundingClientRect();
        const sRect = el.getBoundingClientRect();
        el.scrollTop += (rRect.top - sRect.top) - (el.clientHeight / 2) + (rRect.height / 2);
      }
      setTimeout(() => {
        const el2 = tableScrollRef.current;
        if (!el2) return;
        let cell = null;
        if (t.weekDate) {
          cell = el2.querySelector(`[data-cell="${rk}-${t.weekDate}"]`);
        } else if (t.columnKey) {
          cell = el2.querySelector(`[data-row-id="${rk}"] [data-col="${t.columnKey}"]`);
        }
        if (cell) {
          const cRect = cell.getBoundingClientRect();
          const sRect = el2.getBoundingClientRect();
          el2.scrollLeft += (cRect.left - sRect.left) - (sRect.width / 2) + (cRect.width / 2);
          requestAnimationFrame(() => {
            const after = cell.getBoundingClientRect();
            setAuditCallout((prev) => prev && prev.rk === rk ? {
              ...prev,
              anchor: { top: after.top, left: after.left + after.width / 2, bottom: after.bottom },
            } : prev);
          });
        }
      }, 80);
    });

    setTimeout(() => {
      setHighlightedRowId((prev) => (prev === rk ? null : prev));
    }, 8000);
  }, [loading, data.rows, sortedRows, showToast]);

  useEffect(() => {
    if (auditJumpRef.current) applyPendingAuditJump();
  }, [applyPendingAuditJump, loading, data.rows, data.weekDates, sortedRows]);

  const handleAuditNavigate = useCallback((entry, line) => {
    const target = parseAuditTarget(entry);
    if (line?.weekDate) target.weekDate = line.weekDate;
    if (line?.columnKey) target.columnKey = line.columnKey;
    if (!target.unique_id && !target.empl_code) {
      showToast('This entry has no row to jump to.', 'warning');
      return;
    }
    if (target.action_type === 'DELETE') {
      const stillThere = (data.rows || []).some((r) => String(r.unique_id) === String(target.unique_id));
      if (!stillThere) {
        showToast('That row was deleted. Revert it first if you need it back.', 'warning');
        return;
      }
    }
    setShowAuditModal(false);
    setShowMonthly(false);
    setMonthsAhead(null);

    let needDateChange = false;
    if (target.weekDate) {
      if (target.weekDate < dateRangeFrom) {
        setDateRangeFrom(target.weekDate);
        needDateChange = true;
      }
      if (target.weekDate > dateRangeTo) {
        setDateRangeTo(target.weekDate);
        needDateChange = true;
      }
    }

    auditJumpRef.current = {
      ...target,
      summary: { ...describeAuditJump(entry), entry },
      clearedFilters: false,
      started: Date.now(),
    };
    if (!needDateChange) {
      requestAnimationFrame(() => applyPendingAuditJump());
    }
  }, [data.rows, dateRangeFrom, dateRangeTo, showToast, applyPendingAuditJump]);

  const handleAuditRevert = useCallback(async (entry) => {
    const summary = renderAuditWhatChanged(entry);
    const ok = await confirm({
      title: 'Revert this change?',
      message: `This will restore the previous value on the plan.\n\n${summary}`,
      confirmText: 'Revert',
      cancelText: 'Cancel',
      type: 'warning',
    });
    if (!ok) return;
    try {
      setRevertingAuditId(entry.id);
      const resp = await authenticatedApiCall(instance, accounts, 'POST', `/indirect-resources/audit/${entry.id}/revert`);
      if (resp?.data?.success) {
        showToast(resp.data.message || 'Reverted', 'success');
        setAuditCallout(null);
        setHighlightedRowId(null);
        await fetchAuditLog();
        await fetchData();
      } else {
        showToast(resp?.data?.message || 'Revert failed', 'error');
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Revert failed', 'error');
    } finally {
      setRevertingAuditId(null);
    }
  }, [confirm, instance, accounts, showToast, fetchAuditLog, fetchData]);

  useEffect(() => {
    try { localStorage.setItem('indirectResourcesSortByRecentAlloc', sortByRecentAlloc ? '1' : '0'); } catch (_) {}
  }, [sortByRecentAlloc]);

  const sortColumnRef = useRef(null);
  sortColumnRef.current = sortColumn;
  const handleSort = useCallback((columnKey) => {
    const prevCol = sortColumnRef.current;
    setSortByRecentAlloc(false);
    setSortColumn(columnKey);
    setSortDirection(prevDir => (prevCol === columnKey && prevDir === 'asc') ? 'desc' : 'asc');
  }, []);

  const getSortIndicator = (columnKey) => {
    if (sortColumn !== columnKey) return ' ';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };
  const getSortTitle = (columnKey) => sortColumn === columnKey
    ? `Sorted ${sortDirection === 'asc' ? 'A→Z' : 'Z→A'}. Click to toggle.`
    : 'Click to sort A→Z';

  /** Pivot: group rows by key with count and list of rows for detail expand */
  const pivotByJob = useMemo(() => {
    const map = new Map();
    filteredRows.forEach(r => {
      const key = r['job_#'] ?? '(blank)';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries()).map(([name, rows]) => ({ name, count: rows.length, rows })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [filteredRows]);
  const pivotByDiscipline = useMemo(() => {
    const map = new Map();
    filteredRows.forEach(r => {
      const key = r.discipline ?? '(blank)';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries()).map(([name, rows]) => ({ name, count: rows.length, rows })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [filteredRows]);
  const pivotByRole = useMemo(() => {
    const map = new Map();
    filteredRows.forEach(r => {
      const key = r.role ?? '(blank)';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries()).map(([name, rows]) => ({ name, count: rows.length, rows })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [filteredRows]);
  const pivotByEmplName = useMemo(() => {
    const map = new Map();
    filteredRows.forEach(r => {
      const key = r.empl_name ?? '(blank)';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries()).map(([name, rows]) => ({ name, count: rows.length, rows })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [filteredRows]);
  const pivotByShop = useMemo(() => {
    const map = new Map();
    filteredRows.forEach(r => {
      const key = r.shop ?? '(blank)';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries()).map(([name, rows]) => ({ name, count: rows.length, rows })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [filteredRows]);
  const pivotByRoleDiscipline = useMemo(() => {
    const map = new Map();
    filteredRows.forEach(r => {
      const key = r.role_discipline ?? '(blank)';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries()).map(([name, rows]) => ({ name, count: rows.length, rows })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [filteredRows]);
  const pivotData = pivotGroupBy === 'job' ? pivotByJob : pivotGroupBy === 'discipline' ? pivotByDiscipline : pivotGroupBy === 'role' ? pivotByRole : pivotGroupBy === 'role_discipline' ? pivotByRoleDiscipline : pivotGroupBy === 'empl_name' ? pivotByEmplName : pivotGroupBy === 'shop' ? pivotByShop : [];
  const pivotTitle = pivotGroupBy === 'job' ? 'Job #' : pivotGroupBy === 'discipline' ? 'Discipline' : pivotGroupBy === 'role' ? 'Role' : pivotGroupBy === 'role_discipline' ? 'Role-Discipline' : pivotGroupBy === 'empl_name' ? 'Empl Name' : pivotGroupBy === 'shop' ? 'Shop' : '';
  const [pivotExpandedKey, setPivotExpandedKey] = useState(null); // name of expanded pivot row, or null

  const saveCell = useCallback(async (empl_code, week_start_date, value, row, skipCheck) => {
    const numVal = parseFloat(value) || 0;

    // Over-allocation check before saving (only for non-zero values)
    if (!skipCheck && numVal > 0 && empl_code) {
      try {
        const checkRes = await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources/check-over-allocation', {
          empl_code,
          empl_name: row.empl_name || null,
          week_start_date,
          new_value: numVal,
          exclude_unique_id: row.unique_id
        });
        if (checkRes?.data?.overAllocated) {
          const d = checkRes.data;
          const pct = Math.round(d.total * 100);
          const ok = await confirm({
            title: 'Over-Allocation Warning',
            message: (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', backgroundColor: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
                  <span style={{ fontSize: '1.5rem' }}>👤</span>
                  <div><strong style={{ fontSize: '1rem' }}>{d.person}</strong><br /><span style={{ color: '#92400e', fontSize: '0.85rem' }}>Week of {week_start_date}</span></div>
                  <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <span style={{ fontSize: '1.3rem', fontWeight: 700, color: pct > 100 ? '#dc2626' : '#16a34a' }}>{pct}%</span>
                    <br /><span style={{ fontSize: '0.75rem', color: '#666' }}>total allocation</span>
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, backgroundColor: pct > 100 ? '#dc2626' : '#16a34a', borderRadius: 4, transition: 'width 0.3s' }} />
                  </div>
                </div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Existing allocations this week:</div>
                {(d.conflicts || []).map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 4, backgroundColor: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: '0.85rem' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f59e0b', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}><strong>{c.job}</strong>{c.role ? ` · ${c.role}` : ''}{c.discipline ? ` · ${c.discipline}` : ''}</span>
                    <span style={{ fontWeight: 700, color: '#b45309' }}>{c.allocation}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginTop: 4, backgroundColor: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca', fontSize: '0.85rem' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#dc2626', flexShrink: 0 }} />
                  <span style={{ flex: 1 }}><strong>New value</strong></span>
                  <span style={{ fontWeight: 700, color: '#dc2626' }}>{d.newValue}</span>
                </div>
              </div>
            ),
            confirmText: 'Yes, Save Anyway',
            cancelText: 'Cancel',
            type: 'warning',
            wide: true
          });
          if (!ok) {
            setEditingCell(null);
            setEditingCellPosition(null);
            setEditValue('');
            return;
          }
        }
      } catch (checkErr) {
        console.error('Over-allocation check failed, proceeding with save:', checkErr);
      }
    }

    const key = `${rowKey(row)}-${week_start_date}`;
    setSavingCell(key);
    try {
      await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources', {
        unique_id: row.unique_id,
        'job_#': row['job_#'],
        empl_code: row.empl_code,
        empl_name: row.empl_name,
        discipline: row.discipline,
        role: row.role,
        role_discipline: row.role_discipline || null,
        shop: row.shop,
        start_date: row.start_date || null,
        finish_date: row.finish_date || null,
        allocation: row.allocation != null ? row.allocation : null,
        note: row.note || null,
        division: row.division || null,
        estimate_number: row.estimate_number || null,
        estimate_name: row.estimate_name || null,
        job_name: row.job_name || null,
        comments: row.comments || null,
        week_start_date,
        value: value || null
      }, syncHeaders());
      setEditingCell(null);
      setEditingCellPosition(null);
      setEditValue('');
      setSelectedCell({ type: 'week', rk: rowKey(row), weekDate: week_start_date });
      await fetchData();
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSavingCell(null);
    }
  }, [instance, accounts, fetchData, showToast, confirm]);

  const handleCellBlur = useCallback((row, weekDate) => {
    if (editingCell?.rowKey !== rowKey(row) || editingCell?.week_start_date !== weekDate) return;
    const num = parseWeekCellInput(editValue);
    const valueToSave = num === null ? '0' : String(num);
    const prevValue = row.weeks?.[weekDate] ?? '0';
    if (String(prevValue) !== valueToSave) {
      pushUndo(() => saveCell(row.empl_code, weekDate, prevValue, row));
      saveCell(row.empl_code, weekDate, valueToSave, row);
    } else {
      setEditingCell(null);
      setEditingCellPosition(null);
      setEditValue('');
    }
  }, [editingCell, editValue, saveCell, pushUndo]);

  const handleCellKeyDown = useCallback((e, row, weekDate) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (editingCell?.rowKey === rowKey(row) && editingCell?.week_start_date === weekDate) {
        const num = parseWeekCellInput(editValue);
        const valueToSave = num === null ? '0' : String(num);
        const prevValue = row.weeks?.[weekDate] ?? '0';
        if (String(prevValue) !== valueToSave) {
          pushUndo(() => saveCell(row.empl_code, weekDate, prevValue, row));
          saveCell(row.empl_code, weekDate, valueToSave, row);
        } else {
          setEditingCell(null);
          setEditingCellPosition(null);
          setEditValue('');
        }
      }
    }
    if (e.key === 'Escape') {
      setEditingCell(null);
      setEditingCellPosition(null);
      setEditValue('');
    }
  }, [editingCell, editValue, saveCell, pushUndo]);

  const saveFixedCell = useCallback(async (row, columnKey, value) => {
    if (columnKey === 'last_modified' || columnKey === 'unique_id') return;
    // Employee cells must be chosen from the master dropdown — free-text blur
    // would re-introduce plan-only names that are not on lt_employee.
    if (columnKey === 'empl_code' || columnKey === 'empl_name') {
      showToast('Pick an employee from the dropdown (Hiring / Layoff → Indirect)', 'error');
      setEditingFixedCell(null);
      setEditFixedValue('');
      setInlineSuggestions([]);
      setShowInlineSuggestions(false);
      return;
    }
    const newRole = columnKey === 'role' ? (value ?? '') : (row.role ?? '');
    const newDiscipline = columnKey === 'discipline' ? (value ?? '') : (row.discipline ?? '');
    const autoRoleDiscipline = [newRole, newDiscipline].filter(Boolean).join(' - ');
    const changedDomains = [];
    if (columnKey === 'job_#' || columnKey === 'job_name') changedDomains.push('job', 'estimate');
    if (columnKey === 'estimate_number' || columnKey === 'estimate_name') changedDomains.push('estimate');
    const payload = {
      unique_id: row.unique_id,
      'job_#': columnKey === 'job_#' ? (value ?? '') : (row['job_#'] ?? ''),
      job_name: columnKey === 'job_name' ? (value ?? '') : (row.job_name ?? ''),
      estimate_number: columnKey === 'estimate_number' ? (value ?? '') : (row.estimate_number ?? ''),
      estimate_name: columnKey === 'estimate_name' ? (value ?? '') : (row.estimate_name ?? ''),
      empl_code: columnKey === 'empl_code' ? (value ?? '') : (row.empl_code ?? ''),
      empl_name: columnKey === 'empl_name' ? (value ?? '') : (row.empl_name ?? ''),
      discipline: newDiscipline,
      role: newRole,
      role_discipline: columnKey === 'role_discipline' ? (value ?? '') : (columnKey === 'role' || columnKey === 'discipline' ? autoRoleDiscipline : (row.role_discipline ?? '')),
      division: columnKey === 'division' ? (value ?? '') : (row.division ?? ''),
      shop: columnKey === 'shop' ? (value ?? '') : (row.shop ?? ''),
      start_date: columnKey === 'start_date' ? (value || null) : (row.start_date || null),
      finish_date: columnKey === 'finish_date' ? (value || null) : (row.finish_date || null),
      allocation: columnKey === 'allocation' ? (value != null && value !== '' ? parseFloat(value) : null) : (row.allocation != null ? row.allocation : null),
      note: columnKey === 'note' ? (value ?? '') : (row.note ?? ''),
      comments: columnKey === 'comments' ? (value ?? '') : (row.comments ?? ''),
      plan_status: columnKey === 'plan_status' ? (value || resolvePlanStatus(row)) : (row.plan_status || resolvePlanStatus(row)),
      // Always send (even []) so the API skips full-row identity checks on
      // role/discipline/etc. edits when Job or Employee is still dirty.
      changedDomains: [...new Set(changedDomains)],
    };
    setSavingFixedCell(`${rowKey(row)}-${columnKey}`);
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', '/indirect-resources/row', payload, syncHeaders());
      setEditingFixedCell(null);
      setEditFixedValue('');
      setSelectedCell({ type: 'fixed', rk: rowKey(row), columnKey });
      await fetchData();
      showToast('Saved', 'success');
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSavingFixedCell(null);
    }
  }, [instance, accounts, fetchData, showToast, syncHeaders]);

  const handleFixedCellBlur = useCallback((row, columnKey) => {
    setTimeout(() => {
      if (showInlineSuggestionsRef.current) return;
      // Dropdowns save in onChange. A blur save uses the value from when the
      // editor opened, so it writes the old Status back and the change looks lost.
      if (columnKey === 'plan_status' || columnKey === 'allocation' || columnKey === 'division') {
        setEditingFixedCell((cur) => (
          cur && cur.rowKey === rowKey(row) && cur.columnKey === columnKey ? null : cur
        ));
        return;
      }
      if (editingFixedCell?.rowKey !== rowKey(row) || editingFixedCell?.columnKey !== columnKey) return;
      saveFixedCell(row, columnKey, editFixedValue.trim() || null);
      setInlineSuggestions([]);
      setShowInlineSuggestions(false);
    }, 200);
  }, [editingFixedCell, editFixedValue, saveFixedCell]);

  const handleFixedCellKeyDown = useCallback((e, row, columnKey) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showInlineSuggestions && inlineSuggestions.length > 0) {
        selectInlineSuggestion(inlineSuggestions[0], row);
        return;
      }
      if (editingFixedCell?.rowKey === rowKey(row) && editingFixedCell?.columnKey === columnKey) {
        saveFixedCell(row, columnKey, editFixedValue.trim() || null);
        setInlineSuggestions([]);
        setShowInlineSuggestions(false);
      }
    }
    if (e.key === 'Escape') {
      if (showInlineSuggestionsRef.current) {
        e.stopPropagation();
        setInlineSuggestions([]);
        setShowInlineSuggestions(false);
        return;
      }
      setEditingFixedCell(null);
      setEditFixedValue('');
      setInlineSuggestions([]);
      setShowInlineSuggestions(false);
    }
  }, [editingFixedCell, editFixedValue, saveFixedCell]);

  const handleExport = useCallback(async () => {
    try {
      const windowStart = getMondayOfWeek(dateRangeFrom);
      const toMonday = getMondayOfWeek(dateRangeTo);
      const fromMs = new Date(windowStart + 'T12:00:00').getTime();
      const toMs = new Date(toMonday + 'T12:00:00').getTime();
      const weeks = Math.max(1, Math.min(104, Math.round((toMs - fromMs) / (7 * 24 * 60 * 60 * 1000)) + 1));
      const params = new URLSearchParams();
      params.set('windowStart', windowStart);
      params.set('weeks', String(weeks));
      const res = await authenticatedApiCall(instance, accounts, 'GET', `/indirect-resources/export?${params.toString()}`, null, { responseType: 'blob' });
      const blob = res?.data;
      if (!blob) throw new Error('No data');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `indirect_resources_${windowStart || 'export'}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Export downloaded', 'success');
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Export failed', 'error');
    }
  }, [instance, accounts, dateRangeFrom, dateRangeTo, showToast]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', '/indirect-resources/template', null, { responseType: 'blob' });
      const blob = res?.data;
      if (!blob) throw new Error('No data');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `indirect_resources_template.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Template downloaded', 'success');
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Template download failed', 'error');
    }
  }, [instance, accounts, showToast]);

  const handleImport = useCallback(async (file) => {
    if (!file) return;
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources/import?preview=1', formData, syncHeaders());
      if (res?.data?.success) {
        setImportFile(file);
        setImportPreview(res.data);
      } else {
        showToast(res?.data?.message || 'Could not read that file', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Could not read that file', 'error');
    } finally {
      setImporting(false);
    }
  }, [instance, accounts, showToast]);

  const confirmResourceImport = useCallback(async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources/import', formData, syncHeaders());
      if (res?.data?.success) {
        const rejected = res.data.rejected || 0;
        const msg = res.data.message || `Imported ${res.data.imported || 0} rows`;
        showToast(msg, rejected > 0 && (res.data.imported || 0) === 0 ? 'error' : 'success');
        setShowImportModal(false);
        setImportPreview(null);
        setImportFile(null);
        await fetchData();
      } else {
        showToast(res?.data?.message || 'Import failed', 'error');
      }
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Import failed', 'error');
    } finally {
      setImporting(false);
    }
  }, [instance, accounts, fetchData, showToast, importFile]);

  const addRow = useCallback(async () => {
    if (!newRow.empl_code?.trim() && !newRow.empl_name?.trim()) return;
    setAdding(true);
    try {
      const allocVal = newRow.allocation !== '' ? parseFloat(newRow.allocation) : null;

      // Pre-check for over-allocation before saving
      if (allocVal && allocVal > 0 && (newRow.empl_code?.trim() || newRow.empl_name?.trim())) {
        const checkRes = await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources/check-over-allocation', {
          empl_code: newRow.empl_code.trim() || null,
          empl_name: newRow.empl_name || null,
          new_allocation: allocVal,
          start_date: newRow.start_date || null,
          finish_date: newRow.finish_date || null
        });
        if (checkRes?.data?.overAllocated) {
          const d = checkRes.data;
          const pct = Math.round(d.total * 100);
          setAdding(false);
          const ok = await confirm({
            title: 'Over-Allocation Warning',
            message: (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', backgroundColor: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
                  <span style={{ fontSize: '1.5rem' }}>👤</span>
                  <div><strong style={{ fontSize: '1rem' }}>{d.person}</strong><br /><span style={{ color: '#92400e', fontSize: '0.85rem' }}>New assignment</span></div>
                  <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <span style={{ fontSize: '1.3rem', fontWeight: 700, color: pct > 100 ? '#dc2626' : '#16a34a' }}>{pct}%</span>
                    <br /><span style={{ fontSize: '0.75rem', color: '#666' }}>total allocation</span>
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, backgroundColor: pct > 100 ? '#dc2626' : '#16a34a', borderRadius: 4, transition: 'width 0.3s' }} />
                  </div>
                </div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Existing assignments:</div>
                {(d.conflicts || []).map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 4, backgroundColor: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: '0.85rem' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f59e0b', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}><strong>{c.job}</strong>{c.role ? ` · ${c.role}` : ''}{c.discipline ? ` · ${c.discipline}` : ''}{c.start_date ? <span style={{ color: '#6b7280', marginLeft: 4 }}>({c.start_date} → {c.finish_date})</span> : ''}</span>
                    <span style={{ fontWeight: 700, color: '#b45309' }}>{c.allocation}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginTop: 4, backgroundColor: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca', fontSize: '0.85rem' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#dc2626', flexShrink: 0 }} />
                  <span style={{ flex: 1 }}><strong>New allocation</strong></span>
                  <span style={{ fontWeight: 700, color: '#dc2626' }}>{d.newAllocation}</span>
                </div>
              </div>
            ),
            confirmText: 'Yes, Add Anyway',
            cancelText: 'Cancel',
            type: 'warning',
            wide: true
          });
          if (!ok) return;
          setAdding(true);
        }
      }

      await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources/row', {
        'job_#': newRow['job_#'] || null,
        job_name: newRow.job_name || null,
        estimate_number: newRow.estimate_number || null,
        estimate_name: newRow.estimate_name || null,
        empl_code: newRow.empl_code.trim(),
        empl_name: newRow.empl_name || null,
        discipline: newRow.discipline || null,
        role: newRow.role || null,
        role_discipline: newRow.role_discipline || null,
        division: newRow.division || null,
        shop: newRow.shop || null,
        start_date: newRow.start_date || null,
        finish_date: newRow.finish_date || null,
        allocation: allocVal,
        note: newRow.note || null,
        comments: newRow.comments || null
      }, syncHeaders());
      showToast('Row added', 'success');
      setNewRow({ 'job_#': '', job_name: '', estimate_number: '', estimate_name: '', empl_code: '', empl_name: '', discipline: '', role: '', role_discipline: '', division: '', shop: '', start_date: '', finish_date: '', allocation: '', note: '', comments: '' });
      setAddEmplSearch('');
      setShowAddEmplDropdown(false);
      setAddJobSearch('');
      setShowAddJobDropdown(false);
      setShowAddModal(false);
      await fetchData();
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Failed to add row', 'error');
    } finally {
      setAdding(false);
    }
  }, [newRow, instance, accounts, fetchData, showToast, confirm]);

  const quickAddRow = useCallback(async () => {
    try {
      setAdding(true);
      // Quick Add uses the official "New" placeholder (master row 9999) so it
      // passes employee validation; swap in the real person afterwards (item 11).
      await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources/row', {
        empl_name: 'New',
        empl_code: '9999',
        'job_#': '',
        job_name: '',
        estimate_number: '',
        estimate_name: '',
        discipline: '',
        role: '',
        division: '',
        shop: '',
        allocation: null,
        note: '',
        comments: ''
      }, syncHeaders());
      showToast('Row added as "New" (9999) — click cells to pick the real person and details', 'success');
      setShowAddModal(false);
      setNewRow({ 'job_#': '', job_name: '', estimate_number: '', estimate_name: '', empl_code: '', empl_name: '', discipline: '', role: '', role_discipline: '', division: '', shop: '', start_date: '', finish_date: '', allocation: '', note: '', comments: '' });
      await fetchData();
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Failed to add row', 'error');
    } finally {
      setAdding(false);
    }
  }, [instance, accounts, fetchData, showToast]);

  const handleBulkDelete = useCallback(async () => {
    const count = selectedRows.size;
    if (count === 0) return;
    const ok = await confirm({
      title: 'Delete rows',
      message: `Delete ${count} selected row(s)? This cannot be undone.`,
      confirmText: 'Delete',
      type: 'danger'
    });
    if (!ok) return;
    try {
      // Send unique_ids for deletion
      const rowKeys = Array.from(selectedRows).map(uid => ({ unique_id: uid }));
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources/delete-batch', {
        rows: rowKeys
      }, syncHeaders());
      showToast(res?.data?.deleted ? `Deleted ${res.data.deleted} row(s)` : 'Deleted', 'success');
      setSelectedRows(new Set());
      await fetchData();
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Delete failed', 'error');
    }
  }, [selectedRows, instance, accounts, confirm, fetchData, showToast]);

  const handleSelectRow = useCallback((rk) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rk)) next.delete(rk);
      else next.add(rk);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedRows.size === filteredRows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredRows.map(r => rowKey(r))));
    }
  }, [filteredRows, selectedRows.size]);

  const handleResizeStart = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    saveColumnWidthsToHistory();
    setResizing(colKey);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = columnWidths[colKey] ?? DEFAULT_COLUMN_WIDTHS[colKey];
  }, [columnWidths, saveColumnWidthsToHistory]);

  const handleResizeMove = useCallback((e) => {
    if (resizing == null) return;
    const min = MIN_WIDTHS[resizing] ?? 50;
    const newWidth = Math.max(min, resizeStartWidth.current + (e.clientX - resizeStartX.current));
    setColumnWidths(prev => ({ ...prev, [resizing]: newWidth }));
  }, [resizing]);

  const handleResizeEnd = useCallback(() => {
    if (resizing != null) pushUndo(undoColumnResize);
    setResizing(null);
  }, [resizing, pushUndo, undoColumnResize]);

  const openFilterAtPosition = useCallback((e, columnKey) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterButtonPositions(prev => ({ ...prev, [columnKey]: { top: rect.bottom + 5, left: rect.left } }));
    setFilterSearch('');
    setOpenFilterColumn(prev => prev === columnKey ? null : columnKey);
  }, []);

  const handleDragFillStart = useCallback((e, rowIndex, weekIndex, value) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setDragFill({ active: true, startRowIndex: rowIndex, startWeekIndex: weekIndex, currentRowIndex: rowIndex, currentWeekIndex: weekIndex, startValue: value ?? '' });
    dragFillRef.current = { currentRowIndex: rowIndex, currentWeekIndex: weekIndex };
  }, []);

  const handleDragFillEnter = useCallback((rowIndex, weekIndex) => {
    if (!dragFill.active) return;
    dragFillRef.current = { currentRowIndex: rowIndex, currentWeekIndex: weekIndex };
    setDragFill(prev => prev.active ? { ...prev, currentRowIndex: rowIndex, currentWeekIndex: weekIndex } : prev);
  }, [dragFill.active]);

  const handleDragFillEnd = useCallback(async () => {
    if (!dragFill.active) return;
    const { startRowIndex, startWeekIndex, startValue } = dragFill;
    const endRowIndex = dragFillRef.current.currentRowIndex ?? dragFill.currentRowIndex ?? startRowIndex;
    const endWeekIndex = dragFillRef.current.currentWeekIndex ?? dragFill.currentWeekIndex ?? startWeekIndex;
    setDragFill({ active: false, startRowIndex: null, startWeekIndex: null, currentRowIndex: null, currentWeekIndex: null, startValue: null });
    const minRow = Math.min(startRowIndex, endRowIndex);
    const maxRow = Math.max(startRowIndex, endRowIndex);
    const minWeek = Math.min(startWeekIndex, endWeekIndex);
    const maxWeek = Math.max(startWeekIndex, endWeekIndex);
    const weekDatesOrdered = data.weekDates || [];
    const rowsOrdered = sortedRows;
    const num = parseWeekCellInput(startValue);
    const valueToFill = num === null ? '0' : String(num);
    const toSave = [];
    for (let r = minRow; r <= maxRow; r++) {
      for (let w = minWeek; w <= maxWeek; w++) {
        if (r === startRowIndex && w === startWeekIndex) continue;
        const row = rowsOrdered[r];
        const wd = weekDatesOrdered[w];
        if (row && wd) toSave.push({ row, wd, value: valueToFill });
      }
    }
    if (toSave.length === 0) return;

    // Over-allocation check for non-zero fill values
    const numVal = parseFloat(valueToFill) || 0;
    if (numVal > 0) {
      try {
        const checks = toSave.map(s => ({
          empl_code: s.row.empl_code, empl_name: s.row.empl_name,
          week_start_date: s.wd, new_value: numVal, exclude_unique_id: s.row.unique_id
        }));
        const checkRes = await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources/check-over-allocation-batch', { checks });
        if (checkRes?.data?.overAllocated) {
          const items = checkRes.data.items || [];
          const uniquePeople = [...new Set(items.map(i => i.empl_name))];
          const ok = await confirm({
            title: 'Over-Allocation Warning',
            message: (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', backgroundColor: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
                  <span style={{ fontSize: '1.5rem' }}>📋</span>
                  <div><strong style={{ fontSize: '1rem' }}>Drag Fill</strong><br /><span style={{ color: '#92400e', fontSize: '0.85rem' }}>{toSave.length} cell(s) · {uniquePeople.length} person(s) affected</span></div>
                </div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>Over-allocated weeks:</div>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {items.slice(0, 15).map((item, i) => (
                    <div key={i} style={{ padding: '8px 10px', marginBottom: 4, backgroundColor: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: '0.85rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                        <strong>{item.empl_name}</strong>
                        <span style={{ fontWeight: 700, color: '#dc2626' }}>{Math.round(item.total * 100)}%</span>
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                        Week of {item.week_start_date} — existing: {(item.conflicts || []).map(c => `${c.job} (${c.allocation})`).join(', ')}
                      </div>
                    </div>
                  ))}
                  {items.length > 15 && <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.8rem', padding: 6 }}>...and {items.length - 15} more</div>}
                </div>
              </div>
            ),
            confirmText: 'Yes, Fill Anyway',
            cancelText: 'Cancel',
            type: 'warning',
            wide: true
          });
          if (!ok) return;
        }
      } catch (checkErr) {
        console.error('Over-allocation batch check failed, proceeding:', checkErr);
      }
    }

    setData(prev => {
      const updated = { ...prev, rows: prev.rows.map(r => {
        const matches = toSave.filter(s => s.row.unique_id === r.unique_id);
        if (matches.length === 0) return r;
        const newWeeks = { ...r.weeks };
        matches.forEach(m => { newWeeks[m.wd] = m.value; });
        return { ...r, weeks: newWeeks };
      })};
      return updated;
    });
    setSelectedCell(null);
    showToast(`Filled ${toSave.length} cell(s)`, 'success');
    try {
      const cells = toSave.map(({ row, wd, value }) => ({
        unique_id: row.unique_id, empl_code: row.empl_code, empl_name: row.empl_name,
        discipline: row.discipline, role: row.role, role_discipline: row.role_discipline || null, shop: row.shop,
        'job_#': row['job_#'] || null, start_date: row.start_date || null, finish_date: row.finish_date || null,
        allocation: row.allocation != null ? row.allocation : null, note: row.note || null,
        division: row.division || null, estimate_number: row.estimate_number || null,
        estimate_name: row.estimate_name || null, job_name: row.job_name || null, comments: row.comments || null,
        week_start_date: wd, value
      }));
      await authenticatedApiCall(instance, accounts, 'POST', '/indirect-resources/batch', { cells }, syncHeaders());
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Fill failed — refreshing', 'error');
      await fetchData();
    }
  }, [dragFill.active, dragFill.startRowIndex, dragFill.startWeekIndex, data.weekDates, sortedRows, instance, accounts, fetchData, showToast, confirm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dragFill.active) {
      window.addEventListener('mouseup', handleDragFillEnd);
      return () => window.removeEventListener('mouseup', handleDragFillEnd);
    }
  }, [dragFill.active, handleDragFillEnd]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isInDragFillRange = useCallback((rowIndex, weekIndex) => {
    if (!dragFill.active) return false;
    const { startRowIndex, currentRowIndex, startWeekIndex, currentWeekIndex } = dragFill;
    const curR = currentRowIndex ?? dragFillRef.current.currentRowIndex ?? startRowIndex;
    const curW = currentWeekIndex ?? dragFillRef.current.currentWeekIndex ?? startWeekIndex;
    const minR = Math.min(startRowIndex, curR);
    const maxR = Math.max(startRowIndex, curR);
    const minW = Math.min(startWeekIndex, curW);
    const maxW = Math.max(startWeekIndex, curW);
    return rowIndex >= minR && rowIndex <= maxR && weekIndex >= minW && weekIndex <= maxW;
  }, [dragFill.active, dragFill.startRowIndex, dragFill.startWeekIndex, dragFill.currentRowIndex, dragFill.currentWeekIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll during any drag (week or frozen column) — vertical + horizontal
  useEffect(() => {
    const isDragging = dragFill.active || fixedDragFill.active;
    if (!isDragging) return;

    const SCROLL_ZONE = 50;
    const SCROLL_SPEED = 6;

    let lastMouseX = 0;
    let lastMouseY = 0;

    const doAutoScroll = () => {
      const el = tableScrollRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      let scrolled = false;

      const distFromBottom = rect.bottom - lastMouseY;
      const distFromTop = lastMouseY - rect.top;
      if (distFromBottom < SCROLL_ZONE && distFromBottom > 0) {
        el.scrollTop += Math.max(1, SCROLL_SPEED * (1 - distFromBottom / SCROLL_ZONE));
        scrolled = true;
      } else if (distFromTop < SCROLL_ZONE && distFromTop > 0) {
        el.scrollTop -= Math.max(1, SCROLL_SPEED * (1 - distFromTop / SCROLL_ZONE));
        scrolled = true;
      }

      const distFromRight = rect.right - lastMouseX;
      const distFromLeft = lastMouseX - rect.left;
      if (distFromRight < SCROLL_ZONE && distFromRight > 0) {
        el.scrollLeft += Math.max(1, SCROLL_SPEED * (1 - distFromRight / SCROLL_ZONE));
        scrolled = true;
      } else if (distFromLeft < SCROLL_ZONE && distFromLeft > 0) {
        el.scrollLeft -= Math.max(1, SCROLL_SPEED * (1 - distFromLeft / SCROLL_ZONE));
        scrolled = true;
      }

      if (scrolled) {
        dragAutoScrollRef.current = requestAnimationFrame(doAutoScroll);
      }
    };

    const handleMouseMove = (e) => {
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
      if (dragAutoScrollRef.current) cancelAnimationFrame(dragAutoScrollRef.current);
      dragAutoScrollRef.current = requestAnimationFrame(doAutoScroll);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (dragAutoScrollRef.current) cancelAnimationFrame(dragAutoScrollRef.current);
    };
  }, [dragFill.active, fixedDragFill.active]);

  // Frozen column vertical drag-fill handlers
  const handleFixedDragFillStart = useCallback((e, rowIndex, columnKey, value) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setFixedDragFill({ active: true, startRowIndex: rowIndex, currentRowIndex: rowIndex, columnKey, startValue: value ?? '' });
    fixedDragFillRef.current = { currentRowIndex: rowIndex };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleFixedDragFillEnter = useCallback((rowIndex) => {
    if (!fixedDragFill.active) return;
    fixedDragFillRef.current = { currentRowIndex: rowIndex };
    setFixedDragFill(prev => prev.active ? { ...prev, currentRowIndex: rowIndex } : prev);
  }, [fixedDragFill.active]);

  const handleFixedDragFillEnd = useCallback(async () => {
    if (!fixedDragFill.active) return;
    const { startRowIndex, columnKey, startValue } = fixedDragFill;
    const endRowIndex = fixedDragFillRef.current.currentRowIndex ?? fixedDragFill.currentRowIndex ?? startRowIndex;
    setFixedDragFill({ active: false, startRowIndex: null, currentRowIndex: null, columnKey: null, startValue: null });
    const minRow = Math.min(startRowIndex, endRowIndex);
    const maxRow = Math.max(startRowIndex, endRowIndex);
    const rowsOrdered = sortedRows;
    const toUpdate = [];
    for (let r = minRow; r <= maxRow; r++) {
      if (r === startRowIndex) continue;
      const row = rowsOrdered[r];
      if (row) toUpdate.push(row);
    }
    if (toUpdate.length === 0) return;
    setData(prev => {
      const updated = { ...prev, rows: prev.rows.map(r => {
        if (!toUpdate.some(u => u.unique_id === r.unique_id)) return r;
        return { ...r, [columnKey]: startValue };
      })};
      return updated;
    });
    setSelectedCell(null);
    showToast(`Filled ${toUpdate.length} row(s)`, 'success');
    try {
      await Promise.all(toUpdate.map(row => {
        const newRole = columnKey === 'role' ? startValue : (row.role ?? '');
        const newDiscipline = columnKey === 'discipline' ? startValue : (row.discipline ?? '');
        const autoRD = [newRole, newDiscipline].filter(Boolean).join(' - ');
        const changedDomains = [];
        if (columnKey === 'empl_code' || columnKey === 'empl_name') changedDomains.push('employee');
        if (columnKey === 'job_#' || columnKey === 'job_name') changedDomains.push('job', 'estimate');
        if (columnKey === 'estimate_number' || columnKey === 'estimate_name') changedDomains.push('estimate');
        const payload = {
          unique_id: row.unique_id,
          'job_#': columnKey === 'job_#' ? startValue : (row['job_#'] ?? ''),
          job_name: columnKey === 'job_name' ? startValue : (row.job_name ?? ''),
          estimate_number: columnKey === 'estimate_number' ? startValue : (row.estimate_number ?? ''),
          estimate_name: columnKey === 'estimate_name' ? startValue : (row.estimate_name ?? ''),
          empl_code: columnKey === 'empl_code' ? startValue : (row.empl_code ?? ''),
          empl_name: columnKey === 'empl_name' ? startValue : (row.empl_name ?? ''),
          discipline: newDiscipline,
          role: newRole,
          role_discipline: columnKey === 'role_discipline' ? startValue : (columnKey === 'role' || columnKey === 'discipline' ? autoRD : (row.role_discipline ?? '')),
          division: columnKey === 'division' ? startValue : (row.division ?? ''),
          shop: columnKey === 'shop' ? startValue : (row.shop ?? ''),
          start_date: columnKey === 'start_date' ? (startValue || null) : (row.start_date || null),
          finish_date: columnKey === 'finish_date' ? (startValue || null) : (row.finish_date || null),
          allocation: columnKey === 'allocation' ? (startValue != null && startValue !== '' ? parseFloat(startValue) : null) : (row.allocation != null ? row.allocation : null),
          note: columnKey === 'note' ? (startValue ?? '') : (row.note ?? ''),
          comments: columnKey === 'comments' ? (startValue ?? '') : (row.comments ?? ''),
          changedDomains: [...new Set(changedDomains)],
        };
        return authenticatedApiCall(instance, accounts, 'PUT', '/indirect-resources/row', payload, syncHeaders());
      }));
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.message || 'Fill failed — refreshing', 'error');
      await fetchData();
    }
  }, [fixedDragFill.active, fixedDragFill.startRowIndex, fixedDragFill.columnKey, fixedDragFill.startValue, fixedDragFill.currentRowIndex, sortedRows, instance, accounts, fetchData, showToast, syncHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fixedDragFill.active) {
      window.addEventListener('mouseup', handleFixedDragFillEnd);
      return () => window.removeEventListener('mouseup', handleFixedDragFillEnd);
    }
  }, [fixedDragFill.active, handleFixedDragFillEnd]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isInFixedDragFillRange = useCallback((rowIndex, columnKey) => {
    if (!fixedDragFill.active || fixedDragFill.columnKey !== columnKey) return false;
    const { startRowIndex } = fixedDragFill;
    const curR = fixedDragFill.currentRowIndex ?? fixedDragFillRef.current.currentRowIndex ?? startRowIndex;
    const minR = Math.min(startRowIndex, curR);
    const maxR = Math.max(startRowIndex, curR);
    return rowIndex >= minR && rowIndex <= maxR;
  }, [fixedDragFill.active, fixedDragFill.startRowIndex, fixedDragFill.currentRowIndex, fixedDragFill.columnKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resizing == null) return;
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizing, handleResizeMove, handleResizeEnd]);

  useEffect(() => {
    if (!showAddEmplDropdown || !showAddModal) return;
    const onMouseDown = (e) => {
      if (addModalRef.current && !addModalRef.current.contains(e.target)) setShowAddEmplDropdown(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowAddEmplDropdown(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showAddEmplDropdown, showAddModal]);

  if (loading && !data.rows?.length) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', color: '#c00' }}>{error}</div>
    );
  }

  const { windowStart, weekDates, rows: rawRows } = data;
  const rows = sortedRows;
  const weekWidth = columnWidths.week ?? DEFAULT_COLUMN_WIDTHS.week;
  const totalWeekWidth = weekDates.length * weekWidth;
  const fixedWidth = FIXED_COL_KEYS
    .filter(k => visibleColumns[k])
    .reduce((sum, k) => sum + (columnWidths[k] ?? DEFAULT_COLUMN_WIDTHS[k]), 0);
  const checkboxWidth = 44;

  const renderFixedCellEditor = (row, k) => {
    if (k === 'plan_status') {
      return (
        <select value={editFixedValue || resolvePlanStatus(row)} onChange={(e) => { setEditFixedValue(e.target.value); saveFixedCell(row, k, e.target.value); setEditingFixedCell(null); }} onBlur={() => handleFixedCellBlur(row, k)} autoFocus style={{ width: '100%', minWidth: 0, padding: '0.25rem 0.35rem', border: '1px solid #0d6efd', borderRadius: 2, boxSizing: 'border-box', fontSize: '0.85rem', backgroundColor: '#fff' }}>
          {PLAN_STATUS_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
        </select>
      );
    }
    if (k === 'allocation') {
      return (
        <select value={editFixedValue} onChange={(e) => { setEditFixedValue(e.target.value); saveFixedCell(row, k, e.target.value || null); setEditingFixedCell(null); }} onBlur={() => handleFixedCellBlur(row, k)} autoFocus style={{ width: '100%', minWidth: 0, padding: '0.25rem 0.35rem', border: '1px solid #0d6efd', borderRadius: 2, boxSizing: 'border-box', fontSize: '0.85rem', backgroundColor: '#fff' }}>
          <option value="">—</option>
          {ALLOCATION_OPTIONS.map(v => (<option key={v} value={v}>{v}</option>))}
        </select>
      );
    }
    if (k === DIVISION_COL) {
      return (
        <select value={editFixedValue} onChange={(e) => { setEditFixedValue(e.target.value); saveFixedCell(row, k, e.target.value || null); setEditingFixedCell(null); }} onBlur={() => handleFixedCellBlur(row, k)} autoFocus style={{ width: '100%', minWidth: 0, padding: '0.25rem 0.35rem', border: '1px solid #0d6efd', borderRadius: 2, boxSizing: 'border-box', fontSize: '0.85rem', backgroundColor: '#fff' }}>
          <option value="">—</option>
          {divisionsList.map(d => { const label = d.division_name + (d.location ? ` (${d.location})` : ''); return (<option key={d.id} value={label}>{label}</option>); })}
        </select>
      );
    }
    if (EMPL_AUTOCOMPLETE_COLS.has(k) || JOB_AUTOCOMPLETE_COLS.has(k)) {
      const isEditing = editingFixedCell?.columnKey === k;
      return (
        <div style={{ position: 'relative' }}>
          <input
            value={editFixedValue}
            onChange={(e) => {
              const v = e.target.value;
              setEditFixedValue(v);
              searchInlineSuggestions(v, k);
            }}
            onFocus={() => searchInlineSuggestions(editFixedValue, k)}
            onBlur={() => handleFixedCellBlur(row, k)}
            onKeyDown={(e) => handleFixedCellKeyDown(e, row, k)}
            autoFocus
            type="text"
            style={{ width: '100%', minWidth: 0, padding: '0.25rem 0.35rem', border: '1px solid #0d6efd', borderRadius: 2, boxSizing: 'border-box', fontSize: '0.85rem' }}
            placeholder={EMPL_AUTOCOMPLETE_COLS.has(k) ? 'Search employees...' : 'Search jobs...'}
          />
          {isEditing && showInlineSuggestions && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, minWidth: 260, maxHeight: 220, overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999 }}
              onMouseDown={(e) => e.preventDefault()}>
              {inlineSuggestionsLoading ? (
                <div style={{ padding: '0.5rem', textAlign: 'center', color: '#666', fontSize: '0.8rem' }}>Searching...</div>
              ) : inlineSuggestions.length > 0 ? (
                inlineSuggestions.map((s, si) => (
                  <div key={si}
                    onMouseDown={(e) => { e.preventDefault(); selectInlineSuggestion(s, row); }}
                    style={{ padding: '0.35rem 0.5rem', cursor: 'pointer', fontSize: '0.8rem', borderBottom: si < inlineSuggestions.length - 1 ? '1px solid #eee' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', backgroundColor: s.existing ? '#f0fdf4' : 'transparent' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e8f0fe'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = s.existing ? '#f0fdf4' : 'transparent'; }}>
                    {s.type === 'employee' ? (
                      <><strong>{s.emp_code}</strong> — {s.emp_name} <span style={{ color: '#888' }}>({[s.discipline_code || s.trade, s.role_code].filter(Boolean).join(' · ')})</span>{s.existing && <span style={{ color: '#16a34a', fontSize: '0.7rem', marginLeft: 4 }}>●</span>}</>
                    ) : (
                      <><strong>{s.job_number || <span style={{ color: '#dc3545' }}>Not Awarded</span>}</strong> — {s.job_name || ''} <span style={{ color: '#888' }}>{s.estimate_number ? `Est: ${s.estimate_number}` : ''}</span>{s.existing && <span style={{ color: '#16a34a', fontSize: '0.7rem', marginLeft: 4 }}>●</span>}</>
                    )}
                  </div>
                ))
              ) : editFixedValue.length >= 1 ? (
                <div style={{ padding: '0.5rem', textAlign: 'center', color: '#999', fontSize: '0.8rem' }}>No matches found</div>
              ) : null}
            </div>
          )}
        </div>
      );
    }
    return (
      <input value={editFixedValue} onChange={(e) => setEditFixedValue(e.target.value)} onBlur={() => handleFixedCellBlur(row, k)} onKeyDown={(e) => handleFixedCellKeyDown(e, row, k)} autoFocus type="text" list={k === 'discipline' ? 'irm-legend-disciplines' : k === 'role' ? 'irm-legend-roles' : undefined} style={{ width: '100%', minWidth: 0, padding: '0.25rem 0.35rem', border: '1px solid #0d6efd', borderRadius: 2, boxSizing: 'border-box', fontSize: '0.85rem' }} />
    );
  };

  return (
    <div style={{ padding: '1rem', maxWidth: '100%' }}>
      {toast.show && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', padding: '1rem 1.5rem',
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545',
          color: 'white', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999
        }}>
          {toast.message}
        </div>
      )}

      <ConfirmDialog {...dialogProps} />

      {/* Legend code suggestions for Discipline / Role editing (item 10) */}
      <datalist id="irm-legend-disciplines">
        {LEGEND_DISCIPLINES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
      </datalist>
      <datalist id="irm-legend-roles">
        {LEGEND_ROLES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
      </datalist>

      {/* Header — compact: title + window on one line (item 9, 2026-08-10) */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem 1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, color: '#333', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            📋 Indirect Resources Management
          </h2>
          <span style={{ color: '#666', fontSize: '0.85rem' }}>
            {windowStart && weekDates?.length ? `${weekDates.length}-week window: ${formatWeekLabel(windowStart)} → ${formatWeekLabel(weekDates[weekDates.length - 1])}` : 'Loading...'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          <button onClick={() => setShowImportModal(true)} style={toolbarBtnStyle} title="Import from Excel">📥 Import</button>
          <button onClick={handleExport} style={toolbarBtnStyle} title="Export to Excel">📤 Export</button>
          <button onClick={handleDownloadTemplate} style={toolbarBtnStyle} title="Download empty template">📄 Template</button>
          <button onClick={() => setShowColumnConfig(!showColumnConfig)} style={toolbarBtnStyle} title="Column visibility">⚙️ Columns</button>
          <button onClick={autoFitColumns} title="Auto-fit column widths" style={toolbarBtnStyle}>↔️ Auto-fit</button>
          <button onClick={resetColumnWidths} title="Reset column widths to default" style={toolbarBtnStyle}>🔄 Reset</button>
          <button onClick={runUndo} disabled={undoStack.length === 0} title="Undo last action (Ctrl+Z)" style={{ ...toolbarBtnStyle, opacity: undoStack.length === 0 ? 0.6 : 1 }}>↩️ Undo</button>
          <button onClick={() => setShowHelpModal(true)} title="Help" style={toolbarBtnStyle}>❓ Help</button>
          <button onClick={() => { setAddEmplSearch(''); setShowAddEmplDropdown(false); setEmplSearchResults([]); setAddJobSearch(''); setShowAddJobDropdown(false); setJobSearchResults([]); setAddEstSearch(''); setShowAddEstDropdown(false); setEstSearchResults([]); setShowAddModal(true); }} style={toolbarBtnStyle}>➕ Add Row</button>
          {selectedRows.size > 0 && (
            <button onClick={handleBulkDelete} style={{ ...toolbarBtnStyle, backgroundColor: '#dc3545', color: 'white', border: '1px solid #dc3545' }} title="Delete selected rows">🗑️ Delete ({selectedRows.size})</button>
          )}
          <button onClick={() => fetchData()} style={toolbarBtnStyle} title="Refresh data">↻ Refresh</button>
          <button onClick={() => setShowMonthly(!showMonthly)} style={{ ...toolbarBtnStyle, backgroundColor: showMonthly ? '#e3f2fd' : 'white', borderColor: showMonthly ? '#2196f3' : '#d1d5db' }} title="Toggle monthly summary view">📅 {showMonthly ? 'Weekly' : 'Monthly'}</button>
          <button onClick={() => setShowAnalyticsModal(true)} style={toolbarBtnStyle} title="Histogram, pivot table, over- and under-allocation">📊 Analytics</button>
          <button onClick={() => { setShowAuditModal(true); fetchAuditLog(); }} style={toolbarBtnStyle} title="View change history">📜 Audit Log</button>
        </div>
      </div>

      {/* Column Config Modal */}
      {showColumnConfig && (
        <div style={modalOverlayStyle} onClick={() => setShowColumnConfig(false)}>
          <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Column Configuration</h3>
              <button onClick={() => setShowColumnConfig(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <ColumnConfigPanel
              columns={applyColumnOrder(FIXED_COL_KEYS.map(k => ({ key: k, label: COLUMN_LABELS[k] })), columnOrder)}
              isVisible={(key) => !!visibleColumns[key]}
              onToggle={(key) => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }))}
              onMove={moveColumn}
              onShowAll={() => setVisibleColumns(prev => FIXED_COL_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {}))}
              onHideAll={() => setVisibleColumns(prev => FIXED_COL_KEYS.reduce((acc, k) => ({ ...acc, [k]: false }), {}))}
              compact={false}
            />
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button onClick={() => setShowColumnConfig(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Filters — compact paddings (item 9, 2026-08-10) */}
      <div style={{
        backgroundColor: '#f8f9fa', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '0.6rem',
        display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', alignItems: 'center'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Search</label>
          <input type="text" placeholder="Search all columns..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={filterInputStyle} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Allocation</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#374151', fontWeight: 600, cursor: 'pointer', height: 30 }} title="Show only rows whose Allocation column (next 12 months) is above 0">
            <input type="checkbox" checked={allocAboveZero} onChange={(e) => setAllocAboveZero(e.target.checked)} />
            above 0
          </label>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Sort</label>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#374151', fontWeight: 600, cursor: 'pointer', height: 30 }}
            title="Put people with allocation in the first visible week (this Monday, or your From date) at the top, then the next week, and so on. Click a column header to sort by that column instead."
          >
            <input
              type="checkbox"
              checked={sortByRecentAlloc}
              onChange={(e) => {
                const on = e.target.checked;
                setSortByRecentAlloc(on);
                if (on) setSortColumn(null);
              }}
            />
            current week first
          </label>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>From date</label>
          <input type="date" value={dateRangeFrom} onChange={(e) => { setDateRangeFrom(e.target.value); setMonthsAhead(null); }} style={filterInputStyle} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Months ahead</label>
          <select value={monthsAhead ?? ''} onChange={(e) => setMonthsAhead(e.target.value ? parseInt(e.target.value, 10) : null)} style={filterInputStyle} title="Set To date to X months from From date">
            <option value="">—</option>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (<option key={n} value={n}>{n} month{n > 1 ? 's' : ''}</option>))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>To date</label>
          <input type="date" value={dateRangeTo} onChange={(e) => { setDateRangeTo(e.target.value); setMonthsAhead(null); }} style={filterInputStyle} />
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <span style={{ fontSize: '0.9rem', color: '#666' }}>
            {hasActiveFilters ? `Showing ${rows.length} of ${rawRows.length} rows` : `Total: ${rows.length} rows`}
          </span>
        </div>
      </div>

      {/* Active filters chips - same as Selected Contractors */}
      {hasActiveFilters && (
        <div style={{
          position: 'fixed', top: '60px', right: '40px', zIndex: 1000,
          display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px 12px',
          backgroundColor: '#d1fae5', borderRadius: '8px', border: '1px solid #a7f3d0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxWidth: '500px', alignItems: 'center'
        }}>
          <span style={{ fontSize: '12px', color: '#065f46', fontWeight: '600' }}>🔍 Filters:</span>
          {activeFilters.map((filter, idx) => (
            <span key={idx} style={{ padding: '2px 8px', backgroundColor: filter.type === 'values' ? '#17a2b8' : '#28a745', color: 'white', borderRadius: '12px', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              {COLUMN_LABELS[filter.key] || filter.key}: {filter.type === 'values' ? `${filter.count} selected` : `${filter.mode} "${filter.value}"`}
              <button type="button" onClick={() => clearColumnFilter(filter.key)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 0, fontSize: '12px', lineHeight: 1, fontWeight: 'bold' }}>×</button>
            </span>
          ))}
          {searchTerm && (
            <span style={{ padding: '2px 8px', backgroundColor: '#007bff', color: 'white', borderRadius: '12px', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              Search: "{searchTerm}"
              <button type="button" onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 0, fontSize: '12px', lineHeight: 1, fontWeight: 'bold' }}>×</button>
            </span>
          )}
          {allocAboveZero && (
            <span style={{ padding: '2px 8px', backgroundColor: '#0d6efd', color: 'white', borderRadius: '12px', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              Allocation &gt; 0
              <button type="button" onClick={() => setAllocAboveZero(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 0, fontSize: '12px', lineHeight: 1, fontWeight: 'bold' }}>×</button>
            </span>
          )}
          <button type="button" onClick={clearAllFilters} style={{ padding: '2px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>× Clear All</button>
        </div>
      )}

      {/* Per-column filter dropdown (portal) - uses FilterTemplateDropdown from Development */}
      {openFilterColumn && (
        <FilterTemplateDropdown
          columnKey={openFilterColumn}
          columnLabel={COLUMN_LABELS[openFilterColumn] || openFilterColumn}
          showTextFilters={true}
          isNumericColumn={NUMERIC_FILTER_COLS.includes(openFilterColumn)}
          columnFilters={columnFilters}
          filterMode={filterMode}
          filterText={filterText}
          filterSearch={filterSearch}
          filterButtonPositions={filterButtonPositions}
          setOpenFilterColumn={setOpenFilterColumn}
          setFilterSearch={setFilterSearch}
          getUniqueValues={getUniqueValues}
          handleFilterToggle={handleFilterToggle}
          selectAllColumnFilter={selectAllColumnFilter}
          clearColumnFilter={clearColumnFilter}
          applyTextFilter={applyTextFilter}
        />
      )}

      {auditCallout && (
        <div style={{
          marginBottom: '0.65rem', padding: '0.7rem 0.9rem',
          backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8,
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.8rem', color: '#92400e', fontWeight: 600, marginBottom: 4 }}>
              {[auditCallout.person, auditCallout.job ? `Job ${auditCallout.job}` : null, auditCallout.action]
                .filter(Boolean).join(' · ')}
              {auditCallout.when ? (
                <span style={{ fontWeight: 500, color: '#b45309' }}>
                  {` · ${auditCallout.when}${auditCallout.by ? ` by ${String(auditCallout.by).split('@')[0]}` : ''}`}
                </span>
              ) : null}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.9rem' }}>
              {(auditCallout.lines || []).length === 0 ? (
                <span style={{ color: '#6b7280' }}>Jumped to this row — no from/to values were stored on this older entry.</span>
              ) : (
                (auditCallout.lines || []).map((line, i) => (
                  <div key={i}><AuditFromTo line={line} /></div>
                ))
              )}
            </div>
          </div>
          {isAuditSuperadmin && auditCallout.entry && isAuditRevertable(auditCallout.entry) && (
            <button
              onClick={() => handleAuditRevert(auditCallout.entry)}
              disabled={revertingAuditId === auditCallout.entry.id}
              title="Restore the previous value (superadmin only)"
              style={{
                padding: '5px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                border: '1px solid #c7d2fe', borderRadius: 6, alignSelf: 'center',
                backgroundColor: revertingAuditId === auditCallout.entry.id ? '#eef2ff' : '#fff', color: '#4338ca',
              }}
            >
              {revertingAuditId === auditCallout.entry.id ? 'Reverting…' : '↩ Revert'}
            </button>
          )}
          <button
            onClick={() => { setAuditCallout(null); setHighlightedRowId(null); }}
            title="Dismiss"
            style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#92400e', lineHeight: 1, padding: '0 2px' }}
          >×</button>
        </div>
      )}

      {auditCallout?.anchor && auditCallout.lines?.length > 0 && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: Math.max(8, auditCallout.anchor.top - 46),
          left: Math.min(window.innerWidth - 300, Math.max(8, auditCallout.anchor.left - 130)),
          zIndex: 1200,
          backgroundColor: '#fffbeb',
          color: '#111827',
          padding: '6px 10px',
          borderRadius: 8,
          fontSize: '12px',
          border: '1px solid #f59e0b',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          pointerEvents: 'none',
          maxWidth: 280,
        }}>
          <AuditFromTo line={auditCallout.lines[0]} />
        </div>,
        document.body
      )}

      {/* Table container: sticky left columns + scrollable week columns (same pattern as Selected Contractors) */}
      <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', backgroundColor: '#fff', overflow: 'hidden' }}>
        <div ref={tableScrollRef} style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '749px' }}>
          {showMonthly ? (
          /* Monthly view: same frozen-left layout as weekly, with sort/filter/resize on fixed cols, two-layer year+month header, editable fixed cells */
          (() => {
            const monthGroups = getMonthGroups(weekDates);
            const monthlyData = rows.map(row => {
              const monthAvgs = {};
              let startIdx = 0;
              monthGroups.forEach(mg => {
                const monthKey = `${mg.year}-${mg.month}`;
                const weekSlice = weekDates.slice(startIdx, startIdx + mg.count);
                const values = weekSlice.map(wd => parseFloat(row.weeks?.[wd]) || 0);
                const avg = values.length > 0 ? values.reduce((s, v) => s + v, 0) / values.length : 0;
                monthAvgs[monthKey] = Math.round(avg * 100) / 100;
                startIdx += mg.count;
              });
              return { ...row, monthAvgs };
            });
            const fixedCols = visibleFixedKeys;
            const monthlyFixedWidth = fixedCols.reduce((s, k) => s + (columnWidths[k] ?? DEFAULT_COLUMN_WIDTHS[k]), 0) + checkboxWidth;
            const monthColWidth = 46;
            const totalMonthWidth = monthGroups.length * monthColWidth;
            const yearGroups = [];
            let curYear = null, yCols = 0;
            monthGroups.forEach(mg => {
              if (mg.year !== curYear) {
                if (curYear !== null) yearGroups.push({ year: curYear, colSpan: yCols });
                curYear = mg.year; yCols = 1;
              } else { yCols++; }
            });
            if (curYear !== null) yearGroups.push({ year: curYear, colSpan: yCols });
            return (
              <div style={{ display: 'flex', minWidth: checkboxWidth + monthlyFixedWidth + GAP_SPACER_WIDTH + totalMonthWidth }}>
                {/* FROZEN left columns */}
                <div style={{ flexShrink: 0, width: monthlyFixedWidth, position: 'sticky', left: 0, zIndex: 100, borderRight: '2px solid #dee2e6', boxShadow: '2px 0 4px rgba(0,0,0,0.06)', backgroundColor: '#fff' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', fontSize: '0.85rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 101, backgroundColor: '#e9ecef', isolation: 'isolate', boxShadow: '3px 0 0 0 #e9ecef' }}>
                      <tr style={{ backgroundColor: '#e9ecef', height: DATE_HEADER_HEIGHT }}>
                        <th rowSpan={2} style={{ width: checkboxWidth, minWidth: checkboxWidth, height: DATE_HEADER_HEIGHT, padding: '0.35rem', borderBottom: '2px solid #dee2e6', borderRight: '1px solid #dee2e6', backgroundColor: '#e9ecef', verticalAlign: 'middle' }}>
                          <input type="checkbox" checked={monthlyData.length > 0 && selectedRows.size === monthlyData.length} onChange={handleSelectAll} style={{ cursor: 'pointer' }} />
                        </th>
                        {fixedCols.map((k, i) => {
                          const w = columnWidths[k] ?? DEFAULT_COLUMN_WIDTHS[k];
                          const hasFilter = (columnFilters[k]?.length > 0) || filterMode[k];
                          return (
                            <th key={k} rowSpan={2} style={{ width: w, minWidth: MIN_WIDTHS[k], height: DATE_HEADER_HEIGHT, padding: '0.35rem', borderBottom: '2px solid #dee2e6', borderRight: i === fixedCols.length - 1 ? 'none' : '1px solid #dee2e6', backgroundColor: '#e9ecef', verticalAlign: 'middle' }}>
                              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                <span onClick={() => handleSort(k)} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTitle(k)}>{COLUMN_LABELS[k]}{getSortIndicator(k)}</span>
                                <span onClick={(e) => openFilterAtPosition(e, k)} style={{ cursor: 'pointer', opacity: hasFilter ? 1 : 0.7, display: 'flex', padding: '2px' }} title="Filter column">
                                  <FilterIcon isActive={hasFilter} color="#495057" />
                                </span>
                                <div onMouseDown={(e) => handleResizeStart(e, k)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', zIndex: 2 }} title="Drag to resize column" />
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.length === 0 ? (
                        <tr><td colSpan={fixedCols.length + 1} style={{ padding: '2rem', textAlign: 'center', color: '#666', height: 200 }}>No data.</td></tr>
                      ) : (
                        monthlyData.map((row, idx) => {
                          const rk = rowKey(row);
                          const isAuditHi = highlightedRowId === rk;
                          const rowBg = isAuditHi ? '#fde68a' : (idx % 2 === 0 ? '#fff' : '#f8f9fa');
                          return (
                            <tr key={rk + '-m-' + idx} data-row-id={rk} style={{ backgroundColor: rowBg, height: BODY_ROW_HEIGHT, lineHeight: 1 }}>
                              <td style={{ width: checkboxWidth, minWidth: checkboxWidth, height: BODY_ROW_HEIGHT, maxHeight: BODY_ROW_HEIGHT, padding: '0.15rem 0.4rem', borderBottom: '1px solid #eee', borderRight: '1px solid #dee2e6', backgroundColor: rowBg, verticalAlign: 'middle', boxSizing: 'border-box', overflow: 'hidden' }}>
                                <input type="checkbox" checked={selectedRows.has(rk)} onChange={() => handleSelectRow(rk)} style={{ cursor: 'pointer' }} />
                              </td>
                              {fixedCols.map((k, i) => {
                                const w = columnWidths[k] ?? DEFAULT_COLUMN_WIDTHS[k];
                                const isReadOnly = k === 'last_modified' || k === 'unique_id' || k === 'allocation';
                                const isEditingFixed = !isReadOnly && editingFixedCell?.rowKey === rk && editingFixedCell?.columnKey === k;
                                const isSavingFixed = savingFixedCell === `${rk}-${k}` || savingFixedCell === `${rk}-multi`;
                                const jobNameFallback = k === 'job_name' ? resolveJobNameDisplay(row) : null;
                                const isStatusCol = k === 'plan_status';
                                const statusVal = resolvePlanStatus(row);
                                const statusStyle = PLAN_STATUS_STYLES[statusVal] || PLAN_STATUS_STYLES.estimate;
                                const displayVal = k === 'last_modified' ? formatLastModified(row) : k === 'empl_code' ? formatEmplCode(row[k]) : k === 'job_name' ? jobNameFallback.text : k === 'allocation' ? String(computeAllocation(row, data.weekDates)) : isStatusCol ? planStatusLabel(statusVal) : (row[k] != null ? String(row[k]) : '');
                                const isDerivedJobName = !!jobNameFallback?.derived;
                                return (
                                  <td key={k} data-col={k} style={{ position: 'relative', width: w, minWidth: MIN_WIDTHS[k], height: BODY_ROW_HEIGHT, maxHeight: BODY_ROW_HEIGHT, padding: '0.15rem 0.4rem', borderBottom: '1px solid #eee', borderRight: i === fixedCols.length - 1 ? 'none' : '1px solid #dee2e6', fontSize: '0.85rem', overflow: 'visible', textOverflow: 'ellipsis', whiteSpace: 'nowrap', backgroundColor: rowBg, verticalAlign: 'middle', boxSizing: 'border-box' }}>
                                    {isEditingFixed ? renderFixedCellEditor(row, k) : isStatusCol ? (
                                      <div onClick={() => { if (isReadOnly) return; setEditingCell(null); setEditingCellPosition(null); setEditingFixedCell({ rowKey: rk, columnKey: k }); setEditFixedValue(statusVal); setSelectedCell(null); }} title="Estimate / Awarded / Completed — completed jobs are left off the biweekly plan email" style={{ minHeight: 10, cursor: 'pointer' }}>
                                        <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, color: statusStyle.color, backgroundColor: statusStyle.backgroundColor }}>{displayVal}</span>
                                      </div>
                                    ) : (
                                      <div onClick={() => { if (isReadOnly) return; setEditingCell(null); setEditingCellPosition(null); setEditingFixedCell({ rowKey: rk, columnKey: k }); setEditFixedValue(row[k] != null ? String(row[k]) : ''); setSelectedCell(null); setInlineSuggestions([]); setShowInlineSuggestions(false); }} style={{ cursor: isReadOnly ? 'default' : 'pointer', minHeight: 10, color: isReadOnly ? '#999' : (isDerivedJobName ? '#6c757d' : undefined), fontStyle: isDerivedJobName ? 'italic' : undefined, fontSize: isReadOnly ? '0.75rem' : undefined }} title={isReadOnly ? displayVal : (isDerivedJobName ? 'From Estimate Name (no Description on record) — click to edit' : 'Click to edit')}>
                                        {isSavingFixed ? '...' : displayVal}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                {/* SCROLLABLE month columns (borderCollapse 'separate' so sticky header borders stay opaque) */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', tableLayout: 'fixed', fontSize: '0.85rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 50, backgroundColor: '#e9ecef', isolation: 'isolate' }}>
                      <tr style={{ backgroundColor: '#90caf9' }}>
                        {yearGroups.map((yg, i) => (
                          <th key={i} colSpan={yg.colSpan} style={{ textAlign: 'center', padding: '0.25rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6', backgroundColor: '#90caf9', fontWeight: 600, fontSize: '0.8rem' }}>{yg.year}</th>
                        ))}
                      </tr>
                      <tr style={{ backgroundColor: '#90caf9' }}>
                        {monthGroups.map((mg, i) => (
                          <th key={i} style={{ textAlign: 'center', padding: '0.25rem', borderBottom: '2px solid #dee2e6', borderRight: '1px solid #dee2e6', minWidth: monthColWidth, backgroundColor: '#90caf9', fontWeight: 500, fontSize: '0.8rem' }}>{mg.month}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.length === 0 ? (
                        <tr><td colSpan={monthGroups.length} style={{ height: 200 }} /></tr>
                      ) : (
                        monthlyData.map((row, idx) => (
                          <tr key={rowKey(row) + '-ms-' + idx} data-row-id={rowKey(row)} style={{ backgroundColor: highlightedRowId === rowKey(row) ? '#fde68a' : (idx % 2 === 0 ? '#fff' : '#f8f9fa'), height: BODY_ROW_HEIGHT, lineHeight: 1 }}>
                            {monthGroups.map((mg, i) => {
                              const val = row.monthAvgs[`${mg.year}-${mg.month}`];
                              return (
                                <td key={i} style={{ textAlign: 'center', padding: '0.15rem', borderBottom: '1px solid #eee', borderRight: '1px solid #dee2e6', backgroundColor: getWeekCellColor(val), fontWeight: 500, height: BODY_ROW_HEIGHT, boxSizing: 'border-box' }}>
                                  {formatWeekCellDisplay(val)}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()
          ) : (
          <div style={{ display: 'flex', minWidth: checkboxWidth + fixedWidth + totalWeekWidth }}>
            {/* FROZEN - sticky, solid right border (Debugging: no table-based sticky at boundary) */}
            <div style={{ flexShrink: 0, width: checkboxWidth + fixedWidth, position: 'sticky', left: 0, zIndex: 100, borderRight: '2px solid #dee2e6', boxShadow: '2px 0 4px rgba(0,0,0,0.06)', backgroundColor: '#fff' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', fontSize: '0.85rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 101, backgroundColor: '#e9ecef', isolation: 'isolate', boxShadow: '3px 0 0 0 #e9ecef' }}>
                  <tr style={{ backgroundColor: '#e9ecef', height: DATE_HEADER_HEIGHT }}>
                    <th rowSpan={3} style={{ width: checkboxWidth, minWidth: checkboxWidth, height: DATE_HEADER_HEIGHT, padding: '0.35rem', borderBottom: '2px solid #dee2e6', borderRight: '1px solid #dee2e6', backgroundColor: '#e9ecef', verticalAlign: 'middle' }}>
                      <input type="checkbox" checked={rows.length > 0 && selectedRows.size === rows.length} onChange={handleSelectAll} style={{ cursor: 'pointer' }} />
                    </th>
                    {visibleFixedKeys.map((k, i) => {
                      const w = columnWidths[k] ?? DEFAULT_COLUMN_WIDTHS[k];
                      const hasFilter = (columnFilters[k]?.length > 0) || filterMode[k];
                      return (
                        <th key={k} rowSpan={3} style={{ width: w, minWidth: MIN_WIDTHS[k], height: DATE_HEADER_HEIGHT, padding: '0.35rem', borderBottom: '2px solid #dee2e6', borderRight: i === visibleFixedKeys.length - 1 ? 'none' : '1px solid #dee2e6', backgroundColor: '#e9ecef', verticalAlign: 'middle' }}>
                          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            <span onClick={() => handleSort(k)} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTitle(k)}>{COLUMN_LABELS[k]}{getSortIndicator(k)}</span>
                            <span onClick={(e) => openFilterAtPosition(e, k)} style={{ cursor: 'pointer', opacity: hasFilter ? 1 : 0.7, display: 'flex', padding: '2px' }} title="Filter column">
                              <FilterIcon isActive={hasFilter} color="#495057" />
                            </span>
                            <div onMouseDown={(e) => handleResizeStart(e, k)} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', zIndex: 2 }} title="Drag to resize column" />
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={visibleFixedKeys.length + 1} style={{ padding: '2rem', textAlign: 'center', color: '#666', height: 200 }}>No data.</td></tr>
                  ) : (
                    rows.map((row, idx) => {
                      const rk = rowKey(row);
                      const isAuditHi = highlightedRowId === rk;
                      const rowBg = isAuditHi ? '#fde68a' : (idx % 2 === 0 ? '#fff' : '#f8f9fa');
                      return (
                        <tr key={rk + '-' + idx} data-row-id={rk} style={{ backgroundColor: rowBg, height: BODY_ROW_HEIGHT, lineHeight: 1, boxShadow: isAuditHi ? 'inset 0 0 0 2px #f59e0b' : undefined }}>
                          <td style={{ width: checkboxWidth, minWidth: checkboxWidth, height: BODY_ROW_HEIGHT, maxHeight: BODY_ROW_HEIGHT, padding: '0.15rem 0.4rem', borderBottom: '1px solid #eee', borderRight: '1px solid #dee2e6', backgroundColor: rowBg, verticalAlign: 'middle', boxSizing: 'border-box', overflow: 'hidden' }}>
                            <input type="checkbox" checked={selectedRows.has(rk)} onChange={() => handleSelectRow(rk)} style={{ cursor: 'pointer' }} />
                          </td>
                          {visibleFixedKeys.map((k, i) => {
                            const w = columnWidths[k] ?? DEFAULT_COLUMN_WIDTHS[k];
                            const isReadOnly = k === 'last_modified' || k === 'unique_id' || k === 'allocation';
                            const isEditingFixed = !isReadOnly && editingFixedCell?.rowKey === rk && editingFixedCell?.columnKey === k;
                            const isSavingFixed = savingFixedCell === `${rk}-${k}` || savingFixedCell === `${rk}-multi`;
                            const jobNameFallback = k === 'job_name' ? resolveJobNameDisplay(row) : null;
                            const isStatusCol = k === 'plan_status';
                            const statusVal = resolvePlanStatus(row);
                            const statusStyle = PLAN_STATUS_STYLES[statusVal] || PLAN_STATUS_STYLES.estimate;
                            const displayValue = k === 'last_modified' ? formatLastModified(row) : k === 'empl_code' ? formatEmplCode(row[k]) : k === 'job_name' ? jobNameFallback.text : k === 'allocation' ? String(computeAllocation(row, data.weekDates)) : isStatusCol ? planStatusLabel(statusVal) : (row[k] != null ? String(row[k]) : '');
                            const isDerivedJobName = !!jobNameFallback?.derived;
                            const inFixedFill = isInFixedDragFillRange(idx, k);
                            const isFixedSelected = selectedCell?.type === 'fixed' && selectedCell?.rk === rk && selectedCell?.columnKey === k && !isEditingFixed;
                            return (
                              <td key={k}
                                data-col={k}
                                onMouseEnter={() => handleFixedDragFillEnter(idx)}
                                style={{ position: 'relative', width: w, minWidth: MIN_WIDTHS[k], height: BODY_ROW_HEIGHT, maxHeight: BODY_ROW_HEIGHT, padding: '0.15rem 0.4rem', borderBottom: '1px solid #eee', borderRight: i === visibleFixedKeys.length - 1 ? 'none' : '1px solid #dee2e6', fontSize: '0.85rem', overflow: 'visible', textOverflow: 'ellipsis', whiteSpace: 'nowrap', backgroundColor: inFixedFill ? '#cce5ff' : rowBg, verticalAlign: 'middle', boxSizing: 'border-box', outline: isFixedSelected ? '2px solid #007bff' : (inFixedFill ? '2px solid #80bdff' : 'none'), outlineOffset: '-2px', zIndex: isFixedSelected ? 2 : undefined }}>
                                {isEditingFixed ? renderFixedCellEditor(row, k) : isStatusCol ? (
                                  <div onClick={() => { setEditingCell(null); setEditingCellPosition(null); setEditingFixedCell({ rowKey: rk, columnKey: k }); setEditFixedValue(statusVal); setSelectedCell(null); }} title="Estimate / Awarded / Completed — completed jobs are left off the biweekly plan email" style={{ minHeight: 10, cursor: 'pointer' }}>
                                    <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, color: statusStyle.color, backgroundColor: statusStyle.backgroundColor }}>{displayValue}</span>
                                  </div>
                                ) : (
                                  <div onClick={() => { if (isReadOnly) return; setEditingCell(null); setEditingCellPosition(null); setEditingFixedCell({ rowKey: rk, columnKey: k }); setEditFixedValue(row[k] != null ? String(row[k]) : ''); setSelectedCell(null); setInlineSuggestions([]); setShowInlineSuggestions(false); }} style={{ cursor: isReadOnly ? 'default' : (fixedDragFill.active ? 'crosshair' : 'pointer'), minHeight: 10, color: isReadOnly ? '#999' : (isDerivedJobName ? '#6c757d' : undefined), fontStyle: isDerivedJobName ? 'italic' : undefined, fontSize: isReadOnly ? '0.75rem' : undefined }} title={isReadOnly ? displayValue : (isDerivedJobName ? 'From Estimate Name (no Description on record) — click to edit' : 'Click to edit')}>
                                    {isSavingFixed ? '...' : displayValue}
                                  </div>
                                )}
                                {isFixedSelected && !isReadOnly && (
                                  <div
                                    onMouseDown={(e) => handleFixedDragFillStart(e, idx, k, row[k] ?? '')}
                                    style={{
                                      position: 'absolute',
                                      bottom: -4,
                                      right: -4,
                                      width: 8,
                                      height: 8,
                                      backgroundColor: '#007bff',
                                      cursor: 'crosshair',
                                      borderRadius: 1,
                                      zIndex: 5,
                                      border: '1px solid white'
                                    }}
                                    title="Drag down to fill"
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {/* SCROLLABLE - week columns only, no sticky cells at boundary.
                borderCollapse must be 'separate': collapsed borders are not painted
                as part of the sticky header cells, so scrolling cells show through. */}
            <div style={{ flexShrink: 0, minWidth: totalWeekWidth }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', minWidth: totalWeekWidth, fontSize: '0.85rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10, height: DATE_HEADER_HEIGHT, backgroundColor: '#90caf9', isolation: 'isolate' }}>
                  <tr style={{ height: Math.floor(DATE_HEADER_HEIGHT / 3), lineHeight: 1 }}>
                    {getYearGroups(weekDates).map((g, i) => (
                      <th key={`y-${g.year}-${i}`} colSpan={g.count} style={{ padding: '0.1rem 0.2rem', borderBottom: '1px solid #64b5f6', borderRight: '1px solid #64b5f6', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', backgroundColor: '#64b5f6', lineHeight: 1.2, boxSizing: 'border-box' }}>{g.year}</th>
                    ))}
                  </tr>
                  <tr style={{ height: Math.floor(DATE_HEADER_HEIGHT / 3), lineHeight: 1 }}>
                    {getMonthGroups(weekDates).map((g, i) => (
                      <th key={`m-${g.year}-${g.month}-${i}`} colSpan={g.count} style={{ padding: '0.08rem 0.2rem', borderBottom: '1px solid #64b5f6', borderRight: '1px solid #64b5f6', fontWeight: 600, fontSize: '0.7rem', textAlign: 'center', backgroundColor: '#90caf9', lineHeight: 1.2, boxSizing: 'border-box' }}>{g.month}</th>
                    ))}
                  </tr>
                  <tr style={{ height: Math.floor(DATE_HEADER_HEIGHT / 3), lineHeight: 1 }}>
                    {weekDates.map((wd, wi) => (
                      <th key={wd} style={{ width: weekWidth, minWidth: weekWidth, padding: '0.08rem 0.15rem', borderBottom: '2px solid #dee2e6', borderRight: '1px solid #64b5f6', fontWeight: 700, fontSize: '0.7rem', textAlign: 'center', backgroundColor: '#90caf9', lineHeight: 1.2, boxSizing: 'border-box' }}>
                        {getWeekHeaderDate(wd)}
                        {wi === weekDates.length - 1 && (
                          <div onMouseDown={(e) => handleResizeStart(e, 'week')} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', zIndex: 2 }} title="Drag to resize week columns" />
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? null : (
                    rows.map((row, idx) => {
                      const rk = rowKey(row);
                      const isAuditHi = highlightedRowId === rk;
                      return (
                        <tr key={rk + '-' + idx} data-row-id={rk} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa', height: BODY_ROW_HEIGHT, lineHeight: 1 }}>
                          {weekDates.map((wd, weekIdx) => {
                            const isEditing = editingCell?.rowKey === rk && editingCell?.week_start_date === wd;
                            const isSaving = savingCell === `${rk}-${wd}`;
                            const cellValue = row.weeks && row.weeks[wd];
                            const inFillRange = isInDragFillRange(idx, weekIdx);
                            const isSelected = selectedCell?.type === 'week' && selectedCell?.rk === rk && selectedCell?.weekDate === wd && !isEditing;
                            const cellColor = getWeekCellColor(cellValue);
                            return (
                              <td
                                key={wd}
                                data-cell={`${rk}-${wd}`}
                                style={{ width: weekWidth, minWidth: weekWidth, height: BODY_ROW_HEIGHT, maxHeight: BODY_ROW_HEIGHT, borderBottom: '1px solid #eee', borderRight: '1px solid #dee2e6', padding: '0.15rem 0.2rem', verticalAlign: 'middle', backgroundColor: inFillRange ? '#cce5ff' : cellColor, boxSizing: 'border-box', overflow: 'visible', outline: isSelected ? '2px solid #007bff' : (inFillRange ? '2px solid #80bdff' : (isAuditHi ? '2px solid #f59e0b' : 'none')), outlineOffset: '-2px', zIndex: isSelected ? 2 : undefined }}
                                onMouseEnter={() => handleDragFillEnter(idx, weekIdx)}
                              >
                                <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 0, overflow: 'visible' }}>
                                  {isEditing ? (
                                    <>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onBlur={(e) => { if (!e.relatedTarget?.classList?.contains('week-quick-btn')) handleCellBlur(row, wd); }}
                                        onKeyDown={(e) => handleCellKeyDown(e, row, wd)}
                                        autoFocus
                                        style={{ width: '100%', minWidth: 0, padding: '0.25rem 0.2rem', border: '2px solid #0d6efd', borderRadius: 0, boxSizing: 'border-box', fontSize: '0.8rem' }}
                                      />
                                      {ReactDOM.createPortal(
                                        (() => {
                                          const cellRect = document.querySelector(`[data-cell="${rk}-${wd}"]`)?.getBoundingClientRect();
                                          const popupWidth = 220;
                                          const margin = 16;
                                          let top = editingCellPosition?.top ?? ((cellRect?.bottom || 0) + 4);
                                          let left = editingCellPosition?.left ?? (cellRect?.left || 0);
                                          if (left + popupWidth > window.innerWidth - margin) left = Math.max(margin, window.innerWidth - popupWidth - margin);
                                          return (
                                            <div style={{
                                              position: 'fixed',
                                              top, left,
                                              zIndex: 9999,
                                              display: 'flex', gap: 3, padding: '4px 6px',
                                              backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: 6,
                                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                            }}>
                                              {WEEK_QUICK_VALUES.map((q) => (
                                                <button
                                                  key={q}
                                                  type="button"
                                                  className="week-quick-btn"
                                                  onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    const val = q === 0 ? '0' : String(q);
                                                    const prevVal = row.weeks?.[wd] ?? '0';
                                                    if (String(prevVal) !== val) pushUndo(() => saveCell(row.empl_code, wd, prevVal, row));
                                                    saveCell(row.empl_code, wd, val, row);
                                                  }}
                                                  style={{
                                                    padding: '4px 10px', fontSize: '0.8rem', fontWeight: 600,
                                                    border: '1px solid #ccc', borderRadius: 4,
                                                    background: getWeekCellColor(q), cursor: 'pointer',
                                                    color: '#333', minWidth: 36
                                                  }}
                                                >
                                                  {q}
                                                </button>
                                              ))}
                                            </div>
                                          );
                                        })(),
                                        document.body
                                      )}
                                    </>
                                  ) : (
                                    <div
                                      data-cell={`${rk}-${wd}`}
                                      onClick={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setEditingCellPosition({ top: rect.bottom + 4, left: rect.left });
                                        setEditingFixedCell(null);
                                        setEditingCell({ rowKey: rk, week_start_date: wd });
                                        setEditValue(cellValue != null && cellValue !== '' ? formatWeekCellDisplay(cellValue) : '');
                                        setSelectedCell(null);
                                      }}
                                      style={{ minHeight: 18, cursor: dragFill.active ? 'crosshair' : 'pointer', fontSize: '0.8rem', textAlign: 'center', fontWeight: 500 }}
                                    >
                                      {isSaving ? '...' : formatWeekCellDisplay(cellValue)}
                                    </div>
                                  )}
                                  {isSelected && (
                                    <div
                                      onMouseDown={(e) => handleDragFillStart(e, idx, weekIdx, cellValue ?? '')}
                                      style={{
                                        position: 'absolute',
                                        bottom: -4,
                                        right: -4,
                                        width: 8,
                                        height: 8,
                                        backgroundColor: '#007bff',
                                        cursor: 'crosshair',
                                        borderRadius: 1,
                                        zIndex: 5,
                                        border: '1px solid white'
                                      }}
                                      title="Drag to fill cells"
                                    />
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Add Row Modal */}
      {showAddModal && (() => {
        const closeAddModal = () => {
          setShowAddModal(false);
          setAddEmplSearch('');
          setShowAddEmplDropdown(false);
          setEmplSearchResults([]);
          setAddJobSearch('');
          setShowAddJobDropdown(false);
          setJobSearchResults([]);
          setAddEstSearch('');
          setShowAddEstDropdown(false);
          setEstSearchResults([]);
        };
        const inputStyle = { width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' };
        const labelStyle = { display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' };
        const dropdownStyle = { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '2px', maxHeight: '200px', overflowY: 'auto', backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1050 };
        const dropdownItemStyle = { padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.9rem', borderBottom: '1px solid #eee' };
        const applyAddEmployee = (emp) => {
          if (!emp) return;
          setNewRow(prev => {
            const discipline = emp.discipline_code ?? emp.trade ?? prev.discipline;
            // Role rides along with the person from the employee master
            // (Hiring / Layoff role_code); keep any typed role when master has none.
            const role = emp.role_code || prev.role;
            return {
              ...prev,
              empl_code: emp.emp_code ?? '',
              empl_name: emp.emp_name ?? '',
              discipline,
              role,
              role_discipline: [role, discipline].filter(Boolean).join(' - '),
              division: emp.division ?? prev.division,
              allocation: prev.allocation || '1',
            };
          });
          setAddEmplSearch(`${emp.emp_code} — ${emp.emp_name || ''}`);
          setShowAddEmplDropdown(false);
        };
        const applyAddJob = (proj) => {
          if (!proj) return;
          setNewRow(prev => ({
            ...prev,
            'job_#': proj.job_number ?? '',
            job_name: proj.job_name ?? '',
            estimate_number: proj.estimate_number ?? prev.estimate_number,
            estimate_name: proj.estimate_name ?? prev.estimate_name,
            division: proj.division_name ? (proj.division_name + (proj.division_location ? ` (${proj.division_location})` : '')) : prev.division,
          }));
          setAddJobSearch(`${proj.job_number} — ${proj.job_name || ''}`);
          setShowAddJobDropdown(false);
          if (proj.estimate_number) setAddEstSearch(`${proj.estimate_number} — ${proj.estimate_name || ''}`);
        };
        const applyAddEstimate = (est) => {
          if (!est) return;
          setNewRow(prev => ({
            ...prev,
            estimate_number: est.estimate_number ?? '',
            estimate_name: est.estimate_name ?? '',
            division: est.division_name ? (est.division_name + (est.division_location ? ` (${est.division_location})` : '')) : prev.division,
            'job_#': est.job_number || 'Not Awarded',
            job_name: est.job_name || est.estimate_name || prev.job_name,
          }));
          setAddEstSearch(`${est.estimate_number} — ${est.estimate_name || ''}`);
          setShowAddEstDropdown(false);
          if (est.job_number) setAddJobSearch(`${est.job_number} — ${est.job_name || ''}`);
          else setAddJobSearch('Not Awarded');
        };
        const confirmSingleSearch = async (e, { results, searchFn, query, debounceRef, apply }) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          e.stopPropagation();
          clearTimeout(debounceRef.current);
          let list = results;
          if (list.length !== 1) {
            const q = String(query || '').trim();
            if (!q) return;
            list = (await searchFn(q)) || [];
          }
          if (list.length === 1) apply(list[0]);
        };
        return (
          <div style={modalOverlayStyle} onClick={closeAddModal}>
            <div ref={addModalRef} style={{ ...modalContentStyle, maxWidth: 680 }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
                <h3 style={{ margin: 0 }}>➕ Add Row</h3>
                <button onClick={closeAddModal} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
              </div>

              {/* Employee search - typeahead from lt_employee master list */}
              <div style={{ marginBottom: '1rem', position: 'relative' }}>
                <label style={labelStyle}>Search employee (code or name)</label>
                <input
                  type="text"
                  value={addEmplSearch}
                  onChange={(e) => {
                    const q = e.target.value;
                    setAddEmplSearch(q);
                    setShowAddEmplDropdown(true);
                    clearTimeout(emplSearchDebounceRef.current);
                    emplSearchDebounceRef.current = setTimeout(() => searchEmployees(q.trim()), 250);
                  }}
                  onFocus={() => { if (addEmplSearch.trim()) setShowAddEmplDropdown(true); }}
                  onKeyDown={(e) => confirmSingleSearch(e, { results: emplSearchResults, searchFn: searchEmployees, query: addEmplSearch, debounceRef: emplSearchDebounceRef, apply: applyAddEmployee })}
                  placeholder="Type employee code or name to search..."
                  style={inputStyle}
                />
                {emplSearchLoading && <div style={{ position: 'absolute', right: 10, top: 30, fontSize: '0.75rem', color: '#999' }}>Loading...</div>}
                {showAddEmplDropdown && emplSearchResults.length > 0 && (
                  <div style={dropdownStyle}>
                    {emplSearchResults.map((emp) => (
                      <div
                        key={emp.emp_code}
                        onClick={() => applyAddEmployee(emp)}
                        style={dropdownItemStyle}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e3f2fd'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                      >
                        <strong>{emp.emp_code}</strong> — {emp.emp_name || '(no name)'}
                        {(emp.discipline_code || emp.trade) && <span style={{ color: '#666', fontSize: '0.8rem' }}> · {emp.discipline_code || emp.trade}</span>}
                        {emp.role_code && <span style={{ color: '#666', fontSize: '0.8rem' }}> · {emp.role_code}</span>}
                        {emp.division && <span style={{ color: '#999', fontSize: '0.8rem' }}> · {emp.division}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Job search - typeahead from projects */}
              <div style={{ marginBottom: '1rem', position: 'relative' }}>
                <label style={labelStyle}>Search job (job # or name)</label>
                <input
                  type="text"
                  value={addJobSearch}
                  onChange={(e) => {
                    const q = e.target.value;
                    setAddJobSearch(q);
                    setShowAddJobDropdown(true);
                    clearTimeout(jobSearchDebounceRef.current);
                    jobSearchDebounceRef.current = setTimeout(() => searchJobs(q.trim()), 250);
                  }}
                  onFocus={() => { if (addJobSearch.trim()) setShowAddJobDropdown(true); }}
                  onKeyDown={(e) => confirmSingleSearch(e, { results: jobSearchResults, searchFn: searchJobs, query: addJobSearch, debounceRef: jobSearchDebounceRef, apply: applyAddJob })}
                  placeholder="Type job number or name to search..."
                  style={inputStyle}
                />
                {jobSearchLoading && <div style={{ position: 'absolute', right: 10, top: 30, fontSize: '0.75rem', color: '#999' }}>Loading...</div>}
                {showAddJobDropdown && jobSearchResults.length > 0 && (
                  <div style={dropdownStyle}>
                    {jobSearchResults.map((proj) => (
                      <div
                        key={proj.id || proj.job_number}
                        onClick={() => applyAddJob(proj)}
                        style={dropdownItemStyle}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e3f2fd'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                      >
                        <strong>{proj.job_number}</strong> — {proj.job_name || '(no name)'}
                        {proj.division_name && <span style={{ color: '#999', fontSize: '0.8rem' }}> · {proj.division_name}</span>}
                        {proj.estimate_number && <span style={{ color: '#666', fontSize: '0.8rem' }}> · Est# {proj.estimate_number}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Estimate search - typeahead from estimates */}
              <div style={{ marginBottom: '1rem', position: 'relative' }}>
                <label style={labelStyle}>Search estimate (number or name)</label>
                <input
                  type="text"
                  value={addEstSearch}
                  onChange={(e) => {
                    const q = e.target.value;
                    setAddEstSearch(q);
                    setShowAddEstDropdown(true);
                    clearTimeout(estSearchDebounceRef.current);
                    estSearchDebounceRef.current = setTimeout(() => searchEstimates(q.trim()), 250);
                  }}
                  onFocus={() => { if (addEstSearch.trim()) setShowAddEstDropdown(true); }}
                  onKeyDown={(e) => confirmSingleSearch(e, { results: estSearchResults, searchFn: searchEstimates, query: addEstSearch, debounceRef: estSearchDebounceRef, apply: applyAddEstimate })}
                  placeholder="Type estimate number or name to search..."
                  style={inputStyle}
                />
                {estSearchLoading && <div style={{ position: 'absolute', right: 10, top: 30, fontSize: '0.75rem', color: '#999' }}>Loading...</div>}
                {showAddEstDropdown && estSearchResults.length > 0 && (
                  <div style={dropdownStyle}>
                    {estSearchResults.map((est) => (
                      <div
                        key={est.id}
                        onClick={() => applyAddEstimate(est)}
                        style={dropdownItemStyle}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e3f2fd'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                      >
                        <strong>{est.estimate_number}</strong> — {est.estimate_name || '(no name)'}
                        {est.division_name && <span style={{ color: '#999', fontSize: '0.8rem' }}> · {est.division_name}</span>}
                        {est.awarded && <span style={{ color: '#28a745', fontSize: '0.8rem', fontWeight: 600 }}> · Awarded</span>}
                        {est.job_number && <span style={{ color: '#666', fontSize: '0.8rem' }}> · Job# {est.job_number}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Fixed fields grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                {['empl_code', 'empl_name', 'job_#', 'job_name', 'estimate_number', 'estimate_name', 'discipline', 'role', 'shop'].map(k => (
                  <div key={k}>
                    <label style={labelStyle}>{k === 'discipline' ? 'Discipline (= Trade)' : COLUMN_LABELS[k]}</label>
                    <input
                      type="text"
                      list={k === 'discipline' ? 'irm-legend-disciplines' : k === 'role' ? 'irm-legend-roles' : undefined}
                      value={newRow[k] ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewRow(prev => {
                          const next = { ...prev, [k]: val };
                          if (k === 'role' || k === 'discipline') {
                            const r = k === 'role' ? val : prev.role;
                            const d = k === 'discipline' ? val : prev.discipline;
                            next.role_discipline = [r, d].filter(Boolean).join(' - ');
                          }
                          return next;
                        });
                      }}
                      placeholder={COLUMN_LABELS[k]}
                      style={inputStyle}
                    />
                  </div>
                ))}
                <div>
                  <label style={labelStyle}>Division</label>
                  <select
                    value={newRow.division ?? ''}
                    onChange={(e) => setNewRow(prev => ({ ...prev, division: e.target.value }))}
                    style={{ ...inputStyle, backgroundColor: '#fff' }}
                  >
                    <option value="">— Select —</option>
                    {divisionsList.map(d => {
                      const label = d.division_name + (d.location ? ` (${d.location})` : '');
                      return <option key={d.id} value={label}>{label}</option>;
                    })}
                  </select>
                </div>
              </div>

              {/* Start / Finish / Allocation — optional (item 8, 2026-08-10) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <label style={labelStyle}>Start (optional)</label>
                  <input type="date" value={newRow.start_date ?? ''} onChange={(e) => setNewRow(prev => ({ ...prev, start_date: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Finish (optional)</label>
                  <input type="date" value={newRow.finish_date ?? ''} onChange={(e) => setNewRow(prev => ({ ...prev, finish_date: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Allocation (optional)</label>
                  <select
                    value={newRow.allocation ?? ''}
                    onChange={(e) => setNewRow(prev => ({ ...prev, allocation: e.target.value }))}
                    style={{ ...inputStyle, backgroundColor: '#fff' }}
                  >
                    <option value="">—</option>
                    {[0.25, 0.5, 0.75, 1].map(v => (<option key={v} value={v}>{v}</option>))}
                  </select>
                </div>
              </div>

              {/* Note and Comments */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginTop: '1rem' }}>
                <div>
                  <label style={labelStyle}>Note</label>
                  <input type="text" value={newRow.note ?? ''} onChange={(e) => setNewRow(prev => ({ ...prev, note: e.target.value }))} placeholder="Optional note..." style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Comments</label>
                  <input type="text" value={newRow.comments ?? ''} onChange={(e) => setNewRow(prev => ({ ...prev, comments: e.target.value }))} placeholder="Optional comments..." style={inputStyle} />
                </div>
              </div>

              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={quickAddRow} disabled={adding} style={{ ...toolbarBtnStyle, backgroundColor: '#6c757d', color: 'white', border: '1px solid #6c757d' }} title="Add an empty row and fill in details directly in the table">
                  {adding ? 'Adding...' : '⚡ Quick Add (Empty Row)'}
                </button>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={closeAddModal} style={toolbarBtnStyle}>Cancel</button>
                  <button onClick={addRow} disabled={adding || !newRow.empl_code?.trim()} style={{ ...toolbarBtnStyle, backgroundColor: '#28a745', color: 'white', border: '1px solid #28a745' }}>
                    {adding ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Import Modal */}
      {showImportModal && (
        <div style={modalOverlayStyle} onClick={() => !importing && setShowImportModal(false)}>
          <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <h3 style={{ margin: 0 }}>📥 Import</h3>
              <button onClick={() => !importing && setShowImportModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
              Upload an Excel file with columns: Job #, Empl Code, Empl Name, Discipline, Role, then one column per week (YYYY-MM-DD). Export first to get the template.
            </p>
            <p style={{ margin: '0 0 1rem', color: '#555', fontSize: '0.875rem' }}>
              Each row&apos;s employee, job, and estimate code/name must match <strong>Admin → Employees</strong>, <strong>Projects</strong>, and <strong>Estimates</strong>.
              Mismatched rows are not imported — you get an email with the reason and how to fix them.
              Job # <strong>Holiday</strong> and <strong>Not Awarded</strong> are allowed without a project match.
            </p>
            <input type="file" accept=".xlsx,.xls" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImport(f); }} style={{ marginBottom: '1rem' }} />
            {importing && !importPreview && <p style={{ color: '#0d6efd' }}>Reading file…</p>}
            {importPreview && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', marginBottom: '1rem', fontSize: 14 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Review before importing</div>
                <div>{importPreview.message}</div>
                {importPreview.rejected > 0 && (
                  <div style={{ color: '#b91c1c', marginTop: 6 }}>
                    {importPreview.rejected} row(s) will be skipped (identity mismatch). You will get an email with the reasons.
                  </div>
                )}
                {(importPreview.rejectedRows || []).slice(0, 8).map((r) => (
                  <div key={`rej-${r.excelRow}`} style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    Row {r.excelRow}: {r.empl_name || r.empl_code || '—'} — {(r.reasons || []).map((x) => x.message).join('; ')}
                  </div>
                ))}
              </div>
            )}
            <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => { if (!importing) { setShowImportModal(false); setImportPreview(null); setImportFile(null); } }} style={toolbarBtnStyle}>Cancel</button>
              {importPreview && (
                <button
                  onClick={confirmResourceImport}
                  disabled={importing || (importPreview.imported || 0) === 0}
                  style={{ ...toolbarBtnStyle, background: '#0f766e', color: '#fff', borderColor: '#0f766e' }}
                >
                  {importing ? 'Importing…' : 'Import these changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <div style={modalOverlayStyle} onClick={() => setShowHelpModal(false)}>
          <div style={{ ...modalContentStyle, maxWidth: '640px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem', flexShrink: 0 }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>❓ Resource Management Help</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton compact guideId="indirect-resources-help" title="Resource Management Help" accent="#0d6efd" />
                <button onClick={() => setShowHelpModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid #dee2e6', marginBottom: 10, flexShrink: 0 }}>
              {[['guide', 'Guide'], ['legend', 'Legend']].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setHelpTab(id)}
                  style={{
                    border: 'none',
                    background: helpTab === id ? '#0d6efd' : 'transparent',
                    color: helpTab === id ? '#fff' : '#0d6efd',
                    padding: '7px 16px',
                    borderRadius: '8px 8px 0 0',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {helpTab === 'legend' && (
              <div style={{ fontSize: '0.9rem', color: '#333', overflowY: 'auto', flex: 1, paddingRight: '0.5rem' }}>
                <section style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff3f3', borderRadius: 8, borderLeft: '4px solid #dc3545' }}>
                  <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#dc3545' }}>Who can appear on this dashboard</h3>
                  <p style={{ margin: 0 }}>
                    Only employees flagged <strong>Indirect</strong> on the master list — foremen and above
                    (Foreman, General Foreman, Superintendent, PM) plus staff (clerks, schedulers, ...).
                    People are picked from the dropdown only; free-typed names are rejected — in the grid <em>and</em> in Excel imports.
                  </p>
                </section>
                <EmployeeCodeLegendPanel accent="#0d6efd" />
              </div>
            )}
            <div style={{ fontSize: '0.9rem', color: '#333', overflowY: 'auto', flex: 1, paddingRight: '0.5rem', display: helpTab === 'guide' ? 'block' : 'none' }}>
              <HelpWithVideoTour guideId="indirect-resources-help" title="Resource Management Help" accent="#0d6efd">
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f0f7ff', borderRadius: 8, borderLeft: '4px solid #0d6efd' }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>📋 Overview</h3>
                <p style={{ margin: 0 }}>This screen plans <strong>indirect resource allocation</strong> by person and week.</p>
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 200px', padding: '0.5rem', backgroundColor: 'white', borderRadius: 4, border: '1px solid #dee2e6' }}>
                    <strong>Rows</strong> = people (employees)<br />
                    <strong>Columns</strong> = Job #, Empl Code, Name, Discipline, Role + week dates
                  </div>
                  <div style={{ flex: '1 1 200px', padding: '0.5rem', backgroundColor: 'white', borderRadius: 4, border: '1px solid #dee2e6' }}>
                    <strong>Week cells</strong> = allocation (FTE or %)<br />
                    <em>FTE = Full-Time Equivalent (1 = 100%, 0.5 = 50%)</em>
                  </div>
                </div>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>🛠️ Toolbar</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem' }}>
                  <div><strong>Import / Export</strong> — bulk load or download Excel</div>
                  <div><strong>Template</strong> — empty Excel with headers</div>
                  <div><strong>Columns</strong> — show/hide fixed columns</div>
                  <div><strong>Auto-fit / Reset / Undo</strong> — column widths</div>
                  <div><strong>Add Row</strong> — add a person (search to pre-fill)</div>
                  <div><strong>Delete (n)</strong> — remove selected rows</div>
                  <div><strong>Refresh</strong> — reload data</div>
                  <div><strong>Analytics</strong> — histogram, pivot table, over- and under-allocation</div>
                </div>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>🔍 Filters</h3>
                <p style={{ margin: '0 0 0.35rem' }}><strong>Search</strong> — global text search</p>
                <p style={{ margin: '0 0 0.35rem' }}><strong>Allocation above 0</strong> — on by default; hides rows with no upcoming allocation</p>
                <p style={{ margin: '0 0 0.35rem' }}><strong>Sort: current week first</strong> — on by default. People with allocation in the first visible week (this Monday, or your From date) sit at the top, then the next week, and so on. Click a column header to sort by that column instead.</p>
                <p style={{ margin: '0 0 0.35rem' }}><strong>From date</strong> — Monday of current week by default</p>
                <p style={{ margin: '0 0 0.35rem' }}><strong>Months ahead</strong> — 1–12 to set To date automatically</p>
                <p style={{ margin: 0 }}><strong>Funnel icon</strong> — per-column filter: by default all values are checked; use <strong>Clear</strong> to uncheck all; check only the values you want to see.</p>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>✏️ Table &amp; editing</h3>
                <p style={{ margin: '0 0 0.35rem' }}><strong>Fixed columns</strong> and <strong>week cells</strong> — click to edit, blur or Enter to save, Escape to cancel.</p>
                <p style={{ margin: '0 0 0.35rem' }}><strong>Drag-fill</strong> — blue square at bottom-right of a cell: drag to copy value.</p>
                <p style={{ margin: 0 }}>Column widths are resizable; use checkboxes for batch delete.</p>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#fff8e6', borderRadius: 8, borderLeft: '4px solid #ffc107' }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>📊 Week values (FTE)</h3>
                <p style={{ margin: 0 }}>Week cells use <strong>FTE (Full-Time Equivalent)</strong> — 1 = 100% of a week, 0.5 = 50%, 0.25 = 25%. Use <strong>0, 0.25, 0.5, 0.75, 1</strong> or type any value. Empty = 0.</p>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#fff3f3', borderRadius: 8, borderLeft: '4px solid #dc3545' }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#dc3545' }}>⚠️ Over-Allocation Warning</h3>
                <p style={{ margin: '0 0 0.35rem' }}>When adding a new row, if the person's <strong>total allocation exceeds 100%</strong> (1.0 FTE) across all their assignments during the overlapping date range, a warning will appear.</p>
                <p style={{ margin: '0 0 0.35rem' }}>The warning shows the person's <strong>existing assignments</strong> (job, role, discipline, allocation, dates) and the resulting total.</p>
                <p style={{ margin: 0 }}>You can <strong>override the warning</strong> and proceed, or cancel to adjust the allocation.</p>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f0f7ff', borderRadius: 8, borderLeft: '4px solid #0d6efd' }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>📅 Start, Finish &amp; Allocation</h3>
                <p style={{ margin: '0 0 0.35rem' }}>When adding a row with <strong>Start</strong>, <strong>Finish</strong>, and <strong>Allocation</strong> set, week cells within the date range are <strong>pre-filled</strong> with the allocation value.</p>
                <p style={{ margin: 0 }}><strong>Role-Discipline</strong> is auto-populated by combining Role and Discipline (e.g. "Foreman - Piping").</p>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: 8, borderLeft: '4px solid #28a745' }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#28a745' }}>🏖️ Holidays &amp; Time Off</h3>
                <p style={{ margin: 0 }}>Use Job # <strong>"Holiday"</strong> to record employees' time off. Add a row for the employee with Job # set to <strong>Holiday</strong>, then set the allocation and date range for their leave period. This ensures the over-allocation warning accounts for their absence.</p>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>📅 Date range</h3>
                <p style={{ margin: 0 }}>From = Monday of current week, To = 52 weeks ahead. Use <strong>Months ahead</strong> (1–12) to set To automatically.</p>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>📊 Analytics</h3>
                <p style={{ margin: 0 }}>One window: <strong>Histogram</strong>, <strong>Pivot Table</strong> (group by Job, Discipline, Role, Employee, or Shop — click <strong>+</strong> to expand week cells, same green-to-red colours as the plan grid), plus over- and under-allocation. The window stays the same size when you switch tabs.</p>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: 8, borderLeft: '4px solid #28a745' }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>↩️ Undo (Ctrl+Z)</h3>
                <p style={{ margin: 0 }}><strong>Ctrl+Z</strong> (Cmd+Z on Mac) undoes the last action: cell edits, column resize, and more. Use the Undo button or keyboard shortcut.</p>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: 8 }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>Import / Export</h3>
                <p style={{ margin: '0 0 0.35rem' }}>Export and Template use the current window. Import expects: Job #, Empl Code, Empl Name, Discipline, Role, then week columns.</p>
                <p style={{ margin: 0 }}>Import validates each row against <strong>Admin → Employees</strong>, <strong>Projects</strong>, and <strong>Estimates</strong>. If a code or name does not match, that row is skipped and the importer receives an email with the reason and how to fix it. Use Job # <strong>Holiday</strong> or <strong>Not Awarded</strong> when there is no project number.</p>
              </section>
              <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#fff8e6', borderRadius: 8, borderLeft: '4px solid #ffc107' }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>📧 Emails &amp; notifications</h3>
                <p style={{ margin: '0 0 0.35rem' }}><strong>Status</strong> (Estimate / Awarded / Completed) replaces the old Awarded Yes/No chip. Mark a job <strong>Completed</strong> when it is finished, cancelled, or rejected — that status applies to everyone on the job (or on the same Not Awarded estimate). Awarding an estimate in Admin writes the new job number onto matching plan rows automatically.</p>
                <p style={{ margin: '0 0 0.35rem' }}><strong>Weekly allocation report</strong> (Mondays 8:00 AM ET) covers over- and under-allocation. People whose status in Admin → Employees is <strong>Terminated</strong> or <strong>On Leave</strong> are excluded from that email.</p>
                <p style={{ margin: '0 0 0.35rem' }}><strong>Biweekly Field Resource Plan reminder</strong> (Monday noon, every other week) attaches the current plan. Replies go to Project Controls. The Excel starts with a <strong>Readme</strong>, then <strong>By Project</strong> (grouped) and <strong>Table</strong> (filters) — switch on row 1. Import asks you to confirm creates/updates first. It drops Holiday, <strong>Completed</strong> status, and jobs/estimates <strong>archived in Admin</strong>.</p>
                <p style={{ margin: '0 0 0.35rem' }}><strong>Daily data check</strong> (7:45 AM ET) emails Yousef and Behzad whether any dashboard employee / job / estimate values do not match the source databases — including when everything is clean. Turn it off under Profile → Notification Settings (<em>Indirect Resources Data Discrepancy</em>).</p>
                <p style={{ margin: 0 }}>Import rejection emails go only to the person who ran the import.</p>
              </section>
              <section style={{ marginBottom: '0', padding: '1rem', backgroundColor: '#fff3e0', borderRadius: 8, borderLeft: '4px solid #ff9800' }}>
                <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>🔮 Future data source</h3>
                <p style={{ margin: 0 }}>Data will eventually come from <strong>cost breakdown files</strong> and be <strong>hour-based</strong> instead of FTE.</p>
              </section>
              </HelpWithVideoTour>
            </div>
            <div style={{ marginTop: '1rem', textAlign: 'right', flexShrink: 0 }}>
              <button onClick={() => setShowHelpModal(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && (
        <div style={modalOverlayStyle} onClick={() => setShowAnalyticsModal(false)}>
          <div style={{ ...modalContentStyle, width: '92vw', maxWidth: '92vw', height: '85vh', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem', flexShrink: 0 }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>📊 Analytics</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => { setAnalyticsHelpTab('guide'); setShowAnalyticsHelp(true); }}
                  style={toolbarBtnStyle}
                  title="Analytics help and legends"
                >
                  ❓ Help
                </button>
                <button onClick={() => setShowAnalyticsModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
              </div>
            </div>
            <div style={{ marginBottom: '0.75rem', flexShrink: 0 }}>
              <strong>Total people (rows):</strong> {rows.length}
            </div>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '0.75rem', flexShrink: 0, borderBottom: '1px solid #dee2e6', flexWrap: 'wrap' }}>
              {['histogram', 'pivot', 'over_allocation', 'under_allocation'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setAnalyticsTab(tab);
                    if (tab === 'over_allocation') fetchOverAllocations();
                    if (tab === 'under_allocation') fetchUnderAllocations();
                  }}
                  style={{ padding: '6px 14px', fontSize: '0.85rem', border: 'none', borderBottom: analyticsTab === tab ? '2px solid #007bff' : '2px solid transparent', backgroundColor: 'transparent', cursor: 'pointer', color: analyticsTab === tab ? '#007bff' : '#666' }}
                >
                  {tab === 'histogram' ? 'Histogram' : tab === 'pivot' ? 'Pivot Table' : tab === 'over_allocation' ? 'Over-Allocation' : 'Under-Allocation'}
                </button>
              ))}
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {analyticsTab === 'histogram' && (
                <div style={{ height: '100%', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
                  <ResourceHistogramChart
                    embedded
                    isOpen={true}
                    rows={rows}
                    weekColumns={(weekDates || []).map(wd => ({ key: wd, label: new Date(wd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), date: wd }))}
                  />
                </div>
              )}
              {analyticsTab === 'pivot' && (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 360 }}>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem', flexShrink: 0, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>Group by:</span>
                    {[
                      { key: 'job', label: 'Job' },
                      { key: 'discipline', label: 'Discipline' },
                      { key: 'role', label: 'Role' },
                      { key: 'role_discipline', label: 'Role-Discipline' },
                      { key: 'empl_name', label: 'Empl Name' },
                      { key: 'shop', label: 'Shop' },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => { setPivotGroupBy(key); setPivotExpandedKey(null); }}
                        style={{ ...toolbarBtnStyle, backgroundColor: pivotGroupBy === key ? '#e3f2fd' : 'white', borderColor: pivotGroupBy === key ? '#2196f3' : '#d1d5db' }}
                      >
                        {label}
                      </button>
                    ))}
                    <span style={{ marginLeft: 'auto' }}><WeekColorLegend compact /></span>
                  </div>
                  <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.9rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#e9ecef' }}>
                          <th style={{ width: 36, padding: '0.5rem', borderBottom: '2px solid #dee2e6', borderRight: '1px solid #dee2e6', position: 'sticky', top: 0, zIndex: 4, backgroundColor: '#e9ecef' }}></th>
                          <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #dee2e6', borderRight: '1px solid #dee2e6', position: 'sticky', top: 0, zIndex: 4, backgroundColor: '#e9ecef' }}>{pivotTitle}</th>
                          <th style={{ textAlign: 'right', padding: '0.5rem', borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, zIndex: 4, backgroundColor: '#e9ecef' }}>Count (people)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pivotData.map(({ name, count, rows: groupRows }) => {
                          const isExpanded = pivotExpandedKey === name;
                          return (
                            <React.Fragment key={name}>
                              <tr style={{ borderBottom: '1px solid #eee', backgroundColor: isExpanded ? '#f0f7ff' : undefined }}>
                                <td style={{ padding: '0.35rem', borderRight: '1px solid #eee', verticalAlign: 'middle' }}>
                                  <button
                                    type="button"
                                    onClick={() => setPivotExpandedKey(isExpanded ? null : name)}
                                    style={{ width: 24, height: 24, padding: 0, border: '1px solid #ccc', borderRadius: 4, background: 'white', cursor: 'pointer', fontSize: '14px', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                    title={isExpanded ? 'Collapse detail' : 'Show detail'}
                                  >
                                    {isExpanded ? '−' : '+'}
                                  </button>
                                </td>
                                <td style={{ padding: '0.5rem', borderRight: '1px solid #eee' }}>{name}</td>
                                <td style={{ textAlign: 'right', padding: '0.5rem' }}>{count}</td>
                              </tr>
                              {isExpanded && (() => {
                                const pivotWeeks = data.weekDates || [];
                                const maxLastIdx = groupRows.length === 0 ? -1 : Math.max(...groupRows.map(r => getLastNonZeroWeekIndex(r, pivotWeeks)));
                                const weekDatesToShow = maxLastIdx >= 0 ? pivotWeeks.slice(0, maxLastIdx + 1) : [];
                                return (
                                  <tr style={{ borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa' }}>
                                    <td colSpan={3} style={{ padding: '0.5rem 0.5rem 0.5rem 2rem', borderRight: '1px solid #eee', verticalAlign: 'top' }}>
                                      <div style={{ fontSize: '0.85rem', maxHeight: 360, overflow: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                                          <thead>
                                            <tr style={{ backgroundColor: '#e9ecef' }}>
                                              <th rowSpan={3} style={{ textAlign: 'left', padding: '0.35rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6', whiteSpace: 'nowrap', width: 70, position: 'sticky', top: 0, zIndex: 6, backgroundColor: '#e9ecef' }}>Job #</th>
                                              <th rowSpan={3} style={{ textAlign: 'left', padding: '0.35rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6', whiteSpace: 'nowrap', width: 90, position: 'sticky', top: 0, zIndex: 6, backgroundColor: '#e9ecef' }}>Empl Code</th>
                                              <th rowSpan={3} style={{ textAlign: 'left', padding: '0.35rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6', whiteSpace: 'nowrap', width: 120, position: 'sticky', top: 0, zIndex: 6, backgroundColor: '#e9ecef' }}>Empl Name</th>
                                              <th rowSpan={3} style={{ textAlign: 'left', padding: '0.35rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6', whiteSpace: 'nowrap', width: 85, position: 'sticky', top: 0, zIndex: 6, backgroundColor: '#e9ecef' }}>Discipline</th>
                                              <th rowSpan={3} style={{ textAlign: 'left', padding: '0.35rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6', whiteSpace: 'nowrap', width: 70, position: 'sticky', top: 0, zIndex: 6, backgroundColor: '#e9ecef' }}>Role</th>
                                              <th rowSpan={3} style={{ textAlign: 'left', padding: '0.35rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6', whiteSpace: 'nowrap', width: 60, position: 'sticky', top: 0, zIndex: 6, backgroundColor: '#e9ecef' }}>Shop</th>
                                              {getYearGroups(weekDatesToShow).map((g, i) => (
                                                <th key={`y-${g.year}-${i}`} colSpan={g.count} style={{ padding: '0.25rem', borderBottom: '1px solid #64b5f6', borderRight: '1px solid #64b5f6', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center', backgroundColor: '#64b5f6', position: 'sticky', top: 0, zIndex: 5 }}>{g.year}</th>
                                              ))}
                                            </tr>
                                            <tr style={{ backgroundColor: '#90caf9' }}>
                                              {getMonthGroups(weekDatesToShow).map((g, i) => (
                                                <th key={`m-${g.year}-${g.month}-${i}`} colSpan={g.count} style={{ padding: '0.25rem', borderBottom: '1px solid #64b5f6', borderRight: '1px solid #64b5f6', fontWeight: 600, fontSize: '0.7rem', textAlign: 'center', backgroundColor: '#90caf9', position: 'sticky', top: 26, zIndex: 5 }}>{g.month}</th>
                                              ))}
                                            </tr>
                                            <tr style={{ backgroundColor: '#90caf9' }}>
                                              {weekDatesToShow.map(wd => (
                                                <th key={wd} style={{ textAlign: 'center', padding: '0.2rem', borderBottom: '2px solid #dee2e6', borderRight: '1px solid #64b5f6', minWidth: 40, fontSize: '0.75rem', fontWeight: 700, backgroundColor: '#90caf9', position: 'sticky', top: 50, zIndex: 5 }} title={wd}>{getWeekHeaderDate(wd)}</th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {groupRows.map((r, i) => (
                                              <tr key={(r.empl_code || '') + i} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '0.35rem', borderRight: '1px solid #eee' }}>{r['job_#'] ?? ''}</td>
                                                <td style={{ padding: '0.35rem', borderRight: '1px solid #eee' }}>{r.empl_code ?? ''}</td>
                                                <td style={{ padding: '0.35rem', borderRight: '1px solid #eee' }}>{r.empl_name ?? ''}</td>
                                                <td style={{ padding: '0.35rem', borderRight: '1px solid #eee' }}>{r.discipline ?? ''}</td>
                                                <td style={{ padding: '0.35rem', borderRight: '1px solid #eee' }}>{r.role ?? ''}</td>
                                                <td style={{ padding: '0.35rem', borderRight: '1px solid #eee' }}>{r.shop ?? ''}</td>
                                                {weekDatesToShow.map(wd => (
                                                  <td key={wd} style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.8rem', borderRight: '1px solid #eee', backgroundColor: getWeekCellColor(r.weeks?.[wd]), fontWeight: 500 }}>{formatWeekCellDisplay(r.weeks?.[wd])}</td>
                                                ))}
                                              </tr>
                                            ))}
                                            <tr style={{ borderBottom: '1px solid #eee', fontWeight: 600 }}>
                                              <td colSpan={6} style={{ padding: '0.35rem', borderRight: '1px solid #dee2e6', backgroundColor: '#e8f5e9' }}>Subtotal</td>
                                              {weekDatesToShow.map(wd => {
                                                const sum = groupRows.reduce((s, r) => s + (parseFloat(r.weeks?.[wd]) || 0), 0);
                                                return (
                                                  <td key={wd} style={{ padding: '0.25rem', textAlign: 'center', fontSize: '0.8rem', borderRight: '1px solid #eee', backgroundColor: getWeekCellColor(sum) }}>{formatWeekCellDisplay(sum)}</td>
                                                );
                                              })}
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })()}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {analyticsTab === 'over_allocation' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                      Employees with total weekly allocation &gt; 1.0 in the next 52 weeks. Auto-emailed every Monday at 8:00 AM Eastern (the email counts <strong>awarded projects</strong> only and excludes <strong>Terminated / On Leave</strong> employees).
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={fetchOverAllocations} disabled={overAllocationsLoading} style={{ padding: '4px 10px', fontSize: '0.8rem', border: '1px solid #ced4da', backgroundColor: '#f8f9fa', borderRadius: 4, cursor: overAllocationsLoading ? 'wait' : 'pointer' }}>
                        {overAllocationsLoading ? 'Loading...' : 'Refresh'}
                      </button>
                      <button onClick={sendOverAllocationReportNow} disabled={sendingOverAllocReport} style={{ padding: '4px 10px', fontSize: '0.8rem', border: '1px solid #007bff', backgroundColor: '#007bff', color: '#fff', borderRadius: 4, cursor: sendingOverAllocReport ? 'wait' : 'pointer' }} title="Send the email immediately to all managers + Yousef">
                        {sendingOverAllocReport ? 'Sending...' : '📧 Send now'}
                      </button>
                    </div>
                  </div>
                  {overAllocationsError && (
                    <div style={{ padding: '0.5rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: 4, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{overAllocationsError}</div>
                  )}
                  {!overAllocationsLoading && overAllocations.length > 0 && (
                    <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', color: '#334155', cursor: 'pointer', userSelect: 'none' }}>
                        <input
                          type="checkbox"
                          checked={overAllocGroupByEmployee}
                          onChange={(e) => { setOverAllocGroupByEmployee(e.target.checked); setOverAllocExpandedGroups(new Set()); }}
                        /> Group by employee
                      </label>
                      {overAllocGroupByEmployee && groupedOverAllocations && (
                        <>
                          <button onClick={() => setOverAllocExpandedGroups(new Set(groupedOverAllocations.map(g => g.key)))} style={{ fontSize: '0.72rem', border: '1px solid #ced4da', background: '#f8f9fa', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>Expand all</button>
                          <button onClick={() => setOverAllocExpandedGroups(new Set())} style={{ fontSize: '0.72rem', border: '1px solid #ced4da', background: '#f8f9fa', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>Collapse all</button>
                        </>
                      )}
                      <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: 'auto' }}>
                        {overAllocGroupByEmployee && groupedOverAllocations
                          ? `${groupedOverAllocations.length} employee(s) · ${overAllocations.length} week(s)`
                          : `${overAllocations.length} week(s)`}
                      </span>
                    </div>
                  )}
                  {overAllocationsLoading ? (
                    <div style={{ padding: '1rem', color: '#666' }}>Loading...</div>
                  ) : (overAllocationsLoaded && overAllocations.length === 0) ? (
                    <div style={{ padding: '1rem', backgroundColor: '#d4edda', color: '#155724', borderRadius: 4 }}>
                      ✅ No over-allocations in the next 52 weeks.
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead style={{ backgroundColor: '#f4f4f4' }}>
                        <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ textAlign: 'left', padding: '0.5rem' }}>Employee</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem', whiteSpace: 'nowrap' }}>Week</th>
                          <th style={{ textAlign: 'right', padding: '0.5rem' }}>Total</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem' }}>Contributing assignments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overAllocGroupByEmployee && groupedOverAllocations ? (
                          groupedOverAllocations.map(g => {
                            const open = overAllocExpandedGroups.has(g.key);
                            return (
                              <React.Fragment key={g.key}>
                                <tr
                                  onClick={() => toggleOverAllocGroup(g.key)}
                                  style={{ cursor: 'pointer', backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}
                                  title={open ? 'Click to collapse' : 'Click to expand'}
                                >
                                  <td style={{ padding: '0.5rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    <span style={{ display: 'inline-block', width: 16, color: '#64748b' }}>{open ? '▼' : '▶'}</span>
                                    {g.empl_name}{g.empl_code ? <span style={{ color: '#888', fontWeight: 400 }}> ({g.empl_code})</span> : null}
                                  </td>
                                  <td style={{ padding: '0.5rem', whiteSpace: 'nowrap', color: '#475569' }}>{g.rows.length} week{g.rows.length === 1 ? '' : 's'}</td>
                                  <td style={{ padding: '0.5rem', textAlign: 'right', color: '#dc3545', fontWeight: 'bold' }}>{g.peakTotal}</td>
                                  <td style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>{open ? '' : 'Click to expand weeks'}</td>
                                </tr>
                                {open && g.rows.map((it, idx) => (
                                  <tr key={`${g.key}-${it.week_start_date}-${idx}`} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '0.5rem 0.5rem 0.5rem 2rem', verticalAlign: 'top', color: '#94a3b8' }}>{g.empl_name}</td>
                                    <td style={{ padding: '0.5rem', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                      {new Date(it.week_start_date + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                                    </td>
                                    <td style={{ padding: '0.5rem', verticalAlign: 'top', textAlign: 'right', color: '#dc3545', fontWeight: 'bold' }}>{it.total}</td>
                                    <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>
                                      {(it.conflicts || []).map((c, ci) => (
                                        <div key={ci} style={{ fontSize: '0.8rem', color: '#444' }}>
                                          <strong>{c.job || '(no job)'}</strong>
                                          {c.job_name ? ` — ${c.job_name}` : ''}
                                          {c.role ? ` · ${c.role}` : ''}
                                          {c.discipline ? ` · ${c.discipline}` : ''}
                                          <span style={{ color: '#dc3545' }}> @ {c.allocation}</span>
                                        </div>
                                      ))}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })
                        ) : overAllocations.map((it, idx) => (
                          <tr key={`${it.empl_code || ''}-${it.empl_name || ''}-${it.week_start_date}-${idx}`} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>
                              {it.empl_name || ''}{it.empl_code ? <span style={{ color: '#888' }}> ({it.empl_code})</span> : null}
                            </td>
                            <td style={{ padding: '0.5rem', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                              {new Date(it.week_start_date + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                            </td>
                            <td style={{ padding: '0.5rem', verticalAlign: 'top', textAlign: 'right', color: '#dc3545', fontWeight: 'bold' }}>{it.total}</td>
                            <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>
                              {(it.conflicts || []).map((c, ci) => (
                                <div key={ci} style={{ fontSize: '0.8rem', color: '#444' }}>
                                  <strong>{c.job || '(no job)'}</strong>
                                  {c.job_name ? ` — ${c.job_name}` : ''}
                                  {c.role ? ` · ${c.role}` : ''}
                                  {c.discipline ? ` · ${c.discipline}` : ''}
                                  <span style={{ color: '#dc3545' }}> @ {c.allocation}</span>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              {analyticsTab === 'under_allocation' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ fontSize: '0.85rem', color: '#374151', maxWidth: '48%' }}>
                      Active employees whose total weekly allocation is <strong>below 1.0</strong> (spare capacity), including idle weeks within their assignment range. All work counts here, including estimates. The weekly email also excludes people marked Terminated or On Leave in Admin → Employees.
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.7rem', color: '#666', marginBottom: 2 }}>Window</label>
                        <select value={underAllocMode} onChange={(e) => {
                          const mode = e.target.value;
                          if (mode === 'range' && (!underAllocStart || !underAllocEnd)) {
                            const start = new Date();
                            const end = new Date();
                            end.setMonth(end.getMonth() + 3);
                            const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                            setUnderAllocStart(fmt(start));
                            setUnderAllocEnd(fmt(end));
                          }
                          setUnderAllocMode(mode);
                        }} style={{ padding: '4px 8px', fontSize: '0.8rem', border: '1px solid #ced4da', borderRadius: 4 }}>
                          <option value="weeks">Next N weeks</option>
                          <option value="range">Date range</option>
                        </select>
                      </div>
                      {underAllocMode === 'weeks' ? (
                        <div>
                          <label style={{ display: 'block', fontSize: '0.7rem', color: '#666', marginBottom: 2 }}>Weeks ahead</label>
                          <select value={underAllocWeeks} onChange={(e) => setUnderAllocWeeks(parseInt(e.target.value, 10))} style={{ padding: '4px 8px', fontSize: '0.8rem', border: '1px solid #ced4da', borderRadius: 4 }}>
                            <option value={4}>4 weeks (~1 month)</option>
                            <option value={8}>8 weeks (~2 months)</option>
                            <option value={13}>13 weeks (~3 months)</option>
                            <option value={26}>26 weeks (~6 months)</option>
                            <option value={52}>52 weeks (1 year)</option>
                          </select>
                        </div>
                      ) : (
                        <>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.7rem', color: '#666', marginBottom: 2 }}>From</label>
                            <input type="date" value={underAllocStart} onChange={(e) => setUnderAllocStart(e.target.value)} style={{ padding: '3px 6px', fontSize: '0.8rem', border: '1px solid #ced4da', borderRadius: 4 }} />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.7rem', color: '#666', marginBottom: 2 }}>To</label>
                            <input type="date" value={underAllocEnd} onChange={(e) => setUnderAllocEnd(e.target.value)} style={{ padding: '3px 6px', fontSize: '0.8rem', border: '1px solid #ced4da', borderRadius: 4 }} />
                          </div>
                        </>
                      )}
                      <button onClick={fetchUnderAllocations} disabled={underAllocationsLoading || (underAllocMode === 'range' && (!underAllocStart || !underAllocEnd))} style={{ padding: '5px 12px', fontSize: '0.8rem', border: '1px solid #007bff', backgroundColor: '#007bff', color: '#fff', borderRadius: 4, cursor: underAllocationsLoading ? 'wait' : 'pointer' }}>
                        {underAllocationsLoading ? 'Loading...' : 'Apply'}
                      </button>
                    </div>
                  </div>
                  {underAllocationsError && (
                    <div style={{ padding: '0.5rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: 4, marginBottom: '0.5rem', fontSize: '0.85rem' }}>{underAllocationsError}</div>
                  )}
                  {!underAllocationsLoading && underAllocations.length > 0 && (
                    <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', color: '#334155', cursor: 'pointer', userSelect: 'none' }}>
                        <input
                          type="checkbox"
                          checked={underAllocGroupByEmployee}
                          onChange={(e) => { setUnderAllocGroupByEmployee(e.target.checked); setUnderAllocExpandedGroups(new Set()); }}
                        /> Group by employee
                      </label>
                      {underAllocGroupByEmployee && groupedUnderAllocations && (
                        <>
                          <button onClick={() => setUnderAllocExpandedGroups(new Set(groupedUnderAllocations.map(g => g.key)))} style={{ fontSize: '0.72rem', border: '1px solid #ced4da', background: '#f8f9fa', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>Expand all</button>
                          <button onClick={() => setUnderAllocExpandedGroups(new Set())} style={{ fontSize: '0.72rem', border: '1px solid #ced4da', background: '#f8f9fa', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>Collapse all</button>
                        </>
                      )}
                      <span style={{ fontSize: '0.75rem', color: '#666' }}>Use the <FilterIcon isActive={false} color="#495057" /> icons in the column headers to filter.</span>
                      {underFilter.getActiveFilters().map(f => (
                        <span key={f.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', backgroundColor: '#e7f1ff', color: '#0b5394', border: '1px solid #b6daff', borderRadius: 12, padding: '1px 8px' }}>
                          {underFilter.columnLabels[f.key] || f.key}: {f.type === 'values' ? `${f.count} selected` : `${f.mode} "${f.value}"`}
                          <span onClick={() => underFilter.clearColumnFilter(f.key)} style={{ cursor: 'pointer', fontWeight: 700 }} title="Clear">×</span>
                        </span>
                      ))}
                      {underFilter.getActiveFilters().length > 0 && (
                        <button onClick={() => underFilter.clearAllFilters()} style={{ fontSize: '0.72rem', border: '1px solid #ced4da', background: '#f8f9fa', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>Clear all</button>
                      )}
                      <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: 'auto' }}>
                        {underAllocGroupByEmployee && groupedUnderAllocations
                          ? `${groupedUnderAllocations.length} employee(s) · ${displayedUnderAllocations.length} of ${underAllocations.length} entries`
                          : `${displayedUnderAllocations.length} of ${underAllocations.length} entries`}
                      </span>
                    </div>
                  )}
                  {underAllocationsLoading ? (
                    <div style={{ padding: '1rem', color: '#666' }}>Loading...</div>
                  ) : (underAllocationsLoaded && underAllocations.length === 0) ? (
                    <div style={{ padding: '1rem', backgroundColor: '#d4edda', color: '#155724', borderRadius: 4 }}>
                      ✅ No under-allocations in the selected window.
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          {[
                            { key: 'empl_name', label: 'Employee', align: 'left' },
                            { key: 'role', label: 'Role', align: 'left' },
                            { key: 'week_start_date', label: 'Week', align: 'left', nowrap: true },
                            { key: 'total', label: 'Allocated', align: 'right' },
                            { key: 'available', label: 'Available', align: 'right' },
                            { key: null, label: 'Current assignments', align: 'left' }
                          ].map((col, ci) => {
                            const sortable = !!col.key;
                            const active = sortable && underAllocSort.key === col.key;
                            const hasFilter = sortable && ((underFilter.columnFilters[col.key]?.length > 0) || !!underFilter.filterMode[col.key]);
                            return (
                              <th
                                key={ci}
                                style={{ position: 'sticky', top: 0, zIndex: 5, backgroundColor: '#f4f4f4', textAlign: col.align, padding: '0.5rem', borderBottom: '2px solid #dee2e6', whiteSpace: col.nowrap ? 'nowrap' : undefined, userSelect: 'none' }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
                                  <span onClick={sortable ? () => handleUnderAllocSort(col.key) : undefined} style={{ cursor: sortable ? 'pointer' : 'default' }} title={sortable ? 'Click to sort' : undefined}>
                                    {col.label}{active ? (underAllocSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                                  </span>
                                  {sortable && (
                                    <span onClick={(e) => underFilter.openFilterAtPosition(e, col.key)} style={{ cursor: 'pointer', opacity: hasFilter ? 1 : 0.6, display: 'flex' }} title="Filter column">
                                      <FilterIcon isActive={hasFilter} color="#495057" />
                                    </span>
                                  )}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {displayedUnderAllocations.length === 0 ? (
                          <tr><td colSpan={6} style={{ padding: '1rem', color: '#888', textAlign: 'center' }}>No entries match the filter.</td></tr>
                        ) : underAllocGroupByEmployee && groupedUnderAllocations ? (
                          groupedUnderAllocations.map(g => {
                            const open = underAllocExpandedGroups.has(g.key);
                            return (
                              <React.Fragment key={g.key}>
                                <tr
                                  onClick={() => toggleUnderAllocGroup(g.key)}
                                  style={{ cursor: 'pointer', backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}
                                  title={open ? 'Click to collapse' : 'Click to expand'}
                                >
                                  <td style={{ padding: '0.5rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    <span style={{ display: 'inline-block', width: 16, color: '#64748b' }}>{open ? '▼' : '▶'}</span>
                                    {g.empl_name}{g.empl_code ? <span style={{ color: '#888', fontWeight: 400 }}> ({g.empl_code})</span> : null}
                                  </td>
                                  <td style={{ padding: '0.5rem' }}>{g.role}</td>
                                  <td style={{ padding: '0.5rem', whiteSpace: 'nowrap', color: '#475569' }}>{g.rows.length} week{g.rows.length === 1 ? '' : 's'}</td>
                                  <td style={{ padding: '0.5rem' }}></td>
                                  <td style={{ padding: '0.5rem', textAlign: 'right', color: '#fd7e14', fontWeight: 'bold' }}>{g.totalAvailable}</td>
                                  <td style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>{open ? '' : 'Click to expand weeks'}</td>
                                </tr>
                                {open && g.rows.map((it, idx) => (
                                  <tr key={`${g.key}-${it.week_start_date}-${idx}`} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '0.5rem 0.5rem 0.5rem 2rem', verticalAlign: 'top', color: '#94a3b8' }}>{g.empl_name}</td>
                                    <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>{it.role || ''}</td>
                                    <td style={{ padding: '0.5rem', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                      {new Date(it.week_start_date + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                                    </td>
                                    <td style={{ padding: '0.5rem', verticalAlign: 'top', textAlign: 'right', color: '#6c757d' }}>{it.total}</td>
                                    <td style={{ padding: '0.5rem', verticalAlign: 'top', textAlign: 'right', color: '#fd7e14', fontWeight: 'bold' }}>{it.available}</td>
                                    <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>
                                      {(it.conflicts || []).length === 0 ? (
                                        <span style={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic' }}>No allocation this week</span>
                                      ) : (it.conflicts || []).map((c, ci) => (
                                        <div key={ci} style={{ fontSize: '0.8rem', color: '#444' }}>
                                          <strong>{c.job || '(no job)'}</strong>
                                          {c.job_name ? ` — ${c.job_name}` : ''}
                                          {c.role ? ` · ${c.role}` : ''}
                                          {c.discipline ? ` · ${c.discipline}` : ''}
                                          <span style={{ color: '#6c757d' }}> @ {c.allocation}</span>
                                        </div>
                                      ))}
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })
                        ) : displayedUnderAllocations.map((it, idx) => (
                          <tr key={`${it.empl_code || ''}-${it.empl_name || ''}-${it.week_start_date}-${idx}`} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>
                              {it.empl_name || ''}{it.empl_code ? <span style={{ color: '#888' }}> ({it.empl_code})</span> : null}
                            </td>
                            <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>{it.role || ''}</td>
                            <td style={{ padding: '0.5rem', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                              {new Date(it.week_start_date + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                            </td>
                            <td style={{ padding: '0.5rem', verticalAlign: 'top', textAlign: 'right', color: '#6c757d' }}>{it.total}</td>
                            <td style={{ padding: '0.5rem', verticalAlign: 'top', textAlign: 'right', color: '#fd7e14', fontWeight: 'bold' }}>{it.available}</td>
                            <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>
                              {(it.conflicts || []).length === 0 ? (
                                <span style={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic' }}>No allocation this week</span>
                              ) : (it.conflicts || []).map((c, ci) => (
                                <div key={ci} style={{ fontSize: '0.8rem', color: '#444' }}>
                                  <strong>{c.job || '(no job)'}</strong>
                                  {c.job_name ? ` — ${c.job_name}` : ''}
                                  {c.role ? ` · ${c.role}` : ''}
                                  {c.discipline ? ` · ${c.discipline}` : ''}
                                  <span style={{ color: '#6c757d' }}> @ {c.allocation}</span>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {underFilter.openFilterColumn && (
                    <FilterTemplateDropdown
                      columnKey={underFilter.openFilterColumn}
                      columnLabel={underFilter.columnLabels[underFilter.openFilterColumn] || underFilter.openFilterColumn}
                      showTextFilters={true}
                      isNumericColumn={underFilter.numericColumnKeys.includes(underFilter.openFilterColumn)}
                      isDateColumn={underFilter.dateColumnKeys.includes(underFilter.openFilterColumn)}
                      columnFilters={underFilter.columnFilters}
                      filterMode={underFilter.filterMode}
                      filterText={underFilter.filterText}
                      filterSearch={underFilter.filterSearch}
                      filterButtonPositions={underFilter.filterButtonPositions}
                      setOpenFilterColumn={underFilter.setOpenFilterColumn}
                      setFilterSearch={underFilter.setFilterSearch}
                      getUniqueValues={underFilter.getUniqueValues}
                      handleFilterToggle={underFilter.handleFilterToggle}
                      selectAllColumnFilter={underFilter.selectAllColumnFilter}
                      clearColumnFilter={underFilter.clearColumnFilter}
                      applyTextFilter={underFilter.applyTextFilter}
                    />
                  )}
                </div>
              )}
            </div>
            <div style={{ marginTop: '0.75rem', textAlign: 'right', flexShrink: 0 }}>
              <button onClick={() => setShowAnalyticsModal(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showAnalyticsHelp && (
        <div style={{ ...modalOverlayStyle, zIndex: 1100 }} onClick={() => setShowAnalyticsHelp(false)}>
          <div style={{ ...modalContentStyle, maxWidth: '720px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem', flexShrink: 0 }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem' }}>❓ Analytics Help</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton compact guideId="indirect-resources-analytics-help" title="Analytics Help" accent="#0d6efd" />
                <button onClick={() => setShowAnalyticsHelp(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid #dee2e6', marginBottom: 10, flexShrink: 0 }}>
              {[['guide', 'Guide'], ['legend', 'Legend']].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setAnalyticsHelpTab(id)}
                  style={{
                    border: 'none',
                    background: analyticsHelpTab === id ? '#0d6efd' : 'transparent',
                    color: analyticsHelpTab === id ? '#fff' : '#0d6efd',
                    padding: '7px 16px',
                    borderRadius: '8px 8px 0 0',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {analyticsHelpTab === 'legend' && (
              <div style={{ fontSize: '0.9rem', color: '#333', overflowY: 'auto', flex: 1, paddingRight: '0.5rem' }}>
                <section style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff8e6', borderRadius: 8, borderLeft: '4px solid #ffc107' }}>
                  <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>Week cell colours (FTE)</h3>
                  <p style={{ margin: '0 0 0.75rem' }}>Same green-to-red scale as the plan grid. Empty is treated as zero.</p>
                  <WeekColorLegend />
                </section>
                <section style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff3f3', borderRadius: 8, borderLeft: '4px solid #dc3545' }}>
                  <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#dc3545' }}>Who can appear on the plan</h3>
                  <p style={{ margin: 0 }}>
                    Only employees flagged <strong>Indirect</strong> on the master list — foremen and above
                    (Foreman, General Foreman, Superintendent, PM) plus staff (clerks, schedulers, ...).
                  </p>
                </section>
                <EmployeeCodeLegendPanel accent="#0d6efd" />
              </div>
            )}
            <div style={{ fontSize: '0.9rem', color: '#333', overflowY: 'auto', flex: 1, paddingRight: '0.5rem', display: analyticsHelpTab === 'guide' ? 'block' : 'none' }}>
              <HelpWithVideoTour guideId="indirect-resources-analytics-help" title="Analytics Help" accent="#0d6efd">
                <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#f0f7ff', borderRadius: 8, borderLeft: '4px solid #0d6efd' }}>
                  <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>Four tabs, one window</h3>
                  <p style={{ margin: 0 }}>Analytics stays one size. Switch tabs; the content scrolls if it is taller than the window.</p>
                  <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
                    <li><strong>Histogram</strong> — stacked bars by week, with slicers and group-by</li>
                    <li><strong>Pivot Table</strong> — group people, expand a row to see week cells (same colours as the plan). Headers stay visible as you scroll.</li>
                    <li><strong>Over-Allocation</strong> — weeks over 1.0 FTE, grouped by employee and collapsed until you expand</li>
                    <li><strong>Under-Allocation</strong> — spare capacity, grouped by employee and collapsed until you expand</li>
                  </ul>
                </section>
                <section style={{ marginBottom: '1.25rem', padding: '1rem', backgroundColor: '#fff8e6', borderRadius: 8, borderLeft: '4px solid #ffc107' }}>
                  <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0d6efd' }}>Week colours</h3>
                  <p style={{ margin: '0 0 0.75rem' }}>Green is free. Red is fully booked (or more). Open the <strong>Legend</strong> tab for department, discipline, and role codes.</p>
                  <WeekColorLegend />
                </section>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAuditModal && ReactDOM.createPortal(
        <div style={modalOverlayStyle} onClick={() => setShowAuditModal(false)}>
          <div style={{ ...modalContentStyle, maxWidth: '1280px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 860 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #dee2e6', paddingBottom: '0.75rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>📜 Audit Log</h2>
                <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                  {auditData.total} entries · click a row to jump to the change
                </p>
              </div>
              <button onClick={() => setShowAuditModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search anything in the table..."
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
                style={{ ...filterInputStyle, width: 360, minWidth: 220, flex: '1 1 220px' }}
              />
              <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: 'auto' }}>
                {auditDisplayRows.length === auditFlatItems.length
                  ? `${auditDisplayRows.length} row${auditDisplayRows.length === 1 ? '' : 's'}`
                  : `${auditDisplayRows.length} of ${auditFlatItems.length} rows`}
              </span>
            </div>
            <div style={{ flex: 1, overflow: 'auto', border: '1px solid #dee2e6', borderRadius: 6 }}>
              {auditLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Loading...</div>
              ) : auditData.entries.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No audit log entries found.</div>
              ) : auditDisplayRows.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No rows match the search or column filters.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e9ecef', position: 'sticky', top: 0, zIndex: 1 }}>
                      {AUDIT_TABLE_COLUMNS.map((col) => {
                        const hasFilter = (auditFilterTpl.columnFilters[col.key]?.length > 0) || !!auditFilterTpl.filterMode[col.key];
                        return (
                          <th
                            key={col.key}
                            style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '2px solid #dee2e6', userSelect: 'none', whiteSpace: 'nowrap' }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span
                                onClick={() => handleAuditSort(col.key)}
                                style={{ cursor: 'pointer' }}
                                title={`Sort by ${col.label}`}
                              >
                                {col.label}{auditSort.key === col.key ? (auditSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                              </span>
                              <span
                                onClick={(e) => { e.stopPropagation(); auditFilterTpl.openFilterAtPosition(e, col.key); }}
                                style={{ cursor: 'pointer', opacity: hasFilter ? 1 : 0.6, display: 'flex' }}
                                title={`Filter ${col.label}`}
                              >
                                <FilterIcon isActive={hasFilter} color="#495057" />
                              </span>
                            </div>
                          </th>
                        );
                      })}
                      <th style={{ width: 90, padding: '0.5rem', borderBottom: '2px solid #dee2e6' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditDisplayRows.map(({ entry, line, lineIndex }) => {
                      const badge = { CREATE: { bg: '#d4edda', text: '#155724' }, UPDATE: { bg: '#cce5ff', text: '#004085' }, CELL_UPDATE: { bg: '#e2e3e5', text: '#383d41' }, DELETE: { bg: '#f8d7da', text: '#721c24' }, BULK_DELETE: { bg: '#f8d7da', text: '#721c24' }, IMPORT: { bg: '#fff3cd', text: '#856404' }, REVERT: { bg: '#e0e7ff', text: '#3730a3' }, BATCH_FILL: { bg: '#f3e8ff', text: '#6b21a8' } }[entry.action_type] || { bg: '#e2e3e5', text: '#383d41' };
                      const canJump = !!(parseAuditTarget(entry).unique_id || parseAuditTarget(entry).empl_code);
                      const canRevert = lineIndex === 0 && isAuditSuperadmin && isAuditRevertable(entry);
                      return (
                        <tr
                          key={`${entry.id}-${lineIndex}`}
                          title={canJump ? 'Click to jump to this change on the plan' : ''}
                          style={{ borderBottom: '1px solid #eee', cursor: canJump ? 'pointer' : 'default' }}
                          onClick={() => handleAuditNavigate(entry, line)}
                        >
                          <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap', color: '#6b7280' }}>{new Date(entry.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}</td>
                          <td style={{ padding: '0.4rem 0.5rem' }}><span style={{ padding: '2px 8px', backgroundColor: badge.bg, color: badge.text, borderRadius: 12, fontSize: '0.8rem', fontWeight: 600 }}>{entry.action_type}</span></td>
                          <td style={{ padding: '0.4rem 0.5rem' }}>{entry.empl_name ? `${entry.empl_name} (${formatEmplCode(entry.empl_code)})` : (entry.empl_code || '—')}</td>
                          <td style={{ padding: '0.4rem 0.5rem' }}>{entry.job_ref || '—'}</td>
                          <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600, color: '#374151' }}>{line.field || '—'}</td>
                          <td style={{ padding: '0.4rem 0.5rem', color: '#9ca3af', textDecoration: line.hasFrom ? 'line-through' : 'none' }}>{line.hasFrom ? formatAuditVal(line.from) : '—'}</td>
                          <td style={{ padding: '0.4rem 0.5rem', color: '#111827', fontWeight: 700 }}>{line.to !== undefined && line.to !== null ? formatAuditVal(line.to) : '—'}</td>
                          <td style={{ padding: '0.4rem 0.5rem', fontSize: '0.8rem', color: '#666' }}>{entry.user_name || entry.user_email || '—'}</td>
                          <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {canRevert && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleAuditRevert(entry); }}
                                disabled={revertingAuditId === entry.id}
                                title="Restore the previous value (superadmin only)"
                                style={{
                                  padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                                  border: '1px solid #c7d2fe', borderRadius: 6,
                                  backgroundColor: revertingAuditId === entry.id ? '#eef2ff' : '#fff', color: '#4338ca',
                                }}
                              >
                                {revertingAuditId === entry.id ? 'Reverting…' : '↩ Revert'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem' }}>
              <span style={{ fontSize: '0.85rem', color: '#666' }}>
                Showing {auditDisplayRows.length} of {auditFlatItems.length}
                {auditData.total > (auditData.entries || []).length ? ` (first ${auditData.entries.length} of ${auditData.total} entries)` : ''}
              </span>
              {auditFilterTpl.getActiveFilters().length > 0 && (
                <button onClick={auditFilterTpl.clearAllFilters} style={{ ...smallBtnStyle, backgroundColor: '#6c757d' }}>Clear column filters</button>
              )}
            </div>
            {auditFilterTpl.openFilterColumn && (
              <FilterTemplateDropdown
                columnKey={auditFilterTpl.openFilterColumn}
                columnLabel={auditFilterTpl.columnLabels[auditFilterTpl.openFilterColumn] || auditFilterTpl.openFilterColumn}
                showTextFilters={true}
                isNumericColumn={auditFilterTpl.numericColumnKeys.includes(auditFilterTpl.openFilterColumn)}
                isDateColumn={auditFilterTpl.dateColumnKeys.includes(auditFilterTpl.openFilterColumn)}
                columnFilters={auditFilterTpl.columnFilters}
                filterMode={auditFilterTpl.filterMode}
                filterText={auditFilterTpl.filterText}
                filterSearch={auditFilterTpl.filterSearch}
                filterButtonPositions={auditFilterTpl.filterButtonPositions}
                setOpenFilterColumn={auditFilterTpl.setOpenFilterColumn}
                setFilterSearch={auditFilterTpl.setFilterSearch}
                getUniqueValues={auditFilterTpl.getUniqueValues}
                handleFilterToggle={auditFilterTpl.handleFilterToggle}
                selectAllColumnFilter={auditFilterTpl.selectAllColumnFilter}
                clearColumnFilter={auditFilterTpl.clearColumnFilter}
                applyTextFilter={auditFilterTpl.applyTextFilter}
              />
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const toolbarBtnStyle = {
  padding: '8px 16px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px'
};
const modalOverlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
};
const modalContentStyle = {
  backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', minWidth: '400px', maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
};
const filterInputStyle = { padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', width: '200px', minWidth: '150px' };
const smallBtnStyle = { padding: '0.4rem 0.75rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' };

export default IndirectResourcesDashboard;
